import type {
  CreditOperation,
  FacetCoverage,
  RunFindings,
  EventFrameDef,
  EventFrameKey,
  EventProfile,
  AdjudicationSensitivity,
  ColumnTarget,
  UnsupportedShapeHint,
  ExclusionRule,
  FactType,
  ImportContainer,
  ImportCounts,
  ImportFindingKind,
  ImportPhase,
  ImportPreviewOutcome,
  ImportProseKind,
  ImportRowConflict,
  ImportRowOutcome,
  ImportShape,
  ImportState,
  IdentifierAnchorKind,
  KnowledgeKind,
  KnowledgeSource,
  OccasionKind,
  MoreProseOrigin,
  OrgResearchDepth,
  NegationReason,
  PlanConfidence,
  ProseInputKind,
  ProvenanceEntry,
  ProvenanceKind,
  RecordFieldGroup,
  ResolutionLevel,
  RowEventRole,
  RunDetail,
  ResearchPass,
  RunKind,
  RunState,
  SubjectListLevel,
  SubjectListSort,
  SubjectStatus,
  SeatPart,
} from '@ni/shared';
import {
  BIN_RULES,
  BUSINESS_SUBJECT_STATUSES,
  CLUB_SUBJECT_STATUSES,
  FACET_LABELS,
  IMPORT_PHASE_LABELS,
  PERSONAL_SUBJECT_STATUSES,
  RUN_KIND_LABELS,
  RUN_STATE_LABELS,
  SECURITY_SUBJECT_STATUSES,
  SUBJECT_VOCABULARY_BY_VERTICAL,
  WEALTH_SUBJECT_STATUSES,
} from '@ni/shared';
import { assertNever } from './assert-never';
import { CANCELLED_BY_OFFICE, IDENTITY_WITHDRAWN } from './run-state';
import {
  formatCount,
  formatDate,
  formatDay,
  formatElapsed,
  humanize,
  joinClauses,
  keepLines,
  plural,
} from './format';

/**
 * The plain-language surface of the closed vocabularies.
 *
 * Rule §3.5 is binding here: the words negate, supersede, anti-anchor and
 * lineage never appear in anything a user reads.
 */

/* ————— shared lines: one spelling for a message several surfaces say ————— */

/** A search that matched nothing, on every list and picker. */
export const NO_MATCHES = 'No matches.';

/** The label on a namesake already on record, for every kind of record. */
export const ALREADY_ON_RECORD = 'Already on record';
/** A namesake's look-first link on the name-conflict card. */
export const NAME_CONFLICT_VIEW_RECORD = 'View record';
export const NAME_CONFLICT_VIEW_KNOWLEDGE = 'View in knowledge base';

/** The lead over a namesake decision on a create page. */
export function nameOnRecordLine(kind: 'person' | 'organization' | 'event'): string {
  const article = kind === 'organization' || kind === 'event' ? 'An' : 'A';
  return `${article} ${kind} with this name is already on record. Use that record if it is the same ${kind}, or create a new one.`;
}

/** A run waiting on the office, said the same on the strip, Home and People. */
export const RUN_CONFIRM_IDENTITY = 'Confirm who this is';
export const RUN_MORE_DETAIL = 'More detail needed';
/** An `onchain_entity_pick` waiting on the office, on Home and on the run strip. */
export const RUN_PICK_ONCHAIN_IDENTITY = 'Choose their on-chain identity';
export const ONCHAIN_PICK_ASK =
  'Which of these on-chain identities, if any, is this person? The brief waits for your answer.';
export const ONCHAIN_PICK_CHOOSE = 'This is them';
export const ONCHAIN_PICK_NONE = 'None of these';
export const ONCHAIN_PICK_RECOMMENDED = 'its own links match what we know';
export function reviewBeforeBriefLine(open: number): string {
  return open > 0
    ? `${plural(open, 'finding', 'findings')} to review before the brief`
    : 'Findings to review before the brief';
}

/** The quiet go-link at the end of a Needs review row: where pressing it takes the office. */
export const NEEDS_REVIEW_GO = {
  review: 'Review',
  confirm: 'Confirm',
  detail: 'Add detail',
  open: 'Open',
} as const;
export const NEEDS_REVIEW_EMPTY = 'Nothing to review.';
/**
 * The run-in head over Home's likely-merge questions, with how many there are.
 * One head for all of them: each question is a compact row under it, and the
 * label is said once rather than over every pair.
 */
export function possibleDuplicatesHead(count: number): string {
  return `Possible duplicates · ${count}`;
}

/** The rows past the first few, folded behind one line until asked for. */
export function showMoreLine(count: number): string {
  return `Show ${count} more`;
}

/** What a report's Revise and Refresh do, wherever they are offered. */
export const REPORT_REVISE_HELP = 'Rewrites this version from the same records. No new searches.';
export const REPORT_REFRESH_HELP =
  'Reads the records again and writes a new version with whatever has changed since this one.';

/** The tail every irreversible confirmation ends on. */
export const CANNOT_BE_UNDONE = 'This cannot be undone.';

/** The stamp on a finding the office just kept. */
export const KEPT_JUST_NOW = 'Kept · just now';

/** The heading over a confirmed identity. */
export const IDENTITY_CONFIRMED = 'Identity confirmed';

/** The free-text field for what the office knows about someone. */
export const WHAT_YOU_KNOW = 'What you know';

/** A record or page that is not there: the head, and the line under it. */
export const NOT_FOUND = 'Not found';
export const NOT_FOUND_LINE = 'There is nothing at this address.';

/**
 * **The source-tier words live in `@ni/shared` now** (2026-09-10), and are
 * re-exported here so every call site on this side is unchanged.
 *
 * They were this file's for as long as the dashboard was the only surface that
 * printed them. The MCP connector is the second: it hands each fact's sources
 * to a model that repeats whatever string it is given, and a tier token read
 * aloud to the office is the same defect there as here. The map moved to sit
 * beside `SOURCE_TIERS` itself — one vocabulary, both readers — and its doc
 * comments, including the `club_supplied` ruling, moved with it.
 */
export { SOURCE_TIER_LABELS, sourceTierLabel } from '@ni/shared';

export const FACT_TYPE_LABELS: Record<FactType, string> = {
  vitals: 'Vitals',
  education: 'Education',
  professional_license: 'Professional license',
  employment: 'Employment',
  board_seat: 'Board seat',
  association_membership: 'Membership',
  family_relation: 'Family',
  residence: 'Residence',
  nationality: 'Nationality',
  financial_event: 'Financial event',
  investment: 'Investment',
  digital_asset_holding: 'Crypto holdings',
  litigation: 'Litigation',
  records_check: 'Records check',
  broker_exposure: 'Data broker exposure',
  onchain_exposure: 'On-chain exposure',
  account_exposure: 'Account exposure',
  interest: 'Interest',
  event_participation: 'Event',
  achievement: 'Achievement',
  philanthropy: 'Philanthropy',
  social_tie: 'Connection',
  org_relation: 'Organization link',
  org_profile: 'Organization profile',
  event_profile: 'Event profile',
  finding: 'Finding',
};

/**
 * Why a row is greyed but still listed (ui-design §3.7 / §4.7). Removed things
 * stay visible, and each one says which thing happened in a person's words.
 */
export const NEGATION_LABELS: Record<NegationReason, string> = {
  wrong_fact: 'removed as wrong',
  no_longer_true: 'ended',
  do_not_surface: 'kept off the brief',
  wrong_person: 'removed, wrong person',
  /* An import that was taken back. The words say what happened to the *file*,
     not to the fact — nobody claimed the world was misdescribed — and they name
     the file rather than the mechanism, which is what the session's own page is
     for. */
  unwound: 'removed with its import',
  /* The compound spelling a split retired. The words say the statement moved
     rather than that it was wrong — it was true, and the finer rows beside this
     one are where it now lives. */
  replaced: 'replaced by finer facts',
  note_changed: 'removed with its note',
};

/**
 * Which prose a routed record field came out of. The substrate stores the raw
 * `prose_input.kind` on every field; only `more_prose` reads badly as itself.
 */
export const PROSE_INPUT_KIND_LABELS: Record<ProseInputKind, string> = {
  intake: 'intake',
  note: 'note',
  reference: 'reference',
  more_prose: 'added detail',
  interaction: 'interaction',
};

/**
 * The club's own words for a relationship. Typed against the club's const array
 * rather than the whole `SubjectStatus` union, so a value added to the club
 * vocabulary fails to compile here instead of rendering blank.
 */
const CLUB_STATUS_LABELS: Record<(typeof CLUB_SUBJECT_STATUSES)[number], string> = {
  prospect: 'Prospect',
  member: 'Member',
  contact: 'Contact',
  /* "Declined", not "Rejected": rejection is what happened in the committee
     room, and this word has to read without flinching next to a name. */
  declined: 'Declined',
};

/**
 * The business vertical's own words, typed against its const array for the same
 * reason: a value added to that vocabulary fails to compile here rather than
 * rendering as a blank space on a record header.
 */
const BUSINESS_STATUS_LABELS: Record<(typeof BUSINESS_SUBJECT_STATUSES)[number], string> = {
  intro: 'Intro',
  friend: 'Friend',
  partner: 'Partner',
};

/**
 * The personal vertical's one word. It is never offered as a choice — with one
 * live word there is nothing to choose (`hasStatusChoice`) — but it is still
 * *shown*: the People list's Status column and the graph legend print it, and a
 * blank there would read as a person with no status rather than as a vertical
 * with one.
 *
 * Same spelling as the club's `Contact` and a different table on purpose. The
 * two words mean different things, and the day one of them is relabelled the
 * other must not move with it.
 */
const PERSONAL_STATUS_LABELS: Record<(typeof PERSONAL_SUBJECT_STATUSES)[number], string> = {
  contact: 'Contact',
};

/**
 * The wealth vertical's three words — one label for both seats, because nothing
 * stored says whether an org is a wealth manager or a family office and nothing
 * should.
 *
 * `Prospect` and `Contact` are spelled the way the club spells them and mean
 * something else, which is why this is a table of its own rather than a shared
 * one: a club's Contact is somebody in its network who is not a membership
 * candidate, and a wealth office's is the lawyer or accountant who sends it
 * people. The day either is relabelled the other must not move with it.
 */
const WEALTH_STATUS_LABELS: Record<(typeof WEALTH_SUBJECT_STATUSES)[number], string> = {
  prospect: 'Prospect',
  client: 'Client',
  contact: 'Contact',
};

/**
 * The security vertical's three words — what a record is *to the engagement*,
 * which is what a protective firm files people under.
 *
 * "Subject of interest" is the firm's own phrase and is deliberately not
 * shortened: a "subject" alone is every record on the system, and anything
 * stronger would be the app asserting something about a person nobody has
 * assessed. None of the three says anything about conduct or danger.
 */
const SECURITY_STATUS_LABELS: Record<(typeof SECURITY_SUBJECT_STATUSES)[number], string> = {
  principal: 'Principal',
  subject_of_interest: 'Subject of interest',
  associate: 'Associate',
};

/**
 * Status labels **per vertical**, keyed by `org.vertical` exactly as the wire
 * spells it. Status is a universal invariant of subjects; the vocabulary is not.
 * The same value could mean different things in two verticals, so nothing here
 * is keyed by status alone.
 */
export const SUBJECT_STATUS_LABELS: Readonly<
  Record<string, Readonly<Partial<Record<SubjectStatus, string>>>>
> = {
  club: CLUB_STATUS_LABELS,
  business: BUSINESS_STATUS_LABELS,
  personal: PERSONAL_STATUS_LABELS,
  wealth: WEALTH_STATUS_LABELS,
  security: SECURITY_STATUS_LABELS,
};

/**
 * What to print for a subject's status — and `null` when this build has no words
 * for it, which the caller renders as nothing.
 *
 * An unknown vertical does **not** fall back to club (verticals design note
 * §0.3). A build serving an org whose vertical it does not carry would otherwise
 * label a partner a Prospect, which is worse than a blank space: absence is a
 * no-op, never a fabricated default.
 */
export function subjectStatusLabel(vertical: string, status: SubjectStatus): string | null {
  return SUBJECT_STATUS_LABELS[vertical]?.[status] ?? null;
}

/**
 * **The same words in the plural and in lower case** — "143 members, 252
 * prospects, 209 declined" — which is how a count line reads them (import note
 * §2.4) and not how a heading does.
 *
 * A second table rather than an `s` on the label, because "declined" takes none:
 * a declined prospect is one of the declined, and "209 declineds" is not a
 * sentence anybody would write. Typed against each vertical's const array for
 * the same reason the singular table is, so a new word is a compile error rather
 * than a blank space in the middle of a total.
 */
const CLUB_STATUS_PLURALS: Record<(typeof CLUB_SUBJECT_STATUSES)[number], string> = {
  prospect: 'prospects',
  member: 'members',
  contact: 'contacts',
  declined: 'declined',
};

const BUSINESS_STATUS_PLURALS: Record<(typeof BUSINESS_SUBJECT_STATUSES)[number], string> = {
  intro: 'intros',
  friend: 'friends',
  partner: 'partners',
};

const PERSONAL_STATUS_PLURALS: Record<(typeof PERSONAL_SUBJECT_STATUSES)[number], string> = {
  contact: 'contacts',
};

/** The wealth vertical's three, all of which take a plain `s`. */
const WEALTH_STATUS_PLURALS: Record<(typeof WEALTH_SUBJECT_STATUSES)[number], string> = {
  prospect: 'prospects',
  client: 'clients',
  contact: 'contacts',
};

/** The security vertical's three, all of which take a plain `s`. */
const SECURITY_STATUS_PLURALS: Record<(typeof SECURITY_SUBJECT_STATUSES)[number], string> = {
  principal: 'principals',
  subject_of_interest: 'subjects of interest',
  associate: 'associates',
};

export const SUBJECT_STATUS_PLURALS: Readonly<
  Record<string, Readonly<Partial<Record<SubjectStatus, string>>>>
> = {
  club: CLUB_STATUS_PLURALS,
  business: BUSINESS_STATUS_PLURALS,
  personal: PERSONAL_STATUS_PLURALS,
  wealth: WEALTH_STATUS_PLURALS,
  security: SECURITY_STATUS_PLURALS,
};

/**
 * The plural word, falling back to the **status token itself** rather than to
 * nothing.
 *
 * This one place departs from `subjectStatusLabel`'s "absence, never a
 * fabricated default", and deliberately: the caller is a total, and a clause
 * with a count and no noun ("143 ,") is worse than one naming the wire's own
 * word. Nothing is fabricated — the token is what the file said.
 */
export function subjectStatusPlural(vertical: string, status: SubjectStatus): string {
  return SUBJECT_STATUS_PLURALS[vertical]?.[status] ?? status;
}

/**
 * What the new-person form says in place of a status control when this build
 * does not carry the org's vertical. There is no safe control to render — the
 * words belong to a vocabulary this version does not have — so the form says
 * why rather than sitting there dead.
 */
export const UNKNOWN_VERTICAL_NOTE =
  "This version does not support this organization's type. " +
  'New people cannot be added until it is updated.';

/**
 * The per-vertical wording, used only when that vertical actually holds
 * something back — **empty since 2026-09-05**, when the club's last two words
 * went live and its line ("Prospects only for now") stopped being true.
 *
 * Kept rather than deleted with the line: the derivation below is what decides
 * whether a hint is shown at all, and a vertical that ships a word ahead of the
 * build behind it needs somewhere to say so.
 */
const SUBJECT_STATUS_HINTS: Readonly<Record<string, string>> = {};

/**
 * The line under the status control on the new-person form, for a vertical whose
 * vocabulary is wider than what it accepts today.
 *
 * **Derived, not declared**: the hint is returned only when the vertical's live
 * subset is narrower than its full vocabulary, so it cannot outlive the
 * restriction it describes. That day came on 2026-09-05 — the club accepts all
 * four of its words now, and this went quiet on its own rather than having to be
 * found and deleted. Null for a vertical this build does not carry, and null for
 * one that holds nothing back, which today is every one of them.
 */
export function subjectStatusHint(vertical: string): string | null {
  const vocabulary = SUBJECT_VOCABULARY_BY_VERTICAL[vertical];
  if (!vocabulary) return null;
  if (vocabulary.live.length >= vocabulary.statuses.length) return null;
  return SUBJECT_STATUS_HINTS[vertical] ?? null;
}

/**
 * What the record head's status control is called for a reader who cannot see
 * it. The visible face is the word alone — "Member" and a caret — which says
 * nothing about what the word *is* or that pressing it changes anything, so the
 * accessible name carries both.
 */
export function statusControlLabel(label: string): string {
  return `Status: ${label}. Change status`;
}

/**
 * The record head's one grey line: the record's dates, and — since 2026-09-06 —
 * the one thing about a person that outranks them, which is that they are a user
 * of this system (import note §1.14). The status left this line when it grew a
 * control of its own (design note §2.4) and has not come back.
 *
 * The dates clause appears only once there is a change to name, and it is
 * deliberately not "member since" — what the record knows is when somebody
 * marked the switch here, not when the person joined.
 */
export function recordSubline(
  subject: {
    created_at: string;
    status_changed_at: string | null;
    linked_user?: { id: string; name: string | null; email: string } | null;
  },
  viewerUserId?: string | null,
): string {
  return joinClauses([
    linkedUserClause(subject.linked_user ?? null, viewerUserId ?? null),
    `added ${formatDay(subject.created_at)}`,
    subject.status_changed_at ? `status changed ${formatDay(subject.status_changed_at)}` : null,
  ]);
}

/**
 * **"This is you"**, or "This is <name>" (import note §1.14 / §2.5) — the record
 * saying that the person on this page is a person who uses this system.
 *
 * It leads the subline rather than trailing it because it changes what the whole
 * page is: everything after it is dates. It is the only clause here that is about
 * the reader.
 *
 * The second person is deliberate and so is its narrowness — "you" only when the
 * link is the caller's own account, never when the caller merely *could* be them.
 * A colleague gets their name, because the fact worth saying is that this record
 * is somebody in the office rather than somebody the office researches. The name
 * falls back to the email: `app_user.name` is null until a first sign-in, and
 * "This is " with nothing after it is not a sentence.
 *
 * Null viewer — no `/me` yet — reads as a colleague, which is the safe way round:
 * the worst it can do is name the reader instead of saying "you".
 */
function linkedUserClause(
  linked: { id: string; name: string | null; email: string } | null,
  viewerUserId: string | null,
): string | null {
  if (!linked) return null;
  if (viewerUserId && linked.id === viewerUserId) return 'This is you';
  return `This is ${linked.name ?? linked.email}`;
}

/** The subject fields the annotation below reads; a `SubjectRecord['subject']` satisfies it. */
export interface StatusForAnnotation {
  vertical: string;
  status: SubjectStatus;
  /** The kinds the *current* status derives — what the next run would write. */
  analysis_kinds: string[];
}

/** Facet summaries are `facet_summary:<facet>`; nothing else carries the prefix. */
const FACET_SUMMARY_PREFIX = 'facet_summary:';

/**
 * The quiet line under a reading the current status would not ask for again —
 * a prospect-era Fit still on a member's dossier (design note §2.5).
 *
 * A switch changes nothing already on record (§1.4): the section stays, its
 * content is untouched, and this line is the whole of what is said about it. It
 * is phrased around *the status*, not around the person, because it has to read
 * for every word in every vertical: "Declined" is not a noun somebody is, and
 * "a declined's research" is not a sentence.
 *
 * Null — no line — in three cases: the kind is one the status still derives,
 * the vertical is one this build has no words for (§0.3: absence over a
 * fabricated default), or the kind is a facet summary. Facet summaries follow
 * the run's *depth*, which the record does not carry, so a line here would be a
 * guess about a set this page cannot see.
 */
export function keptFromEarlierLine(
  subject: StatusForAnnotation | undefined,
  kind: string,
): string | null {
  if (!subject) return null;
  if (kind.startsWith(FACET_SUMMARY_PREFIX)) return null;
  if (subject.analysis_kinds.includes(kind)) return null;
  const label = subjectStatusLabel(subject.vertical, subject.status);
  if (!label) return null;
  return `From earlier research. Not updated for ${label}.`;
}

/* ————— the shared controls ————— */

/**
 * **What the ellipsis is**, for a reader who cannot see it (`ui/overflow-menu.tsx`).
 *
 * The glyph is the whole of the button, so it is the whole of what a screen
 * reader would have to go on, and "…" is not a name. It says *actions* rather
 * than "options" or "menu" because what is behind it are things the office does
 * to the record — and the word is the same wherever the button is used, since a
 * reader who learns it once should not have to learn it again per screen.
 */
export const MORE_ACTIONS = 'More actions';

/* ————————————————————————— the people screen ————————————————————————— */

/**
 * **What a person with no record of their own is called** (import note §1.3).
 *
 * A known person is an entity of kind person the org holds without a subject
 * record: a spouse research minted, and, once imports land, every person a file
 * produced that the office chose to keep without tracking. The word sits where
 * a status would, because that is exactly what it is standing in for — there is
 * no status, and saying so is more honest than an empty cell that reads as a
 * loading failure.
 *
 * Deliberately not per-vertical. "Known" is a fact about this system's own
 * records rather than about what the org is, so no vertical renames it.
 */
export const KNOWN_LEVEL_LABEL = 'Known';

/** The three ways to read the list, in the shared vocabulary's own order. */
export const PEOPLE_LEVEL_LABELS: Readonly<Record<SubjectListLevel, string>> = {
  tracked: 'Tracked',
  known: 'Known',
  all: 'All',
};

/** The two orders. "Recently updated" says which end is first; "Name" needs no help. */
export const PEOPLE_SORT_LABELS: Readonly<Record<SubjectListSort, string>> = {
  updated: 'Recently updated',
  name: 'Name',
};

/** The search box asks for the one thing it searches. */
export const PEOPLE_SEARCH_PLACEHOLDER = 'Search by name';

/**
 * **The list is empty, and the line says what it is empty *of*.**
 *
 * There are three lists behind one screen and only one of them is "the org's
 * people": at Tracked, an org that has imported four hundred people and tracked
 * none of them reads "No people yet." over a database full of them, which is not
 * a small infelicity — it is the screen denying the import happened. So the line
 * is the level's, and it names the level in the control's own word.
 *
 * "yet" belongs only where the emptiness is a beginning. An org tracks nobody
 * *yet*; an org that holds nobody outside its tracked people is not waiting for
 * anything, so Known's line is a plain statement of fact.
 */
export const PEOPLE_EMPTY_BY_LEVEL: Readonly<Record<SubjectListLevel, string>> = {
  tracked: 'No tracked people yet.',
  known: 'No known people.',
  all: 'No people yet.',
};

/** The search or the chips matching nobody, which is not the same as holding nobody. */
export const PEOPLE_NONE_MATCH = NO_MATCHES;

/** The next page, and the way back to the whole list from Home. */
export const LOAD_MORE = 'Load more';
export const LOADING_MORE = 'Loading…';
export const SEE_ALL_PEOPLE = 'See all people';

/**
 * **Promotion, from the list** (import note §1.3): the adopt door, offered where
 * the known person already is. Choosing a word gives them a record of their own
 * and carries every fact already on the entity across — nothing is minted and
 * nothing is copied.
 */
export const PROMOTE_ACTION = 'Track';
export const PROMOTE_WORKING = 'Tracking…';

/**
 * The way out of the picker, for the reader who pressed Track to find out what
 * the words were. It is the plain word rather than "Never mind" or "Not now":
 * nothing has been decided, so there is nothing to be gracious about.
 */
export const PROMOTE_CANCEL = 'Cancel';

/** The accessible name for the row's word picker, which is otherwise just words. */
export function promoteControlLabel(name: string): string {
  return `Track ${name} as`;
}

/* ————————————————————————— the organisation's own words ————————————————————————— */

/**
 * **The org profile, on Settings** — shown to a vertical that reads it, which
 * since 2026-09-03 means the business vertical alone (`ORG_PROFILE_READ_BY_VERTICAL`
 * in `@ni/shared`). One prose field per vertical: a club is asked for its values,
 * which `club_fit` reads, and a business org for its description, which
 * `relationship_read` reads.
 *
 * The *setting* is still core; the **word for the org is not**. A business
 * reader is asked about the company, because that is the noun they use for the
 * thing they are describing. Only the fallback says "organisation", and it says
 * it because it has to: a build serving a vertical it does not carry has no
 * word of that org's own to use, and borrowing one from a vertical it does
 * carry would call a company a club.
 *
 * The help line says what to write and what it is for, in that order, and the
 * last sentence is the honest scope: research reads people *in relation to*
 * this. It does not promise the profile changes anything else, because today it
 * does not. It is the **default** help line rather than the only one: a vertical
 * whose reader asks the office for something more specific says so in its own
 * row below (`help`), and one that does not falls here.
 */
export const ORG_PROFILE_HELP =
  'Who you are, in your own words: what the organization does, who founded it, ' +
  'where it is based. Research reads people in relation to this.';

interface OrgProfileFieldCopy {
  readonly label: string;
  readonly placeholder: string;
  /**
   * The line under the field, when this vertical's reader wants a different one
   * from {@link ORG_PROFILE_HELP}. Absent falls back to that default, which is
   * what the business vertical does and what an unregistered vertical gets.
   */
  readonly help?: string;
}

/**
 * The label and placeholder **per vertical**, keyed by `org.vertical` exactly as
 * the wire spells it — the same shape as `SUBJECT_STATUS_LABELS` above, and for
 * the same reason: the vocabulary belongs to the vertical, not to the field.
 *
 * The placeholder is an instruction and never an example. It was a worked
 * example once — a fictional org with a town, a founding year and a founder —
 * and a fabricated fact in a placeholder is a fabricated fact on the page: it
 * sits in the exact spot the reader's own sentence goes, in the shape of an
 * answer. Nothing here invents anything about the reader's org.
 *
 * **The club's row is gone** (2026-09-03), because a club is not asked for a
 * profile at all: nothing a club run derives reads one. The entry was live copy
 * for a field that is no longer on a club's Settings page, and a label kept for
 * a control nobody renders is a label that quietly comes back.
 */
export const ORG_PROFILE_FIELD_COPY: Readonly<Record<string, OrgProfileFieldCopy>> = {
  business: {
    label: 'About the company',
    placeholder: 'About the company, in your own words.',
  },
  /**
   * **The wealth vertical's row** (wealth-vertical note §Settings, ruled
   * 2026-09-16), and the first one that asks for two things in one paragraph:
   * who the office is, and who it serves. The prospect read (`wealth_fit`) reads
   * each person against it.
   *
   * One label for both seats — a wealth manager and a family office — because
   * nothing stored says which an org is and nothing should. "Your organization"
   * rather than "the firm" or "the office" for the same reason.
   *
   * The placeholder is **guided and still an instruction**: four moves in the
   * order the paragraph reads best, and not one invented fact. The rule the club
   * and business rows are held to holds here too — a worked example sits in the
   * exact spot the reader's own sentence goes, in the shape of an answer, and a
   * fabricated fact in a placeholder is a fabricated fact on the page.
   *
   * The help's second sentence is what the analysis prompt enforces
   * (`analysis_wealth_fit.md`: the office's section describes it and is never an
   * instruction), said to the office too.
   */
  wealth: {
    label: 'About your organization and who you serve',
    help: 'Research reads each person against this. It describes you and is not taken as instructions.',
    placeholder:
      'Who you are: what your organization does, where, since when, who founded it.\n\n' +
      'Who you serve best: how they made their money, their stage of life, and the size ' +
      'and complexity of their affairs.\n\n' +
      'What a good relationship looks like, and what you are not looking for.\n\n' +
      'Where you already are: the communities, institutions and cities you are known in.',
  },
};

/**
 * What the field would say for a vertical with no row above.
 *
 * Not a club or business word anywhere in it — "In your own words." is the
 * instruction with the noun taken out. Since 2026-09-03 the Settings page does
 * not reach it: a vertical with no reader for the profile is not asked for one,
 * and a vertical this build does not carry is not asked either. It stays
 * because these two accessors are **total** — they answer for any string — and
 * a helper that returned `undefined` for the case its one caller has already
 * ruled out would push a null check onto the page for nothing.
 */
const ORG_PROFILE_FALLBACK: OrgProfileFieldCopy = {
  label: 'About the organization',
  placeholder: 'In your own words.',
};

/** The org-profile field's label for a vertical — never another vertical's word. */
export function orgProfileLabel(vertical: string): string {
  return ORG_PROFILE_FIELD_COPY[vertical]?.label ?? ORG_PROFILE_FALLBACK.label;
}

/** The org-profile field's placeholder for a vertical — an instruction, never an example. */
export function orgProfilePlaceholder(vertical: string): string {
  return ORG_PROFILE_FIELD_COPY[vertical]?.placeholder ?? ORG_PROFILE_FALLBACK.placeholder;
}

/**
 * The line under the org-profile field — this vertical's own where its reader
 * asks for something more specific, and {@link ORG_PROFILE_HELP} otherwise.
 *
 * Total like the two above, and falling back to the shared line rather than to
 * nothing: every vertical shown this field owes the office a sentence saying what
 * the field is for, and the default one is true of all of them.
 */
export function orgProfileHelp(vertical: string): string {
  return ORG_PROFILE_FIELD_COPY[vertical]?.help ?? ORG_PROFILE_HELP;
}

/**
 * The live count under a capped prose field, in the plainest form there is: what
 * has been written, over what is allowed. No unit word — the cap is beside it,
 * so "1,840 characters of 2,000" says nothing the bare pair does not, and this
 * line sits under a field the eye passes on the way to the next one.
 *
 * The caps themselves are never spelled here: they come from `@ni/shared`, which
 * is where the server reads them from too, so a cap that moves moves in one
 * place.
 */
export function characterCount(length: number, cap: number): string {
  return `${length} / ${cap}`;
}

/**
 * The one line a field earns by going past its cap — shown only then, because
 * the count is the signal everywhere else.
 *
 * It says how far over rather than "too long", since the only useful thing to
 * know at this point is how much to cut. The server's own refusal messages
 * (`CLUB_VALUES_TOO_LONG`, `ORG_PROFILE_TOO_LONG`) are unchanged and still say
 * what the field is for; this is the count's own sentence, and the office should
 * never reach the server's.
 */
export function overLimitNote(over: number): string {
  return `Over the limit by ${plural(over, 'character', 'characters')}.`;
}

/**
 * The line a **note** earns by going past its cap, and it is a different
 * sentence from the settings one on purpose.
 *
 * A settings field is one paragraph that has to fit, so the useful thing to say
 * is how much to cut. A note is not: the text is already written, none of it is
 * surplus, and the way through is two notes rather than a shorter one. So the
 * line says what to do instead of how far over — the count above it is already
 * carrying the number.
 */
export const PROSE_NOTE_OVER_LIMIT = 'Over the limit. Paste this in parts.';

/**
 * **And the same line for a box with no splitter behind it**, which is a
 * different instruction and therefore different words
 * (`docs/notes/2026-09-14-split-giant-intake-note.md`).
 *
 * An organization's own statement is read **inside the request that files it**,
 * under its own restriction, and nothing cuts it into pieces first. "Paste this
 * in parts" would be an instruction the box cannot honour — there is no second
 * paste that joins the first — so the honest thing to ask for is a shorter
 * paragraph, which is what the office would have to write anyway.
 */
export const PROSE_STATEMENT_OVER_LIMIT = 'Over the limit. Shorten this.';

/* ————————————————————————— a note filed as pieces ————————————————————————— */

/**
 * **What a note's reading is doing, in the rail's quiet words.**
 *
 * They were literals inside the overview until a paste needed the same two words
 * in two places — under a piece and, counted, in the header above it — and two
 * copies of `'reading…'` is how a surface ends up saying "reading…" in one place
 * and "being read" in another.
 */
export const NOTE_READING = 'reading…';
export const NOTE_NOT_READ = 'not read yet';

/**
 * **A confidential note** (ruled 2026-09-19): on the record, out of research.
 * The rail's clause under the words, the add box's hover, and the add box's
 * answer — three places, one rule, so they cannot drift into three rules.
 */
export const NOTE_CONFIDENTIAL = 'confidential';
export const NOTE_CONFIDENTIAL_HINT = 'Confidential notes are not referenced in research.';
export const NOTE_SAVED_CONFIDENTIAL = 'Saved as confidential. It will not be used in research.';

/** An owner's rewrite, said beside the author: the words are no longer only theirs. */
export const noteEditedBy = (name: string | null): string => `edited by ${name ?? 'office'}`;
export const NOTE_EDIT_LINE = 'Saving reads the note again and updates the record to match.';
export const NOTE_REMOVE_LINE =
  'This removes the note, and anything on the record that only this note said.';

/**
 * **How a paste is announced on the rail** (split-giant-note note, 2026-09-14).
 *
 * A note over the splitter's threshold is stored as several rows, and the rail
 * shows them as one entry. The header has to say that plainly, because the
 * office pasted one thing and would otherwise be looking at a count they never
 * asked for — "pasted in 8 pieces" is the whole explanation, and the pieces are
 * one click under it.
 */
export function pastedInPieces(pieces: number): string {
  return `pasted in ${plural(pieces, 'piece', 'pieces')}`;
}

/**
 * **How far the reading of a paste has got**, as one clause in the header.
 *
 * Three states and a deliberate order of precedence. Work still under way wins,
 * however many pieces have already failed: what the office is being told is that
 * the record is still changing, and a count of failures over a live reading
 * would ask them to act on a number that is about to move. With nothing in
 * flight, a failure is the thing worth saying; with nothing in flight and
 * nothing failed, the paste is read and the header says so rather than going
 * silent, which on a folded entry would read as a state nobody had bothered to
 * report.
 */
export function pastedReadingState(counts: {
  pieces: number;
  read: number;
  reading: number;
  failed: number;
}): string {
  if (counts.reading > 0) return `${NOTE_READING} (${counts.read} of ${counts.pieces} read)`;
  if (counts.failed > 0) return `${counts.failed} ${NOTE_NOT_READ}`;
  return `${counts.read} of ${counts.pieces} read`;
}

/**
 * **The banner's row for a paste being read**, which is one row and not eight.
 *
 * Eight rows saying "Reading the note…" over one gesture is a banner that has
 * stopped being information. The label says a paste is being read; the detail
 * carries the only two numbers that change under the reader, and the hour it
 * started where the server recorded one.
 */
export const READING_PASTE_LABEL = 'Reading a pasted note…';

export function pasteReadingDetail(read: number, pieces: number, clock: string): string {
  const progress = `${read} of ${pieces} read`;
  return clock.length === 0 ? progress : `${progress}, started ${clock}`;
}

/**
 * **The banner's row for a piece that stopped**, one per piece and each with its
 * own Retry.
 *
 * A piece is named by its place in the paste rather than by its words: the
 * office typed one note, so an excerpt of piece 3 quotes something they never
 * wrote as a unit and would send them looking for a note that does not exist.
 * The excerpt is still on the row underneath — it is what the reading was about
 * — but the sentence leads with the number.
 */
export function pieceFailedLabel(pieceSeq: number): string {
  return `Piece ${pieceSeq} of a pasted note could not be read`;
}

/* ————————————————————————— the identity question ————————————————————————— */

/**
 * The more-prose prompt's headline, **by branch and never inferred**: the
 * payload's `origin` names which of the four asked (ui-design §4.5).
 *
 * `over_fifteen` is the only one that carries a number, and it carries it
 * because the number is the whole reason there is no list to look at: past
 * fifteen the office is told how many matched and asked for a detail, rather
 * than handed a page of names that all read the same. `over_five` says nothing
 * about its count — the held list is right there underneath, and counting it out
 * loud would be telling the reader what they can see.
 */
export function moreProseHeadline(origin: MoreProseOrigin, candidateCount: number): string {
  switch (origin) {
    case 'over_five':
      return 'Too many matches. A detail would narrow it.';
    case 'over_fifteen':
      return `Too many matches to show (${candidateCount}). A detail would narrow it.`;
    case 'zero':
      return 'Nothing matched. Add a detail to search again.';
    case 'none_of_these':
      return 'None of these. Add a detail to search again.';
  }
}

/**
 * The held list's own line, above the rows. It is a **second way to answer**, not
 * a receipt of what was found, so it reads as an offer: the detail box above
 * narrows the search, and this settles it outright.
 */
export const HELD_CANDIDATES_HEAD = 'Or if one of these is them, say so:';

/**
 * A candidate card's match line: a detail on the office's record that a source
 * agreed with, named as the record holds it ("Baghram and Kandahar,
 * Afghanistan"). The record covers what the office confirmed, filed or wrote in
 * a note, which is every place a match can name.
 */
export const matchesRecordLine = (value: string): string => `Matches your record: ${value}`;

/**
 * What a match row is about, for the rare row that names no value and quotes no
 * words: the card still says why it ranks where it does.
 */
const MATCH_KIND_WORDS: Readonly<Record<string, string>> = {
  location: 'a location',
  role_org: 'an employer',
  employer: 'an employer',
  org: 'an organization',
  affiliation: 'an affiliation',
  education: 'a school',
  known_to: 'someone they know',
  age: 'their age',
  email: 'an email address',
  phone: 'a phone number',
  handle: 'an account',
  member_number: 'a member number',
  registry_id: 'a registration number',
  license_id: 'a license number',
  tax_id: 'a tax number',
};

export const matchKindWords = (anchor: string): string => MATCH_KIND_WORDS[anchor] ?? 'a detail';

/** The shortcut on a held row — the same gesture as the pick list's Confirm. */
export const SELECT_CANDIDATE = 'Select this person';
export const SELECT_CANDIDATE_WORKING = 'Selecting…';

/**
 * **"These cards are one person"** on the pick list (2026-10-12). The search
 * can show one person as two cards — a records card with a birth date and a
 * spouse, a web card with an employer — and a single Confirm would record the
 * other card as somebody else. Checking every card that is this person binds
 * them together and sets aside only the rest.
 */
export const SAME_PERSON_OPEN = 'Two or more of these are the same person';
export const SAME_PERSON_ASK = 'Check every card that is this person.';
export const SAME_PERSON_THIS = 'This person';
export const SAME_PERSON_OTHER = 'Someone else';
export const SAME_PERSON_CANCEL = 'Cancel';
export const SAME_PERSON_WORKING = 'Confirming…';
export const SAME_PERSON_TOO_FEW = 'Check at least two cards. For one card, use its own Confirm.';

export function samePersonConfirmLabel(checked: number): string {
  return checked < 2 ? 'Confirm as one person' : `Confirm ${checked} as one person`;
}

/** What the press will do, said before it is pressed. */
export function samePersonOutcome(checked: number, total: number): string {
  const others = total - checked;
  if (others === 0) return `The details on all ${checked} cards will be kept as this person.`;
  return `The details on the checked cards will be kept as this person, and ${
    others === 1 ? 'the other card' : `the other ${others} cards`
  } will be recorded as someone else.`;
}

/**
 * **Cards the search thinks are one person** (2026-10-14): drawn together under
 * one heading on the pick list and the held list, with what they share, what
 * disagrees, and one button that confirms them all.
 */
export const LIKELY_SAME_HEAD = 'Probably one person';
export const LIKELY_SAME_SHARED = 'Both:';
export const LIKELY_SAME_CONFLICT = 'But:';

export function likelySameConfirmLabel(count: number): string {
  return `Confirm these ${count} as one person`;
}

export function likelySameFoot(count: number): string {
  return count === 2
    ? "Confirming one card leaves the other open. It doesn't mark it as someone else."
    : `Confirming one card leaves the other ${count - 1} open. It doesn't mark them as someone else.`;
}

/**
 * The set-aside bin (ui-design §3.6 — nothing suppressed goes into nothing).
 *
 * "Set aside" rather than "ruled out": the pick list's bin holds names an
 * earlier *answer* rejected, and this one also holds names the search itself put
 * down as impossible — a namesake who has died being the case that prompted it.
 * Neither is a rejection the office made, so neither is announced as one.
 */
export function setAsideBinLabel(count: number): string {
  return `${plural(count, 'name', 'names')} set aside`;
}

export const SET_ASIDE_BIN_OPEN_LABEL = 'Hide the names set aside';

/**
 * Why one name was set aside. The pipeline's own sentence when it wrote one;
 * otherwise the anti-anchor that ruled it out, in the pick list's words. An
 * entry with neither says the plain fact rather than showing an empty line —
 * the row is in a bin *of* set-aside names, so the sentence has somewhere to
 * stand.
 */
export function setAsideReason(entry: {
  reason?: string;
  anti_anchor: Record<string, unknown>;
}): string {
  if (entry.reason) return entry.reason;
  const attributes = Object.entries(entry.anti_anchor)
    .map(([key, value]) => `${key} ${String(value)}`)
    .join(' · ');
  return attributes ? `Ruled out by an earlier answer: ${attributes}` : 'Set aside by the search.';
}

/* ————————————————————————— the likely-merge question ————————————————————————— */

/**
 * The clause that marks a side as one of the office's own people, in the
 * People list's own word for it (copy audit §3 #8).
 */
export const MERGE_CANDIDATE_TRACKED = 'tracked';

/** What each side carries, under its name. Lower case — it is a clause, not a heading. */
export function mergeCandidateSideLine(side: {
  connection_count: number;
  tracked: boolean;
}): string {
  return joinClauses([
    plural(side.connection_count, 'connection', 'connections'),
    side.tracked ? MERGE_CANDIDATE_TRACKED : null,
  ]);
}

/**
 * The two answers, in the office's own words rather than the wire's. "Same" and
 * "Different" are what a person says out loud looking at two names; `same` and
 * `different` are what goes on the wire, and the two never have to be the same
 * string.
 */
export const MERGE_CANDIDATE_SAME = 'Same';
export const MERGE_CANDIDATE_DIFFERENT = 'Different';
export const MERGE_CANDIDATE_DIFFERENT_WORKING = 'Saving…';

/**
 * **Same asks twice.** Different is remembered and nothing else, so it answers on
 * the first click; Same folds two records into one and leaves a redirect, and
 * there is no undo in the product for it — so it goes through the same confirm
 * the delete and the wrong-person do, in the same modal grammar.
 */
export const MERGE_CANDIDATE_CONFIRM_HEAD = 'Join these two';
export const MERGE_CANDIDATE_CONFIRM_LINE = `These two will become one record. ${CANNOT_BE_UNDONE}`;
export const MERGE_CANDIDATE_CONFIRM_ACTION = 'Join them';
export const MERGE_CANDIDATE_CONFIRM_WORKING = 'Joining…';

/**
 * **The name the one record keeps** (organisation-identity note §4), asked in
 * the confirm and nowhere else.
 *
 * It belongs there because it is part of the same act: the office is about to
 * turn two records into one, and "which of these two names does the one record
 * answer to" is the second half of that sentence, not a separate question. On
 * the card it would be a control with nothing to do with the two answers beside
 * it; in the confirm it is the last thing looked at before the join.
 *
 * It is asked **only when the two names differ**. Two records spelled the same
 * have no choice in them, and a radio with one answer twice is a question that
 * makes a reader look for the difference.
 */
export const MERGE_CANDIDATE_KEEP_ASK = 'Which name should the record keep?';

/**
 * Why picking the other one is safe, in one clause. It is the whole reason the
 * choice can be offered at all: the spelling that is not kept stays pointed at
 * the record, so a note, an import or a piece of research that uses it still
 * lands on the right person rather than making a second one.
 */
export const MERGE_CANDIDATE_KEEP_NOTE = 'The other spelling will still find this record.';

/**
 * **Why the two are being asked about** — the one quiet disclosure on a
 * likely-merge row. The names are already on the row once; the server's reason
 * sentence and anything somebody said would settle it sit behind this rather
 * than repeating the names at rest.
 *
 * A held question opens with it open: what somebody said would settle it is the
 * one thing on the row a reader should not have to go looking for.
 */
export const MERGE_CANDIDATE_WHY = 'Why';

/**
 * **Merged automatically** — the home screen's band of the answers merge
 * triage gave without asking (merge triage spec §3): one sentence each, written
 * by the server, with the reason in it, and an Undo.
 */
export function mergedAutomaticallyHead(count: number): string {
  return `Merged automatically · ${count}`;
}

/** The band's one control: a merge is unmerged, a "different" is asked again. */
export const TRIAGE_UNDO = 'Undo';
export const TRIAGE_UNDO_WORKING = 'Undoing…';

/**
 * **"What would settle it"** — what somebody said would settle this question,
 * inside the row's Why under the reason (reconcile-through-the-connector note
 * §2.4).
 *
 * It reads as what is missing rather than as a state: the question is still
 * open, nothing about the two records has been written, and answering it from
 * either surface is what clears it. There is no separate clear, so there is no
 * control here — only a sentence and who left it.
 */
export const MERGE_CANDIDATE_NEEDS_INFO = 'What would settle it:';

/**
 * Who left it and when, as one quiet clause under the sentence.
 *
 * **The channel is said only when there is one to say.** A sentence typed in the
 * app reads "asked by Lew"; one written through the connector says so, because
 * "asked through the connector" is the difference between a colleague who looked
 * at this pair and a pass somebody ran over the whole band. A name the account
 * never had is left out rather than printed as a blank — the sentence is the
 * thing the reader came for, and an attribution to nobody is worse than none.
 */
export function mergeCandidateNeedsInfoLine(
  by: string | null,
  via: 'connector' | null,
  day: string,
): string {
  const asked =
    via === 'connector'
      ? by
        ? `asked through the connector by ${by}`
        : 'asked through the connector'
      : by
        ? `asked by ${by}`
        : 'asked';
  return joinClauses([asked, day]);
}

/**
 * **"Lew, through the connector: same firm, the website matches"** — the actor
 * clause on a decision-log row answered from the connector rather than in the
 * app (§3).
 *
 * The member's name is the same name either way and stays first: the office
 * answered, and which hands typed it is the second clause. A dashboard answer
 * renders exactly as it did, because `via` is null there and this is not
 * reached.
 */
export function decisionActorClause(
  name: string | null,
  via: 'connector' | null,
  reason: string | null,
): string | null {
  const actor =
    via !== 'connector' ? name : name ? `${name}, through the connector` : 'through the connector';
  if (!reason) return actor;
  /* The reason hangs off the actor rather than standing beside it: "Lew, through
     the connector: same firm, the website matches" is one statement about who
     said what, and a second clause would put the reason next to the date. A
     reason with nobody to attribute it to stands on its own rather than opening
     with a colon. */
  return actor ? `${actor}: ${reason}` : reason;
}

/**
 * **What that answer reads as once somebody else has given it.** A second reader
 * on the same card gets `DECISION_ALREADY_ANSWERED`, and the card shows the
 * standing answer in place rather than an error (`api/errors.ts`).
 *
 * The name is in the line because it is the whole of what the reader wanted to
 * know: the pair is settled either way, and the only thing still open in their
 * head is which of the two spellings won. "Answered a moment ago" — the generic
 * line this arm used to fall through to — leaves that unanswered on the one
 * answer that has a second half.
 *
 * No "a moment ago". The neighboring lines are one verb each and can afford the
 * recency; this one already carries a name, and three clauses plus a timestamp
 * is a sentence nobody finishes.
 */
export function mergeKeptNameLine(name: string): string {
  return `Said two records were the same, joined them, and kept the name ${name}.`;
}

/**
 * **The third answer** (organisation-identity note §1.3). The University of
 * Texas cluster is the case it was written for: a campus and its system, or a
 * system and its board of regents, are neither one record nor two strangers, and
 * with only Same and Different on the card the office had to throw one of those
 * readings away.
 *
 * **The label names the door and not one of the readings behind it.** It read
 * "One is part of the other" while containment was the step's default and the
 * other two readings were corrections of it; the step now offers five equal
 * sentences, of which "part of" is one, and a door labelled after one of its
 * five answers is a door that has already answered. "Related, not the same" is
 * what the office is saying by opening it — these two belong together, and they
 * are not one record — with which way and which side left to the step.
 *
 * Organisations only. Two people are one person or two, and there is no fact in
 * the catalog this button could write about them.
 */
export const MERGE_CANDIDATE_RELATED = 'Related, not the same';

/**
 * **One question, asked once, and answered with a whole sentence** — and the
 * same words the knowledge panel's Link door asks in, which aliases this
 * constant ({@link KNOWLEDGE_LINK_RELATION_ASK}) rather than repeating it.
 *
 * It was two questions before — which of the two is the whole, then what the
 * other one is to it — and the reader had to hold a direction and a verb apart
 * and put them back together from two lit buttons. A reading says both at once:
 * "HBS is part of Harvard Business School" is one thing to agree with or not,
 * and it is the only way a reader can be sure which way round they have said it.
 */
export const MERGE_CANDIDATE_READING_ASK = 'Which of these is true?';

/** What is about to be recorded, word for word. The Link door says the same. */
export const MERGE_CANDIDATE_RECORD_LEAD = 'This will be recorded as:';

export const MERGE_CANDIDATE_RELATED_ACTION = 'Record it';
export const MERGE_CANDIDATE_RELATED_WORKING = 'Recording…';

export { orgRelationSentence } from '@ni/shared';

/* ————————————————————————— the rename proposal ————————————————————————— */

/**
 * **"This looks like it is really called Pogue Holdings."** — the inbox card for
 * a pending `entity_rename` a lookup filed (organisations-as-subjects note §2
 * chunk 3a, "As grounded" 6).
 *
 * It sits beside the likely-merge card rather than under a person's heading, and
 * for the same reason that one does: the record it is about is an organisation,
 * which has no status to print a heading under and need not be anybody the
 * office follows. The two cards are the whole of what the inbox carries that is
 * not about a person.
 *
 * The heading is a question at a question's size, as the merge card's is,
 * because the row that follows is a record and not a name.
 */
export const RENAME_PROPOSAL_TITLE = 'A name to confirm';

/** The proposal itself, in the run's own claim. */
export function renameProposalAsk(toName: string): string {
  // A name that already ends in a period ("Inc.") takes no second one.
  return `This looks like it is really called ${toName.replace(/\.$/, '')}.`;
}

/**
 * The two answers. Yes names the act; No names **the name that stays**, because
 * "No" alone leaves a reader working out which of the two spellings they just
 * voted for. The same asymmetry the merge card's Same/Different avoids by having
 * two words that are already opposites.
 */
export const RENAME_PROPOSAL_YES = 'Rename it';
export const RENAME_PROPOSAL_YES_WORKING = 'Renaming…';
export function renameProposalNo(fromName: string): string {
  return `Keep ${fromName}`;
}
export const RENAME_PROPOSAL_NO_WORKING = 'Saving…';

/* ————————————————————————— the relationship reading ————————————————————————— */

/**
 * The `relationship_read` block's own words — the reading that answers "what is
 * this person to us", which the business vertical asks for at every status.
 *
 * "What each side might want" rather than "Angles": the wire key is `angles`
 * because that is what the reading is, and the office reads a plain sentence.
 * The head is a noun, like Fit beside it, because it names a reading and not an
 * instruction.
 */
/**
 * **The security vertical's two readings** (security fold, 2026-09-22).
 *
 * `exposure_brief` is what the public record makes knowable about a principal;
 * `case_read` is what is documented about a subject of interest in relation to
 * the engagement. Both are prose, and the sub-heads say what the reading is of
 * rather than what it concludes: neither names a threat, scores anything, or
 * says a person is dangerous, and a sub-head implying otherwise would be the
 * page making the claim the readings refuse to.
 */
export type ProtectiveReadKind = 'exposure_brief' | 'case_read';

export const PROTECTIVE_READ_HEADS: Record<ProtectiveReadKind, string> = {
  exposure_brief: 'Exposure',
  case_read: 'Case analysis',
};

export const PROTECTIVE_READ_SUBS: Record<ProtectiveReadKind, string> = {
  exposure_brief: 'What the public record makes knowable about this person.',
  case_read: 'What is documented, and who reported it.',
};

export const RELATIONSHIP_HEAD = 'Relationship';

/** Labelled as interpretation where it sits among facts (ui-design §3.9). */
export const RELATIONSHIP_SUB = 'Read in relation to your organization.';

export const RELATIONSHIP_LIST_LABELS = {
  connections: 'Connections',
  angles: 'What each side might want',
} as const;

/**
 * **The wealth vertical's prospect read** (`wealth_fit`), which is the fit rows
 * and the angles in one block.
 *
 * "Fit" alone would collide with the club's block in a reader's head and in this
 * file; "Fit and interest" says what the block actually holds — the rows against
 * what the office wrote, and what each side plausibly wants. The sub is the
 * interpretation label the page owes wherever a reading sits among facts
 * (ui-design §3.9), and it names the office's own paragraph rather than "our
 * values", because that is the thing the read was held against.
 *
 * **No terminal period on the sub**, exactly as the club's Fit sub has none: the
 * block appends the as-of clause to it (` · as of Jun 2024`), and a full stop
 * before a middot reads as two fragments rather than one line.
 */
export const WEALTH_FIT_HEAD = 'Fit and interest';

/** The band's label on the record, at the head of Finances. */
export const NET_WORTH_LABEL = 'Estimated net worth';
/** Where the record does not support an estimate. */
export const NET_WORTH_NO_DATA = 'No data';

export const WEALTH_FIT_SUB = 'Read against what your organization does and who it serves';

/**
 * The angles list's label, deliberately the relationship read's own words: the
 * list is the same shape, capped the same way, and asks the same question. A
 * second phrasing would be a second thing to keep true.
 */
export const WEALTH_FIT_ANGLES_LABEL = RELATIONSHIP_LIST_LABELS.angles;

/** The org's sensitivity setting, as the office picks it on Settings. */
export const ADJUDICATION_SENSITIVITY_LABELS: Record<AdjudicationSensitivity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

/**
 * **The run words live in `@ni/shared` now** (2026-09-10), re-exported here so
 * every call site on this side is unchanged — the same move, on the same day and
 * for the same reason, as `SOURCE_TIER_LABELS` at the top of this file.
 *
 * The MCP connector projects `latest_run.kind` and `latest_run.state` to a model
 * that repeats whatever string it is handed, and `prose_refresh` or
 * `awaiting_adjudication` read out to the office is the same defect there as a
 * token on a history row would be here. Both maps and their doc comments moved
 * to sit beside `RUN_KINDS` and `RUN_STATES` themselves.
 *
 * Imported above rather than re-exported straight through, because `runLabel`
 * further down this file reads `RUN_KIND_LABELS` itself and an
 * `export … from` binds nothing locally.
 */
export { RUN_KIND_LABELS, RUN_STATE_LABELS };

/* ————————————————————————— what a later run found ————————————————————————— */

/**
 * A later Re-run's or Refresh's one line (dossier Re-run and Refresh spec §3.5):
 * "Found 3 new, confirmed 11, 1 changed", or "Nothing new since Sep 1, 2026"
 * when it added and changed nothing.
 */
export function runFindingsLine(entry: RunFindings): string {
  const found = entry.new.length;
  const changed = entry.changed.length;
  const confirmed = entry.confirmed.length;
  if (found === 0 && changed === 0) {
    return entry.since ? `Nothing new since ${formatDay(entry.since)}` : 'Nothing new';
  }
  const rest = [
    confirmed > 0 ? `confirmed ${confirmed}` : null,
    changed > 0 ? `${changed} changed` : null,
  ];
  const parts = found > 0 ? [`Found ${found} new`, ...rest] : [rest[1], rest[0]];
  return parts.filter(Boolean).join(', ');
}

/** A later run's coverage of one area, in words; an empty area is "nothing new", not a blank. */
export const RUN_COVERAGE_LABELS: Record<FacetCoverage['status'], string> = {
  complete: 'searched',
  empty: 'nothing new',
  partial: 'partly checked',
  unavailable: 'source unavailable',
  skipped: 'not searched',
  failed: 'search failed',
  unknown: 'not recorded',
};

/* ————————————————————————— run narration ————————————————————————— */

/**
 * A facet key as the office reads it. The registry is server-side and can grow
 * past the stage-1 mirror in `@ni/shared`, so an unknown key is humanized rather
 * than shown raw — `facet_progress` is keyed by whatever the pipeline sent.
 */
/**
 * **The organization lookup's four legs have no words here**, and that is the
 * ruling of 2026-09-10 rather than an omission. They were never in `FACET_KEYS`
 * and so never in the shared `FACET_LABELS` (organisations-as-subjects note §2
 * chunk 3 "As grounded" 1), and the one surface that named them — the strip's
 * facet ledger — no longer runs for `org_lookup`: a lookup's leg is not a step
 * of the timeline, and a row reading "The organization's own site — not started"
 * was answering a question the timeline had not asked.
 *
 * `facetLabel` is therefore the person registry plus the humanized fallback,
 * which is all a person's six legs ever needed.
 */
export function facetLabel(key: string): string {
  return (FACET_LABELS as Record<string, string>)[key] ?? humanize(key);
}

type FacetPhase = NonNullable<RunDetail['facet_progress']>[string]['phase'];

/**
 * What one facet is doing, in end-result words. `pending` is the client's own
 * derivation — a planned facet the pipeline has said nothing about yet — and it
 * says "not started" rather than "queued", because a queue is machinery and the
 * office is being told whether work has begun.
 */
export const FACET_PHASE_LABELS: Record<FacetPhase | 'pending', string> = {
  pending: 'not started',
  retrieving: 'gathering sources',
  extracting: 'reading sources',
  posting: 'filing findings',
  complete: 'done',
};

/**
 * **Stop — the office ending a run it started** (owner ruling, 2026-09-10; the
 * word is the glossary's, copy audit §3 row 11: "Stop" for runs, "Cancel" only
 * to dismiss a dialog).
 *
 * The confirm answers the question the office is actually holding — *do I lose
 * what it found?* — because every claim the run landed stays on record. The way
 * out is **"Keep going"**, named for what it does to the run.
 *
 * `RUN_STOPPED` is what the run says afterwards, and it is deliberately not a
 * failure line: "Research failed" over a run somebody stopped on purpose would
 * report the office's own decision back to them as bad news. It is the server's
 * sentence (`CANCELLED_BY_OFFICE`) minus the full stop.
 */
export const RUN_CANCEL = {
  action: 'Stop',
  working: 'Stopping…',
  confirmHead: 'Stop run',
  confirmLine: 'Stop this run? What it has read so far stays on record.',
  confirm: 'Stop the run',
  dismiss: 'Keep going',
} as const;

/** What the strip says about a run the office stopped. */
export const RUN_STOPPED = 'Stopped by the office';

/** Resume was pressed and the run has not moved after the stall window. */
export const RESUME_STALLED_LINE = 'The run has not resumed. Press Resume again.';

/* ————————————— the person's run block and Research tab (blend spec, step 4) ————————————— */

/** The run block's label, over its start time: which kind of run this is. */
export function runBlockLabel(kind: RunKind, pass: ResearchPass | null): string {
  if (kind === 'quick_search') return 'Quick search';
  if (kind === 'prose_refresh') return 'Rewrite';
  if (kind === 'event_scan') return 'Attendee scan';
  if (kind === 'report_generate') return 'Report';
  if (pass === 'refresh') return 'Refresh';
  return 'Research run';
}

/** "3 of 6 sections done": the plan, counted, and nothing about time. */
export function sectionsDoneLine(done: number, planned: number): string {
  return `${done} of ${planned} ${planned === 1 ? 'section' : 'sections'} done`;
}

/** Whether a live run needs anyone, said under what it is doing. */
export const RUN_BLOCK_NOTES = {
  working: 'Nothing for you to do. Sections fill in below as each one finishes.',
  workingPlain: 'Nothing for you to do.',
  quiet:
    'Some stages run for several minutes without reporting in. Nothing for you to do; if the run stops, this line will say so.',
  failed: 'What it found so far stays on record. Resume picks the run back up.',
  /** A Resume the server accepted, waiting for the run to start again. */
  resumeQueued: 'Starting again. This can take a few minutes; nothing for you to do.',
  complete: 'Opening the brief.',
  beforeStopped: 'before it stopped',
} as const;

/** The run block's one disclosure: the ledger, the duplicates and the timeline. */
export const RUN_DETAIL_LABEL = 'Run detail';

/**
 * **"Brief ready"**, shown once: only when the run finishes while the person is
 * on the page, never on a later visit (blend spec, "Run complete").
 */
export function runCompleteHead(kind: RunKind): string {
  switch (kind) {
    case 'quick_search':
      return 'Quick search done';
    case 'prose_refresh':
      return 'Brief rewritten';
    case 'event_research':
      return 'Event research done';
    case 'event_scan':
      return 'Scan done';
    case 'report_generate':
      return 'Report written';
    default:
      return 'Brief ready';
  }
}

/**
 * **A completed run that delivered nothing**, said the same way on its own page and
 * on the credits page: both read the run's delivery receipt (`RunSummary.delivered`),
 * which is what it was charged on.
 */
export const RUN_UNDELIVERED_HEAD = 'Finished';
export function undeliveredLine(kind: RunKind | null): string {
  switch (kind) {
    case 'quick_search':
      return 'No confirmed match';
    case 'org_lookup':
    case 'event_research':
      return 'Nothing found';
    case 'event_scan':
      return 'Nobody screened';
    default:
      return 'Incomplete';
  }
}

/** The navigator's group heads and the lines a group shows before it has entries. */
export const SECTION_NAV = {
  brief: 'Brief',
  research: 'Research',
  briefLater: 'Written when the research finishes.',
  briefAfterReview: 'Written after your review.',
  notYet: 'not yet',
  sourceNotes: 'Source notes',
  phoneLabel: 'Sections',
} as const;

/** "Show the 44 facts behind this · 12 sources": the panel's one disclosure. */
export function evidenceDisclosureLabel(facts: number): string {
  return facts === 1 ? 'Show the 1 fact behind this' : `Show the ${facts} facts behind this`;
}

/** How one source fared, after its name in the panel's "Searched" line. */
export const SEARCHED_STATUS_WORDS: Record<FacetCoverage['status'], string> = {
  complete: 'searched',
  empty: 'nothing found',
  partial: 'partly available',
  unavailable: 'not available',
  skipped: 'not searched',
  failed: 'search failed',
  unknown: 'not recorded',
};

/** Inside a panel's disclosure. */
export const EVIDENCE = {
  searched: 'Searched',
  onRecord: 'On record',
  checks: 'Records checks',
  findings: 'Findings',
  source: 'Source',
  expandAll: 'Expand all',
  collapse: 'Collapse',
  pendingReview: 'pending review',
  needsReview: 'needs review',
} as const;

export function filterFactsPlaceholder(count: number): string {
  return `Filter ${plural(count, 'fact', 'facts')}`;
}

export function moreFromSourceLine(count: number): string {
  return `${count} more from this source`;
}

export function moreSourcesLine(sources: number): string {
  return `Show ${plural(sources, 'more source', 'more sources')}`;
}

/**
 * A records check or a scored finding, in words: calm for a clean check, the
 * level in copper for an adverse one. Never a score.
 */
export const SEVERITY_WORDS = {
  none: 'Clear',
  info: 'Noted',
  moderate: 'Moderate',
  severe: 'Severe',
} as const;

/** The research aside. */
export const RESEARCH_ASIDE = {
  status: 'Status',
  identity: 'Identity',
  research: 'Research',
  notes: 'Notes',
  connections: 'Connections',
  confirmed: 'Confirmed',
  notConfirmed: 'Not confirmed',
  peopleOnRecord: 'People on record',
  noPeople: 'No ties to anyone else on record.',
} as const;

/** The Overview's side panel: the labels it adds to the Research aside's. */
export const OVERVIEW_ASIDE = {
  added: 'Added',
} as const;

/** The aside's research line: one word for where the research stands. */
export const RESEARCH_STATE_WORDS = {
  none: 'None yet',
  starting: 'Starting',
  running: 'Running',
  paused: 'Paused',
  waiting: 'Waiting on you',
  resuming: 'Resuming',
  failed: 'Failed',
  stopped: 'Stopped',
  complete: 'Complete',
  pausedNote: 'waiting on your review',
} as const;

/**
 * The run timeline's row labels: one lookup over **both** `stage_history` kinds,
 * which is safe because the progress-stage ids and the run-state ids are
 * disjoint sets (nothing is named `running` on one side and a stage on the
 * other). Anything unrecognised — a stage this build has not met — falls back to
 * its humanized key rather than disappearing.
 *
 * Every line is end-result language: what the run had finished or begun, never
 * the id the pipeline spells it with.
 */
export const RUN_TIMELINE_LABELS: Record<string, string> = {
  /* progress stages (`t: 'stage'`) */
  quick_search: 'Searching for the person',
  after_initial_pass: 'First pass finished',
  research: 'Searching by section',
  orchestrator: 'Checking for gaps',
  after_orchestrator_round: 'Follow-up round finished',
  before_summary: 'Ready to write up',
  simple_summary: 'Writing the first draft',
  summary: 'Writing the brief',
  /* The spend ceiling, as a stage rather than a state: the run went on to finish,
     and this is the line that says it stopped collecting early. The engine posts
     it once, on the way to `complete`, because `progress` is last-write-wins and
     only a *stage* survives into `stage_history` (`driver.BUDGET_CAPPED_STAGE`).
     Without an entry here the office read the raw key. */
  budget_capped: 'Stopped at the spend limit',
  /* The organisation lookup's one boundary stage (`driver.ORG_LOOKUP_STAGE`).
     Its per-leg stages are `facet:<key>` and the server drops those from the
     timeline outright (`isFacetStage`), so a lookup's history is this line, the
     write-up boundary above, and the states. */
  org_lookup: 'Looking the organization up',
  /* **The first of the engine's three narration lines** for a lookup
     (`server/src/modules/runs/run-state.ts`, `SITE_NOT_READ_STAGE`). The other
     two carry a number and so are parsed below rather than looked up here.

     It is the line that tells a lookup which found nothing apart from a lookup
     which could not read anything: an organisation whose home page says nothing
     about it, and one whose home page never opened, are not the same answer.
     Which URL failed is not said — the engine's business, and the office's move
     is the same either way. */
  site_not_read: 'The organization’s site could not be read.',
  /* The server's own line, beside the cap's: a lookup read a leader's name off
     the page and the record could not tell which person it meant, so the claim
     was left out rather than held (`claim-ingest.service.ts`,
     `SUBJECT_UNRESOLVED_STAGE`). Written plainly and without the name: the point
     is that the leadership list is one short and why, and a name the record
     cannot place is the last thing to put on a timeline the office reads as
     fact. A fixed string here rather than a parsed prefix because this line
     carries nothing. */
  subject_unresolved:
    'A named person could not be told apart from others on record, so they were left out.',
  /* **The office's own line** (`server/src/modules/runs/run-state.ts`,
     `CANCELLED_BY_OFFICE_STAGE`) — the only stage on this list that a person
     wrote rather than a machine. It is on the timeline as well as in the run's
     error because the error is one field the strip stops rendering the moment
     the run leaves the page, and the timeline is what is still there next week
     when somebody asks why this investigation cost a third of the last one. */
  cancelled_by_office: 'Stopped by the office',

  /* accepted transitions (`t: 'state'`) */
  created: 'Run created',
  running: 'Working',
  checkpointed: 'Continuing',
  awaiting_identity_pick: 'Waiting on identity',
  awaiting_prose: 'Waiting on more detail',
  awaiting_adjudication: 'Waiting on review',
  complete: 'Complete',
  failed: 'Failed',
};

/**
 * **The two lines the server writes for itself**, and the reason `timelineLabel`
 * below parses rather than only looking up.
 *
 * A stage entry is `{t, v, at}` and `v` is a bare string, so a line that has to
 * carry a number or a name carries it *in* the id — `people_capped:5`,
 * `name_found_taken:Pogue Holdings` (`server/src/modules/runs/run-state.ts`).
 * The cap differs by depth (5 at a lookup, 20 at an investigation) and the name
 * is the whole of what the second line has to say, so neither can be a fixed
 * string in the table above.
 *
 * The prefixes are the server's ids and are matched exactly; anything after the
 * first colon is the value, which is what lets an organisation called
 * "Rosen: Partners" survive the split.
 */
export const PEOPLE_CAPPED_PREFIX = 'people_capped:';
export const NAME_FOUND_TAKEN_PREFIX = 'name_found_taken:';

/**
 * **The two counted lines the *engine* writes for a lookup**
 * (`run-state.ts`, `pagesReadStage` / `claimsPostedStage`).
 *
 * They are a pair and the pair is the point: pages read with nothing recorded is
 * a run that opened the site and found nothing it could say, which is a real
 * answer and was the one the first prod lookup gave with no way to say so. So
 * both are always shown, including at zero — a line that only appeared when it
 * had a number to boast about would leave the office reading an empty timeline
 * for two quite different runs.
 *
 * Parsed rather than looked up for the same reason the two above are: the count
 * rides in the id because a stage entry's `v` is a bare string.
 */
export const PAGES_READ_PREFIX = 'pages_read:';
export const CLAIMS_POSTED_PREFIX = 'claims_posted:';

/**
 * **The third counted line, and the only one that is an exception report**
 * (`run-state.ts`, `pagesSetAsideStage`).
 *
 * A lookup on an organisation with a confirmed website sets a page aside when
 * the page states a *different* website or a different city for the company it
 * is describing: it is a page about somebody else of the same name, and nothing
 * on it is recorded (`claims/stated-identity.ts`). Medici's second lookup read
 * one such page — a 2018 press release about an Austin telehealth app — and the
 * only visible trace was the wrong company's website appearing on the record.
 *
 * The engine posts it only above zero, so unlike the pair above there is no line
 * on a run where nothing was set aside. That asymmetry is the point: those two
 * are a run's account of its own reading and mean something at zero, this one is
 * "something unusual happened", and a line saying it did not would appear on
 * every timeline there is.
 */
export const PAGES_SET_ASIDE_PREFIX = 'pages_set_aside:';

/**
 * **The fourth counted line, and the second exception report** (`run-state.ts`,
 * `aliasesFoundStage`).
 *
 * Since R10 (2026-09-10) an address printed on a page for this company is an
 * alias rather than a reason to disbelieve the page: a company may answer at
 * several, and `medici.md` — the host that started all of this — turned out to be
 * Medici's own former one. The run says how many it turned up, and the record
 * says which, under the website as "Also seen as".
 *
 * Posted only above zero, like the line above it and for the same reason.
 */
export const ALIASES_FOUND_PREFIX = 'aliases_found:';

/**
 * "Kept five people from this run; the rest were left out."
 *
 * **In words**, because the line is a sentence and the two numbers it can carry
 * are the depth plans' caps — five at a lookup and twenty at an investigation.
 * A figure would read as a tally where what is meant is a rule. Anything else
 * falls back to digits rather than growing a numeral table this line has no use
 * for.
 *
 * **"from this run"**, not "from this page": the cap is the run's, applied
 * across everything it read, and "page" would tell the office that the next page
 * is where the rest went.
 */
const NUMBER_WORDS: readonly string[] = [
  'no',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
  'twenty',
];

/**
 * The count as a word up to twenty and as digits above it — the ordinary rule
 * for prose. Zero is `no`, which is the word a sentence about a count that did
 * not happen wants ("Read no pages"); a line where zero has to be a *noun*
 * rather than a quantity says so itself rather than asking for one here.
 */
function numberWord(count: number): string {
  return NUMBER_WORDS[count] ?? String(count);
}

export function peopleCappedLine(kept: number): string {
  const noun = kept === 1 ? 'person' : 'people';
  return `Kept ${numberWord(kept)} ${noun} from this run; the rest were left out.`;
}

/** "Found the name Pogue Holdings, but another record already goes by it." */
export function nameFoundTakenLine(name: string): string {
  return `Found the name ${name}, but another record already goes by it.`;
}

/** "Read three pages." — and "Read no pages." for a site that gave up nothing. */
export function pagesReadLine(pages: number): string {
  return `Read ${numberWord(pages)} ${pages === 1 ? 'page' : 'pages'}.`;
}

/**
 * "Recorded two findings from what was read."
 *
 * **"Findings"**, the glossary's word for what research surfaced (copy audit
 * §3 #6), whatever catalog type each one is. "From what was read" ties the
 * count to the line above it: these came out of those pages, and a run with
 * pages and no findings is the outcome worth being able to see.
 *
 * Zero is said as a noun rather than as a quantity ("nothing"), because
 * "Recorded no things" is not English.
 */
export function claimsPostedLine(claims: number): string {
  if (claims === 0) return 'Recorded nothing from what was read.';
  return `Recorded ${numberWord(claims)} ${claims === 1 ? 'finding' : 'findings'} from what was read.`;
}

/**
 * "Set aside two pages about a different company."
 *
 * **"A different company"** and not "another organization": the office's word
 * for the thing on the other side of a namesake page is a company, and the
 * sentence has to say in one read why a page the run paid to fetch produced
 * nothing. It does not name the pages — which addresses they were is the
 * engine's business and is in its logs; the office's move is the same either
 * way, which is to check the confirmed website on the record.
 */
export function pagesSetAsideLine(pages: number): string {
  const noun = pages === 1 ? 'page' : 'pages';
  return `Set aside ${numberWord(pages)} ${noun} about a different organization.`;
}

/**
 * "Found two other websites for this organization."
 *
 * **"Other"** because the record already shows one, and this line is about the
 * ones beside it. **"For this organization"** because the whole of R10 is that
 * these are *this* company's addresses rather than evidence of another company —
 * a line reading "found two websites" would leave the office wondering whose.
 *
 * It does not name them: which addresses they were is on the record, under the
 * website, and a timeline that listed them would be saying twice what the office
 * has to go to the record to act on anyway.
 */
export function aliasesFoundLine(aliases: number): string {
  const noun = aliases === 1 ? 'website' : 'websites';
  return `Found ${numberWord(aliases)} other ${noun} for this organization.`;
}

/**
 * The counted lines' shared shape: an exact prefix, then a run of digits.
 *
 * Digits are tested rather than `Number()` because `Number('')` is 0 — an empty
 * suffix would otherwise report a cap that kept nobody, or a run that read no
 * pages when nothing said so. A suffix this build cannot read falls through to
 * the humanized id: the timeline's own rule is that such a line is shown ugly,
 * never dropped and never invented.
 */
function countedLine(
  value: string,
  prefix: string,
  line: (count: number) => string,
): string | null {
  if (!value.startsWith(prefix)) return null;
  const suffix = value.slice(prefix.length);
  return /^\d+$/.test(suffix) ? line(Number(suffix)) : null;
}

export function timelineLabel(value: string): string {
  const counted =
    countedLine(value, PEOPLE_CAPPED_PREFIX, peopleCappedLine) ??
    countedLine(value, PAGES_READ_PREFIX, pagesReadLine) ??
    countedLine(value, CLAIMS_POSTED_PREFIX, claimsPostedLine) ??
    countedLine(value, PAGES_SET_ASIDE_PREFIX, pagesSetAsideLine) ??
    countedLine(value, ALIASES_FOUND_PREFIX, aliasesFoundLine);
  if (counted !== null) return counted;
  if (value.startsWith(NAME_FOUND_TAKEN_PREFIX)) {
    const name = value.slice(NAME_FOUND_TAKEN_PREFIX.length).trim();
    if (name.length > 0) return nameFoundTakenLine(name);
  }
  return RUN_TIMELINE_LABELS[value] ?? humanize(value);
}

/**
 * The People table's "Latest" column — one phrase, derived from the row.
 *
 * The wrong-person unwind shows up twice here. A run it withdrew carries
 * `IDENTITY_WITHDRAWN`, and a research run that had already **completed** when
 * the identity was rejected is still `complete` while its analyses are gone —
 * so an unestablished identity disqualifies "Dossier ready" too. Both read
 * "Identity rejected", which is what actually happened.
 *
 * **And the Cancel press reads "Stopped"** (owner ruling, 2026-09-10). It is the
 * withdrawal's rule one code over and for the withdrawal's reason: the column
 * said "Research failed" about a run the office deliberately stopped, which is
 * the app reporting somebody's own decision back to them as bad news, on the one
 * screen where they see every person at once. One word rather than the strip's
 * sentence — the column is a phrase per row and "Stopped by the office" would be
 * telling a reader of their own roster who the office is.
 */
export function latestPhrase(entry: {
  latest_run: {
    kind: RunKind;
    state: RunState;
    error_code: string | null;
    completed_at: string | null;
  } | null;
  identity_established: boolean;
}): string {
  const latest = entry.latest_run;
  if (!latest) return 'Added';
  if (latest.error_code === IDENTITY_WITHDRAWN) return 'Wrong person';
  if (latest.error_code === CANCELLED_BY_OFFICE) return 'Stopped';
  if (latest.state === 'complete' && latest.kind === 'research' && !entry.identity_established) {
    return 'Wrong person';
  }
  /* The three-way switches below were binary `quick_search ? … : …` ternaries
     until the prose rerun joined the vocabulary (§6.3). A binary ternary is not
     a compile error when a third kind appears — it silently files the new kind
     under whichever branch the `else` happened to be, which here would have had
     the People list announce "Research running" and "Dossier ready" for a run
     that researches nothing and produces no dossier. */
  switch (latest.state) {
    case 'created':
    case 'running':
    // A checkpointed run is between legs with the next one already enqueued.
    // Nothing is asked of the office, so the column says what it says while the
    // pipeline is working — the distinction belongs on the strip, not here.
    case 'checkpointed':
      switch (latest.kind) {
        case 'quick_search':
          return 'Quick search running';
        case 'prose_refresh':
          return 'Rewriting the brief…';
        default:
          return 'Research running';
      }
    case 'awaiting_identity_pick':
      return RUN_CONFIRM_IDENTITY;
    case 'awaiting_prose':
      return RUN_MORE_DETAIL;
    case 'awaiting_adjudication':
      return reviewBeforeBriefLine(0);
    case 'failed':
      // Generic on purpose: a prose rerun that failed did not fail at research.
      // The alternative — a per-kind failure line — is a wording the People list
      // does not have the vocabulary for, since `latest_run` carries the kind
      // but the phrase is one line for every row.
      return latest.kind === 'prose_refresh' ? 'Rewrite failed' : 'Research failed';
    case 'complete':
      switch (latest.kind) {
        case 'prose_refresh':
          return 'Brief rewritten';
        default:
          return 'Brief ready';
      }
    default:
      return 'Added';
  }
}

/* ————————————————————————— imports (import note §2.4) ————————————————————————— */

/**
 * **Every word the Imports pages read in.**
 *
 * The rule the design states for this surface is narrower than §3.5's: the
 * office reads about *people, notes and files*, never about the machinery that
 * moved them. So "pipeline", "ingest" and "schema" do not appear here, and
 * neither does "record field" — a `record_field` target is **a detail kept on
 * the person**, which is what one is.
 *
 * The builders live beside the words rather than in the routes, for the reason
 * every other derived sentence in this file does: what the office reads is then
 * assertable without standing a page up.
 */

/** The wider of the two bin vocabularies — the session's, not the profiler's. */
type ImportBinRule = (typeof BIN_RULES)[number];

/** The four states, as the ledger's own column says them (§1.1). */
export const IMPORT_STATE_LABELS: Readonly<Record<ImportState, string>> = {
  in_progress: 'In progress',
  complete: 'Complete',
  /* "Canceled", with the detail line saying nothing was written — the two
     together are the whole difference between this state and the next one. */
  canceled: 'Canceled',
  undone: 'Undone',
};

/**
 * **Why a row is in the needs-a-look list**, one line per rule (§1.8).
 *
 * Each says what is true of the row rather than what the office should do about
 * it: nine of the thirteen are answered by a name, two by a level, and one — the
 * sentinel — arrives already answered and wants a level to overturn it. A line
 * that told everybody to "fix the name" would be wrong on four of them.
 */
export const BIN_REASON_LINES: Readonly<Record<ImportBinRule, string>> = {
  initials_only: 'Initials only',
  no_last_name: 'No last name',
  single_token: 'One word only',
  looks_like_email: 'Looks like an email address',
  looks_like_url: 'Looks like a link',
  decorated: 'The name carries a decoration',
  looks_like_org: 'Looks like an organization, not a person',
  unsplit_couple: 'Two people under one heading',
  role_name: 'A relationship word, not a name',
  duplicate_name: 'Another row on this file has the same name',
  identifier_conflict: 'Two rows share an identifier and disagree on another',
  /* Already set aside when the office first sees it: the question is whether to
     overturn that, so the line says what was decided and not what is wrong. */
  sentinel: 'Read as a channel rather than a person, and set aside',
  /* The plan wrote this one and the row carries the plan's own words; this is
     the label on the chip that groups them. */
  custom: 'Flagged by the plan',
};

/** The bin line, and `null` for a rule this build has no words for. */
export function binReasonLine(rule: string): string | null {
  return BIN_REASON_LINES[rule as ImportBinRule] ?? null;
}

/**
 * **Which reasons a name answers** (§1.8). The two identity outcomes are
 * questions about *which person a row is*, and re-stating the name a row already
 * carries leaves the file saying exactly what it said — so the inline name
 * editor is offered on the eight shape checks and not on those two.
 */
export function binReasonWantsAName(rule: string | null): boolean {
  return (
    rule !== null &&
    rule !== 'duplicate_name' &&
    rule !== 'identifier_conflict' &&
    /* And not the sentinel: the row's name is the channel's own text and is
       exactly right. What is in question is whether it is a person at all,
       which a level answers and a name does not. */
    rule !== 'sentinel'
  );
}

/** The three levels, in the office's words (§1.3). */
export const RESOLUTION_LEVEL_LABELS: Readonly<Record<ResolutionLevel, string>> = {
  subject: 'Tracked',
  known: 'Known',
  set_aside: 'Set aside',
};

/** What each level *means*, for the control's title — one clause each. */
export const RESOLUTION_LEVEL_HINTS: Readonly<Record<ResolutionLevel, string>> = {
  subject: 'A person of their own: a status, the People list, research.',
  known: 'Kept and findable, with no record of their own.',
  set_aside: 'Not written at all.',
};

/** The five things a column can become, named for what the office gets. */
export const COLUMN_TARGET_LABELS: Readonly<Record<ColumnTarget['kind'], string>> = {
  fact: 'A fact about them',
  /* Never "record field": what one *is* is a detail kept on the person. */
  record_field: 'A detail kept on the person',
  prose: 'Notes',
  relation: 'A relationship',
  ignore: 'Leave it out',
  /* Never "unsupported", which is a word about this system rather than about
     their file. What the office is being told is that the column holds
     something real and nothing here can carry it yet. */
  unsupported: 'Nothing here can hold this yet',
  seat: 'Where they sit',
};

/** Which part of the seat a seating column holds, as the Plan tab says it. */
export const SEAT_PART_LABELS: Readonly<Record<SeatPart, string>> = {
  section: 'Section',
  table: 'Table',
  row: 'Row',
  seat: 'Seat number',
  whole: 'The whole seat',
};

/**
 * **The two relation targets, in the office's words** — a family relation and a
 * tie between two people, which is what the Plan tab's select offers and what
 * the target sentence opens with.
 *
 * A table rather than `humanize`, for the reason every other one here is a
 * table: `social_tie` machined into "Social tie" is a word nothing else in the
 * product says, and the record already prints its own facts as "Connection".
 */
export const RELATION_TARGET_LABELS: Readonly<Record<'family_relation' | 'social_tie', string>> = {
  family_relation: 'A relationship',
  social_tie: 'A connection',
};

/**
 * **The five things an identifier column can be written as** (§1.5) — the Plan
 * tab's "How people are told apart" section.
 *
 * `external_id` is the one that made this a table: machined it reads "External
 * id", which is a database word for a thing the office knows as *the number the
 * other system uses*. The others are said as the office says them, which is why
 * `member_number` is a member number and not a "Member number" field.
 */
export const ANCHOR_KIND_LABELS: Readonly<Record<IdentifierAnchorKind, string>> = {
  email: 'Email address',
  phone: 'Phone number',
  member_number: 'Member number',
  external_id: 'An id from the system this came out of',
  handle: 'A profile link',
};

/** What an occasion is — one kind today, and named rather than machined. */
export const OCCASION_KIND_LABELS: Readonly<Record<OccasionKind, string>> = {
  event: 'An event everybody on the file took part in',
};

/** The two prose kinds, said as what they are rather than as their key. */
export const IMPORT_PROSE_KIND_LABELS: Readonly<Record<ImportProseKind, string>> = {
  note: 'a note',
  reference: 'an earlier write-up',
};

/** The three groups an operational column is classified into (§1.13). */
export const RECORD_FIELD_GROUP_LABELS: Readonly<Record<RecordFieldGroup, string>> = {
  membership: 'Membership',
  commercial: 'Money',
  workflow: 'Workflow',
};

/** How sure the planner is, in its own three words (§1.7) — never a number. */
export const PLAN_CONFIDENCE_LABELS: Readonly<Record<PlanConfidence, string>> = {
  high: 'Sure',
  medium: 'Fairly sure',
  low: 'Worth a look',
};

/**
 * A fact field's name in the plan editor. Most fields read plainly once
 * `humanize`'d (`place`, `since`); these are the ones that would otherwise show
 * the catalog's own key.
 */
const FACT_FIELD_WORDS: Readonly<Record<string, string>> = {
  org: 'Organization',
  a: 'First person',
  b: 'Second person',
  subject_role: 'Their role',
  match_basis: 'Matched on',
  starts_on: 'Starts',
  ends_on: 'Ends',
  as_of: 'As of',
  case_name: 'Case',
  addresses_count: 'Number of addresses',
  emails_count: 'Number of emails',
  phones_count: 'Number of phones',
  employers_count: 'Number of employers',
  relatives_count: 'Number of relatives',
};
export function factFieldLabel(field: string): string {
  return FACT_FIELD_WORDS[field] ?? humanize(field);
}

/** What the plan editor calls each kind of file it cannot import yet. */
export const UNSUPPORTED_SHAPE_LABELS: Readonly<Record<UnsupportedShapeHint, string>> = {
  row_event: 'One occasion per row',
  interaction: 'Interactions',
  membership: 'Memberships',
  other: 'Something else',
};

/**
 * **One column's target, rendered as a sentence the office can check by eye**
 * (§1.7) — the Plan tab's third column.
 *
 * Everything the executor will do with the column is in the line, because the
 * plan is a document a person reads: which fact and which of its fields, which
 * detail and under which heading, whether the notes are read — which is the half
 * that costs money — and how the other end of a relationship is found.
 */
export function planTargetWords(target: ColumnTarget): string {
  switch (target.kind) {
    case 'fact':
      /* The catalog's word for the type and not `humanize`'d: `social_tie`
         reads as "Social tie" through the machine and as "Connection" through
         the table the rest of the app already prints. The field goes through
         {@link factFieldLabel}. */
      return `A fact about them · ${FACT_TYPE_LABELS[target.type]} · ${factFieldLabel(target.field)}`;
    case 'record_field':
      return joinClauses([
        'A detail kept on the person',
        humanize(target.key),
        RECORD_FIELD_GROUP_LABELS[target.group],
      ]);
    case 'prose': {
      const kind =
        typeof target.prose_kind === 'string'
          ? IMPORT_PROSE_KIND_LABELS[target.prose_kind]
          : 'a note or an earlier write-up, by how the cell starts';
      /* Whether they are read is the half of a prose target that costs money, so
         it is said on every one of them rather than only on the ones that are. */
      return joinClauses([
        `Notes · ${kind}`,
        target.extract ? 'read at import' : 'kept, not read',
        target.split === 'dated_log' ? 'split per dated entry' : null,
      ]);
    }
    case 'relation':
      return joinClauses([
        RELATION_TARGET_LABELS[target.type],
        target.type === 'family_relation'
          ? humanize(target.relation ?? '')
          : (target.context ?? 'a tie'),
        target.resolve === 'in_file_name'
          ? 'someone else on this file'
          : 'a person kept for the name',
      ]);
    case 'ignore':
      return `Leave it out · ${target.reason}`;
    /* The planner's own sentence, whole and unedited: it is the entire value of
       this target, and a label with the sentence cut off it would be `ignore`
       with a longer name. The shape hint is deliberately not printed — it is a
       filing word for whoever reads the escape hatch's mail, not something the
       office has any use for. */
    case 'unsupported':
      return `${COLUMN_TARGET_LABELS.unsupported} · ${target.meaning}`;
    case 'seat':
      return `${COLUMN_TARGET_LABELS.seat} · ${SEAT_PART_LABELS[target.part]}`;
    default:
      return assertNever(target, 'import plan target');
  }
}

/**
 * **What the dry run says will happen to this person** (§1.8) — the People tab's
 * outcome line, in end-result words.
 *
 * `ambiguous` carries the count because the sentence is useless without it: the
 * office is being asked to pick, and how many there are decides whether they are
 * shown the cards or a collapsed list.
 */
export function previewOutcomeLine(outcome: ImportPreviewOutcome, candidateCount: number): string {
  switch (outcome) {
    case 'create':
      return 'will be created';
    case 'attach':
      return 'already on record: attach';
    case 'ambiguous':
      return `${plural(candidateCount, 'person shares', 'people share')} this name`;
    case 'folded':
      return 'the same identifier arrived earlier on this file';
    case 'skip':
      return 'not imported';
    default:
      return assertNever(outcome, 'import preview outcome');
  }
}

/** What happened at commit, for the row that has been through one. */
export const IMPORT_ROW_OUTCOME_LABELS: Readonly<Record<ImportRowOutcome, string>> = {
  pending: 'Not yet',
  created: 'Created',
  attached: 'Added to their record',
  skipped: 'Not written',
  failed: 'Failed',
};

/**
 * **The person landed and the "this is you" link did not** (§1.14).
 *
 * Two sentences, told apart by whether somebody else holds the person: with a
 * user id there is somebody to go and ask, and without one the refusal was about
 * the record rather than about the link. Never an error line — the row imported
 * in full, which is what the second half of each sentence says.
 */
export function linkSkippedLine(skip: { user_id: string | null; reason: string }): string {
  return skip.user_id
    ? 'Someone here is already recorded as this person, so the sign-in link was left alone. Everything else was imported.'
    : `The sign-in link could not be set: ${skip.reason}. Everything else was imported.`;
}

/**
 * **The status word a count takes**, singular at one and plural above it — "1
 * member", "225 contacts", "1 declined".
 *
 * The singular is `subjectStatusLabel` lowered, because a count line is not a
 * heading; the plural is its own table, since "declined" takes no s. Both fall
 * back to the wire's own token for a vertical this build does not carry, which
 * is `subjectStatusPlural`'s rule rather than `subjectStatusLabel`'s — a count
 * with no noun beside it is worse than the file's own word. In practice the
 * fallback is unreachable from the summary, whose breakdown is empty when the
 * vertical has no vocabulary at all.
 */
function subjectStatusCount(vertical: string, status: SubjectStatus, count: number): string {
  return plural(
    count,
    subjectStatusLabel(vertical, status)?.toLowerCase() ?? status,
    subjectStatusPlural(vertical, status),
  );
}

/** The Import tab's opening summary: a head line and the facts under it. */
export interface ImportSummary {
  /** How many people the file produces — the line the list opens with. */
  head: string;
  /** One fact per line, in order, under the head. */
  lines: string[];
}

/**
 * **What the Import tab opens with** (§2.4) — what this file is about to put on
 * record, as a list rather than a sentence.
 *
 * ```
 * 621 people
 * 252 prospects · 143 members · 15 contacts · 209 declined · 2 known
 * 25 already on record
 * 1,540 notes to read
 * ```
 *
 * It was one horizontal sentence joined with middle dots until 2026-09-06 —
 * "621 people: 252 prospects, 143 members, … · 25 already on record · 1,540
 * notes to read" — and Lew could not read it: six numbers in a row, three
 * different separators doing three different jobs, and no way to find the one
 * number you came for without reading all of them. The facts are lines now, one
 * each, and the only clause that keeps the middle dot is the status breakdown,
 * which is one fact — what the head count is made of — said in the vertical's
 * own words.
 *
 * **No price anywhere in it.** It carried "about $20" off the session's
 * `estimated_usd` until 2026-09-06, when the rate behind that number turned out
 * to be ten times under; the estimate was withdrawn rather than recalibrated, so
 * the count of notes stands alone and the only money on the tab is the limit the
 * office sets and what the reading has actually spent.
 *
 * The head count is the people the file *produces* — tracked plus known — and
 * set-aside rows are named separately rather than folded in, because they are
 * the rows the office decided against and a total that counted them would be a
 * total of nobody in particular. The breakdown runs in the vertical's own
 * vocabulary order, the same order the People screen's chips run in, and a
 * status no row carries is simply absent. A vertical this build does not carry
 * gets the head count and no breakdown, for the reason `subjectStatusLabel`
 * returns null rather than falling back to the club's words.
 *
 * **"Already on record" is what the dry run said before a commit, and what
 * happened after one.** They are two different questions with one word, and the
 * sentence answers whichever one the office is actually asking: before the
 * commit, the number is the people the resolver matched to somebody already on
 * record (`by_preview_outcome.attach`) — which is precisely what the office
 * wants to know before pressing anything; afterwards it is what the commit did.
 *
 * The clause used to be absent entirely before a commit, because `ImportCounts`
 * carried no grouping by what the dry run said. It does now, for this.
 */
export function importSummary(
  counts: ImportCounts,
  vertical: string,
  /**
   * The columns the notes come from (2026-09-29). Whether they are read is the
   * switch under this summary, so the line names the columns and promises nothing.
   */
  noteColumns: readonly string[] = [],
  /**
   * An attendee list counts attendees (owner's ruling, 2026-10-24): the word is
   * the event's, so only an upload into an event says it; a member list or any
   * other import keeps "people".
   */
  forEvent = false,
): ImportSummary {
  const people = counts.by_level.subject + counts.by_level.known;
  const vocabulary = SUBJECT_VOCABULARY_BY_VERTICAL[vertical];
  const words = (vocabulary?.statuses ?? [])
    .map((status) => ({ status, count: counts.by_status[status] ?? 0 }))
    .filter((entry) => entry.count > 0)
    .map((entry) => subjectStatusCount(vertical, entry.status, entry.count));
  if (counts.by_level.known > 0) {
    words.push(`${formatCount(counts.by_level.known)} known`);
  }

  /* The commit's own count once there is one, and the dry run's until then. */
  const attaching =
    counts.by_outcome.attached > 0 ? counts.by_outcome.attached : counts.by_preview_outcome.attach;

  return {
    head: forEvent ? plural(people, 'attendee', 'attendees') : plural(people, 'person', 'people'),
    lines: keepLines([
      words.length > 0 ? joinClauses(words) : null,
      attaching > 0 ? `${formatCount(attaching)} already on record` : null,
      counts.notes_to_read > 0
        ? `${plural(counts.notes_to_read, 'note', 'notes')} to read` +
          (noteColumns.length > 0 ? `, from ${noteColumns.join(', ')}` : '')
        : null,
      counts.by_level.set_aside > 0 ? `${formatCount(counts.by_level.set_aside)} set aside` : null,
      /* **What a file of occasions adds beyond its people** (owner's ruling,
         2026-09-07), in the three numbers it adds them in — occasions on the
         club's record, times somebody was at one, people this file says know
         each other. Three lines and not one, because they are three different
         things and no two of them are interchangeable. All zero and all absent
         on every other file. */
      counts.events > 0 ? importEvents(counts.events) : null,
      counts.participations > 0 ? importParticipations(counts.participations) : null,
      counts.ties > 0 ? importTies(counts.ties) : null,
      /* **What the plan could describe and cannot write** (owner's ruling,
         2026-09-07), said here because this list is what the office reads
         before pressing Import and a column nobody can carry yet is part of
         what is about to happen to their file.

         Last, and it does **not** stop the commit: the office asked for the
         file to be brought in, and everything above this line is going in. It
         is a thing to say, not a door to shut — the Plan tab is where the
         columns and the planner's sentences are, and where the shape can be
         sent on. */
      counts.unsupported_columns > 0
        ? `${plural(counts.unsupported_columns, 'column', 'columns')} not importable yet`
        : null,
    ]),
  };
}

/**
 * **What the commit did**, one fact per line (§2.4) — the same list grammar the
 * summary above it uses, for the same reason: it was a horizontal sentence of
 * six clauses ("219 created · 1 added to a record already here · 6 not written ·
 * 773 facts written · 86 of 225 notes read") and the office had to read all of
 * it to find any of it.
 *
 * The order is what happened to the people, then what was written about them.
 * Created and attached are unconditional — a commit that created nobody is a
 * fact about the commit, not an absence — and everything below them appears
 * only when it happened, which is why a clean commit with no notes is two lines
 * rather than seven.
 *
 * **No money.** The office pays in credits, and provider spend in dollars is
 * not what it is charged; that figure belongs to the operator app.
 */
export function importResultLines(session: {
  counts: ImportCounts;
  target_event?: { display_name: string } | null;
}): string[] {
  const counts = session.counts;
  const event = session.target_event ?? null;

  return keepLines([
    /* **An attendee list says where it went** (owner's ruling, 2026-10-24): the
       people it wrote are the event's attendees, named as such with the event,
       and only the ones already on record are worth a line of their own. */
    ...(event
      ? [
          `${plural(
            counts.by_outcome.created + counts.by_outcome.attached,
            'attendee',
            'attendees',
          )} added to ${event.display_name}`,
          counts.by_outcome.attached > 0
            ? `${formatCount(counts.by_outcome.attached)} already on record`
            : null,
        ]
      : [
          plural(counts.by_outcome.created, 'person created', 'people created'),
          `${formatCount(counts.by_outcome.attached)} added to a record already here`,
        ]),
    counts.by_outcome.skipped > 0 ? `${formatCount(counts.by_outcome.skipped)} not written` : null,
    counts.by_outcome.failed > 0 ? `${formatCount(counts.by_outcome.failed)} failed` : null,
    /* **What the file actually said about them**, beside how many of them
       landed. The two are different questions and a session can answer the first
       well and the second not at all: a commit whose every claim ingest threw
       read as 618 created with nothing on the page disagreeing. Left out when
       the plan states no facts, which is an honest zero rather than an alarming
       one. */
    counts.claims_written > 0
      ? plural(counts.claims_written, 'fact written', 'facts written')
      : null,
    counts.notes_to_read > 0
      ? `${formatCount(counts.notes_read)} of ${plural(counts.notes_to_read, 'note', 'notes')} read`
      : null,
    /* The same three the summary above promised, said again as what happened —
       the office reads the result block against the list they pressed Import
       under, and a number that appeared on one and not the other would be the
       commit having quietly done something else. */
    counts.events > 0 ? importEvents(counts.events) : null,
    counts.participations > 0 ? importParticipations(counts.participations) : null,
    counts.ties > 0 ? importTies(counts.ties) : null,
  ]);
}

/**
 * **What the session is doing, in the run strip's grammar** (§1.11, §2.4).
 *
 * "Importing · 412 of 621", then "Reading notes · 412 of 1,540". No money, for
 * the reason {@link importResultLines} gives.
 * Both are fractions rather than a spinner because both receipts are on the wire
 * for exactly that reason.
 *
 * The importing numerator is every row the phase has finished with — created,
 * attached, skipped and failed alike — because a row written nowhere is a row
 * that is done, and a progress number that never reached its total would be one
 * nobody could read.
 *
 * `null` when the session is not moving: the strip is a line about work in
 * flight, and a finished session says what it did rather than where it is.
 */
export function importProgressLine(session: {
  phase: ImportPhase | null;
  counts: ImportCounts;
}): string | null {
  const { phase, counts } = session;
  if (phase === null) return null;

  if (phase === 'importing') {
    const outcomes = counts.by_outcome;
    const total =
      outcomes.pending + outcomes.created + outcomes.attached + outcomes.skipped + outcomes.failed;
    const done = total - outcomes.pending;
    /* **And what those rows have said so far**, once any of them has said
       anything. The row fraction moves whether the facts land or not — a row is
       finished either way — so a commit writing nothing at all looked exactly
       like one writing everything, right up to the end. This is the clause that
       does not. */
    const said =
      counts.claims_written > 0 ? ` · ${formatCount(counts.claims_written)} facts written` : '';
    return `${IMPORT_PHASE_LABELS.importing} · ${formatCount(done)} of ${formatCount(total)}${said}`;
  }

  if (phase === 'reading_notes') {
    return (
      `${IMPORT_PHASE_LABELS.reading_notes} · ` +
      `${formatCount(counts.notes_read)} of ${formatCount(counts.notes_to_read)}`
    );
  }

  return IMPORT_PHASE_LABELS[phase];
}

/**
 * **The one line under a session's name** — what the file is, how big, where the
 * session has got to, and when it was opened.
 *
 * The phase used to be a tinted half-width box of its own between the head and
 * the tabs, on a page that already had a two-column split under it: a fifth
 * block of geometry to say one word. It is a clause on this line now, and the
 * tinted line is kept for the one thing it is good at — saying that something
 * is *moving*, with the strip's own pulse.
 *
 * So the phase appears twice only when it is working, and then the two are
 * saying different things: this one is where the session is, the strip is what
 * it is doing this second.
 */
export function importHeadLine(session: {
  file: { container: ImportContainer; size: number };
  state: ImportState;
  phase: ImportPhase | null;
  counts: Pick<ImportCounts, 'needs_a_look'>;
  preview_stale: boolean;
  created_at: string;
}): string {
  const step = importNextStep(session);
  return joinClauses([
    IMPORT_CONTAINER_LABELS[session.file.container],
    importFileSize(session.file.size),
    step === null ? IMPORT_STATE_LABELS[session.state] : IMPORT_NOT_IMPORTED_YET,
    step ?? (session.phase === null ? null : IMPORT_PHASE_LABELS[session.phase]),
    `opened ${formatDay(session.created_at)}`,
  ]);
}

/**
 * **"Not imported yet", wherever an uncommitted session is named** (import UX
 * audit, 2026-10-24). "In progress · A plan to look at" read as "it is being
 * imported" to an operator whose two uploads sat unpreviewed for days, so a
 * session before its Import press says it has not landed, and what lands it.
 */
export const IMPORT_NOT_IMPORTED_YET = 'Not imported yet';

/**
 * The next step of a session that has not been imported, as a clause; null for
 * any other. `counts` and `preview_stale` are optional because the ledger and
 * the event's upload line do not carry them: there a previewed session is
 * simply ready. A row edit after the preview leaves the phase at `previewed`,
 * so only `preview_stale` can say the preview is owed again.
 */
export function importNextStep(session: {
  state: ImportState;
  phase: ImportPhase | null;
  counts?: Pick<ImportCounts, 'needs_a_look'>;
  preview_stale?: boolean;
}): string | null {
  if (session.state !== 'in_progress') return null;
  switch (session.phase) {
    case 'profiled':
      return 'Reading the file';
    case 'waiting_on_you':
      return 'Waiting on your answer';
    case 'plan_ready':
      return 'Preview, then import';
    case 'previewed': {
      if (session.preview_stale) return 'Preview again, then import';
      const look = session.counts?.needs_a_look ?? 0;
      return look > 0 ? `${plural(look, 'person needs', 'people need')} a look` : 'Ready to import';
    }
    default:
      return null;
  }
}

/**
 * **Whether the session is working this second** (§2.4) — the one question the
 * tinted line under the head answers.
 *
 * Not "is the phase non-null": a session sitting at `plan_ready` or
 * `waiting_on_you` has a phase and is doing nothing at all, and a pulsing line
 * over a page that is waiting for the office is a page that looks busy for
 * days. These two phases are the ones where work is actually in flight; the
 * opening turn is the third, and the page knows about that one itself.
 */
export const importIsWorking = (session: { phase: ImportPhase | null }): boolean =>
  session.phase === 'importing' || session.phase === 'reading_notes';

/**
 * **The reading stopped at the organization's monthly research allowance rather than
 * at the end** (§1.11) — the server's own answer, read rather than re-derived.
 * An import session has no spending limit of its own (run defaults sweep,
 * 2026-09-26).
 *
 * It was arithmetic here until 2026-09-06, and every clause of that was wrong in
 * one direction or another: the phase stays `reading_notes` while a session is
 * paused rather than going null, and the state stays `in_progress` with it. So a
 * paused session read as an ordinary one with unread notes.
 *
 * A function still, rather than the field inline at each call, because what the
 * page asks is "is this paused" and the field is where the answer comes from.
 */
export function importPausedAtCeiling(session: { counts: ImportCounts }): boolean {
  return session.counts.notes_paused_for_spend;
}

/**
 * **The reading stopped because the model is rate limiting** — the third stop
 * that is not a failure, and the only one nothing else on the read can see.
 *
 * A session waiting out a quota is byte-identical to one whose leg is mid-note:
 * `in_progress`, `reading_notes`, `error` null, units pending, the fraction
 * exactly where the last leg left it. So the strip pulsed a number that would
 * not move for eight minutes and the office had nothing to read but the pulse.
 *
 * **Asked of the clock and not only of the field**, because the field is a
 * moment and this page outlives it: the server sends the timestamp only while it
 * is in the future, but between two polls it becomes the past under a reader
 * standing on it, and a banner that waits for the next fetch to notice is a
 * banner that says the session is waiting after the successor has started
 * reading.
 */
export function importWaitingForModel(
  session: { waiting_for_model_until: string | null },
  now: number = Date.now(),
): boolean {
  return waitingUntil(session.waiting_for_model_until, now) !== null;
}

/** The moment, parsed, or `null` where there is none or it has passed. */
function waitingUntil(at: string | null, now: number): number | null {
  if (at === null) return null;
  const stamp = new Date(at).getTime();
  return Number.isNaN(stamp) || stamp <= now ? null : stamp;
}

/**
 * **What every surface calls this wait**: the ledger's whole state cell and the
 * banner's opening clause, so the two read as one condition seen from two
 * distances rather than as two conditions.
 */
export const IMPORT_WAITING_FOR_MODEL = 'Waiting to retry';

/**
 * **The waiting banner's line** — the fact, when it will be tried again, and how
 * far the reading got.
 *
 * "Waiting for the model's rate limit · trying again in 3 min · 86 of 225 notes
 * read".
 *
 * The middle clause is the one that earns the banner. "Waiting" on its own is
 * indistinguishable from stuck to somebody who has been watching the same
 * fraction for five minutes, and the difference between two minutes and fifteen
 * is the difference between staying on the page and going to do something else.
 * Rounded **up**, and never to zero: a wait with nine seconds left is still a
 * wait, and "trying again in 0 min" is a sentence about nothing.
 *
 * The fraction is the paused banner's, word for word, so a session that stops
 * twice for two different reasons counts its notes the same way both times. It
 * is dropped when there is nothing to count. There is no money clause: what this
 * is waiting on is not the spend, and an amount here would invite the one
 * gesture that will not help.
 *
 * **And no button.** Nothing the office can press makes a quota lift sooner, and
 * a control that only re-runs the wait is a control that teaches people to press
 * it.
 */
export function importWaitingBannerLine(
  session: { counts: ImportCounts; waiting_for_model_until: string | null },
  now: number = Date.now(),
): string {
  const counts = session.counts;
  const until = waitingUntil(session.waiting_for_model_until, now);

  return joinClauses([
    IMPORT_WAITING_FOR_MODEL,
    until === null ? null : `trying again in ${Math.max(1, Math.ceil((until - now) / 60_000))} min`,
    counts.notes_to_read > 0
      ? `${formatCount(counts.notes_read)} of ${plural(counts.notes_to_read, 'note', 'notes')} read`
      : null,
  ]);
}

/**
 * **The fact itself, in the words every surface says it in** — the banner under
 * the session's head, the Import tab's own block, and the state cell in the
 * ledger. One string, because a session that is paused is paused wherever it is
 * being looked at. It names the monthly research allowance in the words of
 * {@link ORG_SPEND_LIMIT_LINE}, because that is the only limit an import meets.
 */
export const IMPORT_PAUSED_AT_LIMIT = 'Paused: monthly research allowance used up';
export const IMPORT_PAUSED_LINE = `${IMPORT_PAUSED_AT_LIMIT}.`;
export const IMPORT_READ_THE_REST = 'Read the rest';
export const IMPORT_READING_THE_REST = 'Starting…';

/**
 * **The paused banner's line** (2026-09-06) — the fact, how far the reading got,
 * and the one move there is, in the run strip's own clause grammar.
 *
 * "Paused: monthly research allowance used up · 86 of 225 notes read · contact Arbiter
 * to continue".
 *
 * The pause used to be visible only inside the Import tab, which is one of four
 * and not the one anybody stands on. So the banner is under the head on every
 * tab. **No figure about money**, as with every monthly-limit line
 * ({@link ORG_SPEND_LIMIT_LINE}): the allowance is the operator's, and what the
 * office can do about it is ask Arbiter (run defaults sweep, 2026-09-26). The
 * fraction is the one from the result list, word for word, so the banner and
 * the tab under it cannot count the same notes differently.
 */
export function importPausedBannerLine(session: { counts: ImportCounts }): string {
  const counts = session.counts;

  return joinClauses([
    IMPORT_PAUSED_AT_LIMIT,
    counts.notes_to_read > 0
      ? `${formatCount(counts.notes_read)} of ${plural(counts.notes_to_read, 'note', 'notes')} read`
      : null,
    'contact Arbiter to continue',
  ]);
}

/* ——————————————— the organization's month ——————————————— */

/**
 * **What the office is told when the organization has spent its month**
 * (2026-09-09) — one sentence, on the code and on the banner alike, so the two
 * cannot come to say different things.
 *
 * **No figure in it, deliberately.** The allowance is the operator's, and the
 * office is shown neither what it is nor what has been spent against it: a
 * number the reader cannot change is a question rather than an answer, which is
 * the same rule that keeps the research settings off the office's own page. What
 * is left is the two things that are actually useful — that work has stopped,
 * and the one move there is.
 *
 * "Contact Arbiter" rather than "an operator" or "your administrator": the
 * office knows who Arbiter is, and the other two are words for a role nobody in
 * a club has a name for.
 *
 * **The owner's words, verbatim (2026-09-09).** It said "reached its monthly
 * spend limit … to raise it" for a day; the office's word for the thing is an
 * allowance, and "to continue" is the reader's own next move rather than the
 * operator's. Do not rewrite it here without asking him. "This month's" is the
 * owner's own revision (copy audit §4 row 1, applied 2026-10-01).
 */
export const ORG_SPEND_LIMIT_LINE =
  'This month’s research allowance is used up. Contact Arbiter to continue.';

/* ————— credits (credits launch spec, steps 1–4) ————— */

/* **Credit is not the research allowance above.** The spend limit is the operator's
   runaway backstop; credit is what the account's plan and grants buy. The two
   refusals are different sentences so they cannot read as one problem, and none
   of the credit lines says "allowance". */
export const CREDITS_EXHAUSTED_LINE =
  'There is not enough credit for this. Contact Arbiter to add credits.';
/* Surface-neutral: the refusal also reaches presses whose surface is not the
   record's, so the line sends the office to the record's page, where every button
   says the price. */
export const CREDIT_PRICE_CHANGED_LINE =
  'The price has changed. Open the record to see it and press again there.';
/* Resume shows no price, so there is none to go and see: a scan's remaining
   attendees can grow while it is stopped, never past what its first press held. */
export const RESUME_PRICE_CHANGED_LINE =
  'The credit needed to restart has changed. Press Resume again; nothing extra is charged.';
/* The import page's own: its price moves as the month's included rows are used, and
   it is on this page that the button shows the new one. */
export const IMPORT_PRICE_CHANGED_LINE =
  'The price has changed. Check the new price on the button and press again.';
export const PLAN_EXCLUDES_PAID_WORK_LINE =
  'Your plan does not include new searches, research or imports. Contact Arbiter to change it.';

/* **Before a press is refused** (ruled 2026-09-26): the app-wide line and Settings
   say it when credit is low or out, and a priced button the balance cannot cover
   says why it is disabled. Low is "cannot cover a dossier"; out is "cannot cover
   any priced work". Plan credits about to lapse are deliberately not announced. */
export const CREDITS_LOW_LABEL = 'Credit is running low';
export function creditsLowLine(
  availableUnits: number,
  dossierUnits: number,
  unitsPerCredit: number,
): string {
  return `${creditAmount(availableUnits, unitsPerCredit)} left, not enough for research (${creditAmount(dossierUnits, unitsPerCredit)}). Contact Arbiter to add credits.`;
}
export const CREDITS_OUT_LABEL = 'Out of credit';
export function creditsOutLine(quickLooksLeft: boolean): string {
  return quickLooksLeft
    ? 'Included quick searches still work. Contact Arbiter to add credits.'
    : 'Contact Arbiter to add credits.';
}
/** Beside a priced button the balance cannot cover. */
export function creditShortLine(availableUnits: number, unitsPerCredit: number): string {
  return `Not enough credit · ${creditAmount(availableUnits, unitsPerCredit)} left`;
}
/* Beside a failed run's Resume the balance cannot cover. The failure returned the
   run's hold and the resume holds it again, so the balance must cover it, but the
   run is still charged once (ruled 2026-09-30: never imply a failure costs credits). */
export function resumeShortLine(neededUnits: number, unitsPerCredit: number): string {
  return `Needs ${creditAmount(neededUnits, unitsPerCredit)} available to restart · nothing extra is charged`;
}

/** Ledger units as the office reads them: "1 credit", "2.5 credits". */
export function creditAmount(units: number, unitsPerCredit: number): string {
  const credits = units / unitsPerCredit;
  return `${formatCount(credits)} ${credits === 1 ? 'credit' : 'credits'}`;
}

/** A button's label with the price it accepts, when there is one to show. */
export function pricedLabel(
  label: string,
  units: number | null | undefined,
  unitsPerCredit: number | undefined,
) {
  return units && unitsPerCredit ? `${label} · ${creditAmount(units, unitsPerCredit)}` : label;
}

/** A scan's label with the most it can cost: it settles only the attendees it screens. */
export function maxPricedLabel(
  label: string,
  units: number | null | undefined,
  unitsPerCredit: number | undefined,
) {
  return units && unitsPerCredit
    ? `${label} · up to ${creditAmount(units, unitsPerCredit)}`
    : label;
}

/** What a charge was for, in the office's words. */
export const CREDIT_OPERATION_LABELS: Record<CreditOperation, string> = {
  quick_look: 'Quick search',
  person_research: 'Research',
  research_recheck: 'Refresh',
  org_investigation: 'Investigation',
  event_research: 'Event research',
  travel_brief: 'Travel brief',
  event_scan: 'Attendee scan',
  import: 'Import',
};

/* `used` can pass `included`: a free quick look resumed after its month filled
   keeps the slot its failure gave back. */
export function quickLooksLine(used: number, included: number): string {
  return `${formatCount(Math.min(used, included))} of ${formatCount(included)} quick searches this month`;
}

export function importRowsLine(used: number, included: number): string {
  return `${formatCount(used)} of ${formatCount(included)} import rows this month`;
}

/**
 * This month's plan credits: what is left, and when the rest lapses. The lapse is
 * the first instant of the next UTC month, named as the month it ends, so a
 * reader west of UTC is not told it lapses on the last day.
 */
export function monthlyCreditsLine(
  monthly: { granted: number; available: number; expires_at: string },
  unitsPerCredit: number,
): string {
  const month = new Date(new Date(monthly.expires_at).getTime() - 1).toLocaleDateString('en-US', {
    month: 'long',
    timeZone: 'UTC',
  });
  const left = formatCount(monthly.available / unitsPerCredit);
  const granted = formatCount(monthly.granted / unitsPerCredit);
  return `Plan credits this month: ${left} of ${granted} left · unused plan credits lapse at the end of ${month}`;
}

/** The run kind an operation charges for, where it names one. */
const OPERATION_RUN_KIND: Record<CreditOperation, RunKind | null> = {
  quick_look: 'quick_search',
  person_research: 'research',
  research_recheck: 'research',
  org_investigation: 'org_lookup',
  event_research: 'event_research',
  travel_brief: null,
  event_scan: 'event_scan',
  import: null,
};

/** One recent charge's standing, in the office's words. */
export function chargeStanding(
  charge: {
    operation: CreditOperation;
    state: 'open' | 'charged' | 'returned';
    basis: 'allowance' | 'credit';
    units: number;
    outcome: string | null;
  },
  unitsPerCredit: number,
): string {
  if (charge.outcome === 'undone') return 'Returned · Undone';
  /* An import holds its maximum and is charged for the rows it wrote beyond the
     month's included rows; one entirely within them says so. */
  if (
    charge.operation === 'import' &&
    charge.state === 'returned' &&
    charge.outcome === 'not_delivered'
  )
    return 'Not charged · No new rows';
  if (charge.operation === 'import' && charge.state !== 'returned') {
    if (charge.basis === 'allowance' || charge.units === 0)
      return charge.state === 'open' ? 'Included rows · In progress' : 'Included rows · Used';
    if (charge.state === 'open')
      return `Up to ${creditAmount(charge.units, unitsPerCredit)} · In progress`;
  }
  /* A scan holds its maximum and is charged for the attendees it screened; one
     with nothing to charge (every attendee free at the rate) says so. */
  if (charge.operation === 'event_scan') {
    if (charge.basis === 'allowance')
      return charge.state === 'open' ? 'No charge · In progress' : 'No charge';
    if (charge.state === 'open')
      return `Up to ${creditAmount(charge.units, unitsPerCredit)} · In progress`;
  }
  const price =
    charge.basis === 'credit'
      ? creditAmount(charge.units, unitsPerCredit)
      : 'Included quick search';
  if (charge.state === 'open') return `${price} · In progress`;
  if (charge.state === 'charged')
    return charge.basis === 'credit' ? `${price} · Charged` : `${price} · Used`;
  switch (charge.outcome) {
    case 'no_confirmed_match':
    case 'not_delivered':
      return `Not charged · ${undeliveredLine(OPERATION_RUN_KIND[charge.operation])}`;
    case 'cancelled':
      return 'Not charged · Stopped';
    case 'continuation_not_started':
      return 'Not charged · Research did not start';
    default:
      return 'Not charged · Did not finish';
  }
}

/** One row of the ledger, past the columns every state shares (§2.4). */
export interface ImportLedgerRow {
  /** The state word, from {@link IMPORT_STATE_LABELS}. */
  state: string;
  /** The counts that matter *for that state*, or null where none do. */
  detail: string | null;
}

export const IMPORT_NOTHING_WRITTEN = 'Nothing was written';

/**
 * **What the ledger says about one session**, which is a different sentence per
 * state (§2.4): a session in flight is a place it has got to, a finished one is
 * what it wrote, an undone one is when it was taken back, and a canceled one is
 * the fact that nothing happened.
 *
 * One function over the four rather than four columns, because three of the four
 * would be empty on every row.
 *
 * **And one session in flight is not in flight** (2026-09-06): a reading leg
 * stopped at the monthly research allowance keeps `in_progress` and keeps the phase,
 * so the ledger said `Reading notes` about a session that had not read anything
 * for two days. The pause replaces the
 * state word rather than sitting under it, because what a reader of this column
 * wants to know is which rows are waiting on them; the phase said in its place
 * would be the same lie in a quieter type.
 */
export function importLedgerRow(entry: {
  state: ImportState;
  phase: ImportPhase | null;
  counts: ImportCounts;
  waiting_for_model_until: string | null;
  updated_at: string;
}): ImportLedgerRow {
  const state = IMPORT_STATE_LABELS[entry.state];
  const step = importNextStep(entry);

  switch (entry.state) {
    case 'in_progress':
      if (importPausedAtCeiling(entry)) return { state: IMPORT_PAUSED_AT_LIMIT, detail: null };
      /* **And the other session in flight that is not in flight.** A reading leg
         stopped on the model's rate limit keeps `in_progress` and keeps the
         phase, exactly as the paused one does, so this column said `Reading
         notes` about a row that would not move for a quarter of an hour. It
         replaces the state word for the same reason the pause does — a reader of
         this column is scanning for which rows are stopped — and it does *not*
         say when, because a countdown in a list is a number that is wrong by the
         time the eye reaches the next row. The session page is where the wait is
         quantified. */
      if (importWaitingForModel(entry)) return { state: IMPORT_WAITING_FOR_MODEL, detail: null };
      if (step !== null) return { state: IMPORT_NOT_IMPORTED_YET, detail: step };
      return { state, detail: entry.phase === null ? null : IMPORT_PHASE_LABELS[entry.phase] };
    case 'complete': {
      const detail = joinClauses([
        entry.counts.by_outcome.created > 0
          ? `${formatCount(entry.counts.by_outcome.created)} created`
          : null,
        entry.counts.by_outcome.attached > 0
          ? `${formatCount(entry.counts.by_outcome.attached)} already on record`
          : null,
        entry.counts.by_level.known > 0
          ? `${formatCount(entry.counts.by_level.known)} known`
          : null,
        entry.counts.by_level.set_aside > 0
          ? `${formatCount(entry.counts.by_level.set_aside)} set aside`
          : null,
      ]);
      return { state, detail: detail.length > 0 ? detail : IMPORT_NOTHING_WRITTEN };
    }
    case 'canceled':
      return { state, detail: IMPORT_NOTHING_WRITTEN };
    case 'undone':
      return { state, detail: `Undone ${formatDay(entry.updated_at)}` };
    default:
      return assertNever(entry.state, 'import ledger state');
  }
}

/* ————— the ledger and the New import door ————— */

export const IMPORTS_TITLE = 'Imports';
export const IMPORTS_EMPTY = 'No files have been imported yet.';
export const IMPORTS_NEW = 'New import';
/**
 * **What the New import button says while it is working** — and it now says the
 * one thing the door is actually doing.
 *
 * It said "Reading the file…" while the door held the office through the upload
 * *and* the planner's opening turn, which is up to two minutes on a real board:
 * the modal sat locked, the new row appeared on the ledger behind it, and
 * nothing said why the button had not come back. The door is the upload alone
 * now (§1.1) — a few seconds — and the word on the button is that.
 */
export const IMPORTS_NEW_WORKING = 'Uploading…';

/** The picker, which is a button over a hidden input rather than the browser's. */
export const IMPORT_FILE_CHOOSE = 'Choose a file';
export const IMPORT_FILE_CHANGE = 'Change';
export const IMPORT_FILE_LABEL = 'File';
export const IMPORT_FILE_HINT = 'A spreadsheet, a CSV or a PDF.';
export const IMPORT_DESCRIPTION_LABEL = 'What is this file?';
export const IMPORT_DESCRIPTION_PLACEHOLDER = 'the club contact board, all groups';
export const IMPORT_NEEDS_A_FILE = 'Choose a file to import.';

/** What the file picker accepts, in the shape an `accept` attribute takes. */
export const IMPORT_FILE_ACCEPT = '.csv,.xlsx,.pdf';

/* ————— the session page ————— */

export const IMPORT_TAB_LABELS = {
  plan: 'Plan',
  people: 'People',
  commit: 'Import',
  file: 'File',
} as const;

/**
 * **What a tab the session has not reached yet says** (§2.4) — one line, naming
 * what would get it there rather than reporting that nothing is here.
 */
export const IMPORT_TAB_NOT_YET: Readonly<Record<'plan' | 'people' | 'commit', string>> = {
  plan: 'There is no plan yet. Answer the questions in the conversation and one will be proposed.',
  people: 'The people this file produces appear once there is a plan to read it with.',
  commit: 'Run the preview on the People tab to see what this file would do.',
};

export const IMPORT_CHAT_PLACEHOLDER = 'Say what you want changed';
/**
 * **The composer's own line, under the questions** — "or", because the buttons
 * above it are the ordinary way to answer and this is the way out of them.
 */
export const IMPORT_CHAT_HINT = 'Or say what you want changed';
export const IMPORT_CHAT_SEND = 'Send';
export const IMPORT_CHAT_SENDING = 'Sending…';
export const IMPORT_CHAT_EMPTY = 'Nothing has been said yet.';
export const IMPORT_CHAT_RETRY = 'Try again';
/**
 * **The opening turn, in flight** (§1.2) — the run strip's grammar, on the one
 * turn nobody pressed a button for.
 *
 * The session page runs it on arrival, and it is the slowest turn in the module:
 * the planner reads the whole profile before it says anything. Without a line
 * the office lands on a page with an empty conversation and no reason for it,
 * which is the same silence the old locked modal had — moved rather than fixed.
 */
export const IMPORT_OPENING_TURN = 'Reading the file and working out a plan';
/**
 * **The opening turn was tried and did not come back.**
 *
 * The upload runs it and deliberately does not fail when the planner is
 * unreachable — losing a 25 MB upload over a moment's outage would be the worse
 * failure by far — so what the office is left with is a live session with
 * nothing said on it. The line says which of the two that is: nobody has asked
 * yet, and asking is one press away.
 */
/** The conversation column's head, on the same rule as the tabs beside it. */
export const IMPORT_CHAT_HEAD = 'Conversation';

export const IMPORT_CHAT_NOT_ASKED = 'No plan yet. Ask for one, or describe the file.';
/**
 * **The batch, named on the button** (§6.3).
 *
 * Pressing an option no longer sends anything: the picks are held in the tab
 * and go up together when the office presses Send. So the button has to say how
 * much it is carrying, or the picks look like nothing happened. Plain "Send"
 * when the composer's text is all there is, which is the turn this button
 * always sent.
 */
export function importSendLabel(picked: number): string {
  return picked === 0 ? IMPORT_CHAT_SEND : `Send ${plural(picked, 'answer', 'answers')}`;
}

/** Reopen one settled question's options. Re-answering is a deliberate act. */
export const IMPORT_CHAT_CHANGE_ANSWER = 'Change answer';
/** And back out of it, leaving the answer the transcript holds where it is. */
export const IMPORT_CHAT_KEEP_ANSWER = 'Keep this answer';

/**
 * **What is happening, with a number that moves** (§6.3).
 *
 * The page does not grey while a turn runs, so something has to say the turn is
 * running. The server cannot say more than this: `phase` moves only when the
 * agent's message is written, and whether the rows are being rebuilt is not
 * observable from outside the transaction — so "Rebuilding the rows" is not
 * shown rather than guessed.
 */
export const IMPORT_CHAT_WORKING_ANSWERS = 'Working on your answers…';
export const IMPORT_CHAT_SENDING_MESSAGE = 'Sending your message…';

/**
 * **After a refresh mid-turn** the mutation is gone and the transcript ends in
 * the office's own message, so the marker hangs off that message instead.
 *
 * Past three minutes it stops counting and says what to do: the model call is
 * bounded well under that, so a clock still running at 3:00 is a turn that is
 * not coming back. The composer stays usable throughout — a turn that failed
 * after writing the user row would otherwise leave the session mute forever.
 */
export const IMPORT_CHAT_WAITING = 'Waiting for the assistant’s reply…';
export const IMPORT_CHAT_NO_REPLY = 'No reply yet. You can send another message.';
export const IMPORT_NO_REPLY_AFTER_SECONDS = 180;
/**
 * **How long a turn has to be out before the marker appears at all.**
 *
 * The marker is for the turn *this tab is not running*, and for a second or two
 * after a send succeeds that is exactly what the transcript looks like: the
 * mutation has settled, the office's own message is the last one on the page,
 * and the planner's reply is still travelling back on the refetch. Without a
 * floor the line flashed on every successful send. Nothing is owed that fast, so
 * nothing is said that fast.
 */
export const IMPORT_WAITING_AFTER_SECONDS = 5;

/**
 * `null` under the floor, which is the render saying nothing rather than the
 * caller knowing when to. Past three minutes it stops counting and says what to
 * do instead.
 */
export function importWaitingLine(seconds: number): string | null {
  if (seconds < IMPORT_WAITING_AFTER_SECONDS) return null;
  return seconds >= IMPORT_NO_REPLY_AFTER_SECONDS
    ? IMPORT_CHAT_NO_REPLY
    : `${IMPORT_CHAT_WAITING} ${formatElapsed(seconds)}`;
}

/**
 * **The server already held those answers** — `409 IMPORT_QUESTION_ANSWERED`,
 * which is what a double press finds. It is information rather than a failure:
 * the named picks are dropped and everything else the office picked is still
 * there to send.
 *
 * **Only when there is something else.** The second sentence was unconditional,
 * so the commonest refusal of all — one question, answered twice, nothing else
 * in the batch — told the office that what they picked was still here to send
 * when the refusal had just taken the last of it away. The line says the true
 * half on its own, and earns the second sentence by there being picks left.
 *
 * **It names the record, not the planner.** "The planner already had those
 * answers" is wrong in the case that produces this line most often: the turn
 * whose model call failed wrote the office's row on its way out, so the answers
 * are on the session and the planner never saw them at all. What the office has
 * to know is where the answers are, which is here.
 */
export const IMPORT_CHAT_ALREADY_ANSWERED = 'Those answers were already sent.';
export const IMPORT_CHAT_STILL_PICKED = 'Anything else you picked is still here to send.';

export function importAlreadyAnsweredLine(picksRemain: boolean): string {
  return picksRemain
    ? `${IMPORT_CHAT_ALREADY_ANSWERED} ${IMPORT_CHAT_STILL_PICKED}`
    : IMPORT_CHAT_ALREADY_ANSWERED;
}

export const IMPORT_PLAN_MESSAGE_LINK = 'See the plan';
export const IMPORT_NOTES_LABEL = 'Columns to review';

/**
 * **The muted words beside the answer the plan already holds.**
 *
 * A question next to a plan is a proposal, and the marked option is what says
 * so — but there are two things it can mean, and the office reads them
 * differently. "As planned" is the planner's own default, the size rule's
 * answer: press something else and the plan is redrawn. "As you said" is *their
 * own sentence*, read off the description and written into the plan before they
 * were ever asked; being told that back is what stops them answering a question
 * they had already answered.
 *
 * Which of the two it is comes off the question's own text, because that is
 * where the planner is told to say it ("(as you said)", both bodies at 1.6.0).
 * A second wire key saying the same thing would be a second thing that can
 * disagree with the sentence the office actually reads.
 */
export const IMPORT_OPTION_AS_PLANNED = 'as planned';
export const IMPORT_OPTION_AS_YOU_SAID = 'as you said';

export const defaultOptionSuffix = (questionText: string): string =>
  /as you said/i.test(questionText) ? IMPORT_OPTION_AS_YOU_SAID : IMPORT_OPTION_AS_PLANNED;

/** The plan editor. */
export const IMPORT_PLAN_SAVE = 'Save the plan';
export const IMPORT_PLAN_SAVING = 'Saving…';
export const IMPORT_PLAN_SAVED_RERUN =
  'The plan is saved. Run the preview again to see what it does.';
export const IMPORT_PLAN_READ_ONLY =
  'This part of the plan cannot be edited here yet. Ask for the change in the conversation and the assistant will rewrite it.';
export const IMPORT_PLAN_NONE = 'No plan yet.';

/* ————————————————————————— the plan at a glance ————————————————————————— */

/**
 * **The six or seven lines the Plan tab opens with**, and the labels are fixed
 * and in this order for the reason the planner's own message labels are: the
 * office reads the same lines about every file, in the same places.
 *
 * They are deliberately close to the planner's own six — the tab is saying back
 * what the message said, computed from the plan rather than written by a model,
 * which is the whole point of having both.
 */
export const IMPORT_GLANCE_HEAD = 'At a glance';
export const IMPORT_GLANCE_LABELS = {
  /* An attendee list's event, first (owner's ruling, 2026-10-24). */
  goesOnto: 'Goes onto',
  people: 'People',
  become: 'Become',
  names: 'Names',
  /* The three kinds of column the office otherwise had to read the table for
     (2026-09-29): what becomes a fact, what is kept on the record as written,
     and who each person is linked to. */
  facts: 'Facts',
  kept: 'Kept on record',
  relationships: 'Relationships',
  /* An attendee list's seating columns, each with the part it holds. */
  seats: 'Seats',
  notes: 'Notes',
  leftOut: 'Left out',
  /* Directly under "Left out", where it is read as the distinction it is: what
     the plan chose to skip, then what it could not carry (owner's ruling,
     2026-09-07). */
  notImportable: 'Not importable yet',
  occasion: 'Occasion',
  secondSheet: 'Second sheet',
} as const;
export const IMPORT_GLANCE_NONE = 'none';
export const IMPORT_GLANCE_TRACKED_ONLY = 'tracked people only';
export const IMPORT_PLAN_COLUMNS_EDIT = 'Edit columns';
export const IMPORT_PLAN_COLUMNS_HIDE = 'Hide the column editor';
/** How many columns the planner was least sure of, beside the fold that hides them. */
export const importPlanColumnsUnsure = (count: number): string => `${formatCount(count)} to review`;

/**
 * **What a document's one column is called to a person.**
 *
 * A file read as one document has exactly one column and its header is the
 * literal word `body` — the planner is told so, because the plan addresses
 * columns by index and header and a document has to have one. It is a fine name
 * for a contract and a terrible one on a page: "Notes: body read now" is a line
 * about a machine. This is what the glance says instead.
 */
export const IMPORT_DOCUMENT_BODY_COLUMN = 'the write-up under each heading';

/**
 * **The kinds of row a file has that are not people**, in as few words as name
 * one — these are read inside a count, as "40 rows (9 group totals, 9 blank)",
 * so each is a noun phrase and not a sentence.
 */
export const EXCLUSION_RULE_LABELS: Readonly<Record<ExclusionRule, string>> = {
  leading_lines: 'lines above the header',
  repeated_header: 'repeated headers',
  single_cell_row: 'group titles',
  nameless_row: 'group totals',
  blank_row: 'blank',
  sentinel_name: 'not a person',
  date_only_row: 'withdrawn profiles',
};

/** The editable half, and the two labels that say what each control does. */
export const IMPORT_PLAN_EVERYONE_BECOMES = 'Everyone becomes';
export const IMPORT_PLAN_TRACKED_ARE = 'Tracked people are';
export const IMPORT_PLAN_GROUP_RULES_HEAD = 'Except by group';
export const IMPORT_PLAN_DOCUMENT_PROSE = 'The bio paragraph becomes';
export const IMPORT_PLAN_READ_AT_IMPORT = 'Read them at import';

/**
 * **Everything the plan says that is not the office's to change here** (§1.7),
 * folded into one section at the foot of the tab.
 *
 * It was eight small-caps sections in the middle of the page, each with its own
 * copy of the sentence saying it could not be edited — eight headings, eight
 * refusals, above the two controls that actually do something. The rule has not
 * changed and the detail has not gone: it is one fold, closed, with the sentence
 * said once at the bottom of it.
 */
export const IMPORT_PLAN_EVERYTHING_HEAD = 'Everything in the plan';
export const IMPORT_PLAN_EVERYTHING_OPEN = 'Hide the rest of the plan';

/* ————— what nothing here can hold yet (owner's ruling, 2026-09-07) —————

   The planner met a column it could describe and had no target for. That is not
   the office's decision and must not be printed as one: `ignore` says *we chose
   not to import this*, and reading a gap in the product under that heading is
   how the gap stays in the file and out of everybody's head. So it gets a
   section of its own, above the controls, with the planner's own sentence
   against each column — and one way to send the shape on. */
export const IMPORT_PLAN_UNSUPPORTED_HEAD = 'Not importable yet';
export const IMPORT_PLAN_UNSUPPORTED_LEAD =
  'These columns are not imported. You can change any of them below.';

/** The escape hatch: one press, an email the office can read before sending. */
export const IMPORT_SHAPE_MAIL_TO = 'soroush@arbiterdefense.group';
export const IMPORT_SHAPE_SEND = 'Send this file’s shape to Arbiter';
/* **What goes in the mail, said before it is opened.** The office is about to
   send something about their members' file to somebody outside the office, and
   the one thing they need to know is that it is the shape and not the people. */
export const IMPORT_SHAPE_SEND_HINT =
  'Sends the file’s column names, a few sample values and what the assistant made ' +
  'of them; no people.';

/* ————— cells the file gets wrong, corrected on the plan (§4.4, 2026-09-15) —————

   A correction is the one thing on this tab the office asked for by hand: two
   rows carried member number 0074 and one of them is 0075, and until now there
   was nowhere on the plan to put that. So it is a section of its own rather
   than a line in the read-only fold — what somebody asked for is not detail to
   be folded away — and it is drawn only where there is one, because a heading
   over an empty list says a question was asked that never was.

   The heading says *to the file* on purpose. The file is not touched: the
   correction is a sentence about how one of its cells is read, and the hint
   under the heading is where that is said before anybody wonders. */
export const IMPORT_PLAN_CORRECTIONS_HEAD = 'Corrections to the file';
export const IMPORT_PLAN_CORRECTIONS_HINT =
  'These cells are read as you corrected them. The file is unchanged.';

/* **And the way back off** (the 2026-09-15 review). A correction that stops
   applying — the file was replaced and the row moved under it — refuses the
   import, and the sentence the commit answers with says to fix or remove it
   here. There was nothing here to remove it with: this section was read-only,
   and the only other writer of corrections is the planner, which was being told
   to carry every one of them forward. The office was left with a plan they could
   not commit and could not change.

   **One word, and no confirmation.** Removing a correction takes the file back
   to what it says, which is where it started; it is saved by the same Save
   button every other change on this tab is saved by, so the way to undo a press
   is to reload the page rather than to answer a dialog about one. */
export const IMPORT_PLAN_CORRECTION_REMOVE = 'Remove';

/* Named on the row it is on, because a list of six identical *Remove* buttons is
   six controls a screen reader cannot tell apart. The visible label stays the one
   word; this is what is read out. */
export const importPlanCorrectionRemoveLabel = (where: string, column: string): string =>
  `Remove the correction on ${where}, ${column}`;

/* ————— what the dry run could not do with the plan itself (§4.4, 2026-09-15) —————

   A correction names a cell, and the dry run is where anybody finds out the
   cell it names does not say what the correction says it says: the plan asked
   for 0074 → 0081 and the file has 0074, so the change was not made and nobody
   was told. `ImportSession.plan_issues` is the channel that tells them, and
   this is the heading over it.

   The heading says *could not apply* and not *errors*, because none of these is
   the file's fault or the office's mistake in the ordinary sense: it is the
   plan and the bytes disagreeing, and the sentence under it says the two ways
   out — change the plan, or change the correction — and then run the dry run
   again, because a list taken against the old plan says nothing about the new
   one. Drawn only where there is one, for the reason the corrections are. */
export const IMPORT_PLAN_ISSUES_HEAD = 'What the preview could not apply';
export const IMPORT_PLAN_ISSUES_HINT =
  'The preview could not apply these. Fix the plan or the correction, then run the ' +
  'preview again.';

export const IMPORT_PLAN_COLUMNS_HEAD = 'Columns';
export const IMPORT_PLAN_EXCLUSIONS_HEAD = 'Rows left out';
export const IMPORT_PLAN_IDENTIFIERS_HEAD = 'How people are told apart';
export const IMPORT_PLAN_RESOLUTION_HEAD = 'What each person becomes';
export const IMPORT_PLAN_OCCASION_HEAD = 'What this file is about';
export const IMPORT_PLAN_NO_OCCASION = 'A source of contacts, not an occasion.';

/**
 * **A file whose rows are occasions** (owner's ruling, 2026-09-07) — the Plan
 * tab's own section for it, and the words the People and Import tabs say about
 * what it produced.
 *
 * The heading is plural where `IMPORT_PLAN_OCCASION_HEAD` is about the file as a
 * whole: one is "this file is a dinner", and this one is "this file is a year of
 * them". Two different statements, so two different sections, and a file never
 * carries both.
 */
export const IMPORT_PLAN_OCCASIONS_HEAD = 'Occasions';
export const IMPORT_PLAN_OCCASIONS_LEAD =
  'Each row is an occasion. You can change what the people on it become.';
export const IMPORT_PLAN_OCCASION_NAME = 'Each row is';
export const IMPORT_PLAN_OCCASION_DATED = 'Dated by';
export const IMPORT_PLAN_OCCASION_UNDATED = 'no date on the row';
export const IMPORT_PLAN_OCCASION_TIE = 'They knew each other as';
export const IMPORT_PLAN_OCCASION_NO_TIE = 'Nothing is written between the people on a row.';
export const IMPORT_PLAN_OCCASION_PEOPLE_COLUMN = 'Names from';
export const IMPORT_PLAN_OCCASION_PEOPLE_ROLE = 'They were the';

/** The three things a person can be to one of those rows. */
export const ROW_EVENT_ROLE_LABELS: Readonly<Record<RowEventRole, string>> = {
  host: 'host',
  guest: 'guest',
  participant: 'took part',
};

/** "12 occasions", "guest of Ada Fenwick" — the People tab's line for such a row. */
export const rowEventOccasions = (count: number): string =>
  `${formatCount(count)} ${count === 1 ? 'occasion' : 'occasions'}`;

export const rowEventRoleLine = (role: RowEventRole, host: string | null): string =>
  host ? `${ROW_EVENT_ROLE_LABELS[role]} of ${host}` : ROW_EVENT_ROLE_LABELS[role];

/**
 * **What a file of occasions added, in the three numbers it added them in** —
 * the Import tab's summary and the result block after it.
 *
 * Three lines rather than one, because they are three different things: how many
 * evenings the club now has on record, how many times somebody was at one, and
 * how many people this file says know each other.
 */
export const importEvents = (count: number): string =>
  `${formatCount(count)} ${count === 1 ? 'occasion' : 'occasions'}`;
export const importParticipations = (count: number): string =>
  `${formatCount(count)} ${count === 1 ? 'participation' : 'participations'}`;
export const importTies = (count: number): string =>
  `${formatCount(count)} ${count === 1 ? 'tie' : 'ties'}`;

/** The People tab. */
export const IMPORT_ROWS_EMPTY = NO_MATCHES;
export const IMPORT_PREVIEW_RUN = 'Run preview';
export const IMPORT_PREVIEW_RUNNING = 'Reading the file…';
export const IMPORT_PREVIEW_STALE =
  'Something has changed since the last preview. Run it again before importing.';

/* ————— the preview bar, on every tab (§1.11, §2.4) —————

   Re-running the dry run is what unblocks Commit and what turns an answered
   duplicate group back into ordinary rows, and it was a button inside one tab.
   These are the bar's three lines, and they are shorter than
   `IMPORT_PREVIEW_STALE` on purpose: that sentence has to carry its own recourse
   because it sits under a control somewhere else, and this one has the button
   beside it. */
export const IMPORT_PREVIEW_BAR_NEVER =
  'Nothing is imported yet. Run the preview to see what this file will do, then import.';
/** The banner while the planner's question is open and no preview is owed. */
export const IMPORT_BANNER_QUESTION =
  'Nothing is imported yet. The planner has a question about the file, in the conversation.';
export function importBannerNeedsLook(count: number): string {
  return `Nothing is imported yet. ${plural(count, 'person needs', 'people need')} a look before importing.`;
}
export const IMPORT_BANNER_READY = 'Ready to import. Nothing is written until Import is pressed.';
/** Ready, except that the preview found something to confirm on the Import tab. */
export const IMPORT_BANNER_REVIEW_FINDINGS =
  'Nothing is imported yet. The preview found something to confirm before importing.';
export const IMPORT_BANNER_TO_PEOPLE = 'Open People';
export const IMPORT_BANNER_TO_IMPORT = 'Review and import';
export const IMPORT_PREVIEW_BAR_STALE = 'Things have changed since the last preview.';
export const IMPORT_PREVIEW_BAR_RUNNING = 'Running the preview…';

/* **"Everyone matching", not "everything shown."** The gesture reaches every row
   the filter matches, which on a file of six hundred is almost never the fifty
   on screen — and "shown" is the reading that gets somebody to set aside six
   hundred people believing they set aside fifty. */
export const IMPORT_BULK_APPLY = 'Apply to everyone matching';
export const IMPORT_BULK_APPLYING = 'Applying…';
/**
 * **Why the status control is off**, in the two lengths the bar has room for
 * (D8): the whole reason on the control itself, where somebody who reaches for
 * it finds it, and a clause under the row, which is all a row of controls can
 * carry without becoming a paragraph with a form beside it.
 */
export const IMPORT_BULK_STATUS_OFF =
  'Only a tracked person has a status. Narrow the list to Tracked, or set them to Tracked here.';
export const IMPORT_BULK_STATUS_OFF_SHORT = 'Only a tracked person has a status.';
export const IMPORT_ROWS_SEARCH_PLACEHOLDER = 'Name';
export const IMPORT_ROW_NAME_SAVE = 'Save';
export const IMPORT_ROW_NAME_SAVING = 'Saving…';
/** Settles a row flagged for its name with the name as the file has it. */
export const IMPORT_ROW_NAME_KEEP = 'Keep name';
export const IMPORT_ROW_NAME_KEEP_HINT =
  'The name is right as it is, for example a stage name or a single name.';

export function importNeedsALookChip(count: number): string {
  return `Needs review: ${formatCount(count)}`;
}

/* ————— where the bios went (§1.11, §1.12) ————— */

/**
 * **What a row will do with the prose it carries**, said on the row itself
 * before the commit.
 *
 * The office ran a preview on the bios PDF, read "Medici Day" under a name, and
 * asked where everything else the file said about that person had gone. Nothing
 * on the page answered it: the dry run says what the file *states directly* — a
 * column, a heading, a date — and a bio is not that. It is filed as a note and
 * read afterwards, and what it says lands on the record one fact at a time while
 * the reading runs.
 *
 * So the row says so before anything is pressed. `units` is what the row files
 * and `read_at_import` is what becomes of it — the two questions the wire's pair
 * asks — and the word for a unit follows the file: a **bio** in a document, a
 * **note** in a grid, because that is what the office called the thing when they
 * uploaded it.
 *
 * `null` on a row with no prose at all, which is most rows of most spreadsheets:
 * a line reading "0 notes" is a sentence about nothing.
 */
export function importRowNotesLine(
  notes: { units: number; read_at_import: boolean },
  shape: ImportShape,
): string | null {
  if (notes.units <= 0) return null;
  const filed =
    shape === 'document'
      ? notes.units === 1
        ? '1 bio kept as a note'
        : `${formatCount(notes.units)} bios kept as notes`
      : notes.units === 1
        ? '1 note'
        : `${formatCount(notes.units)} notes`;
  return joinClauses([
    filed,
    notes.read_at_import ? IMPORT_NOTES_READ_AFTER : IMPORT_NOTES_KEPT_BACKGROUND,
  ]);
}

export const IMPORT_NOTES_READ_AFTER = 'read after import';
export const IMPORT_NOTES_KEPT_BACKGROUND = 'kept as background, not read';
export const IMPORT_NOTES_READING = 'reading…';
export const IMPORT_NOTES_BACKGROUND_ONLY = 'kept as background';

/**
 * **Retry lives on the Import tab, and this is the row saying so.**
 *
 * A prose input has a door of its own (`POST /inputs/:id/extract-retry`), but
 * the id it takes is not on the row wire — `notes_status` is four numbers — so
 * the People tab cannot offer it. The one gesture that reaches these units is
 * the Import tab's Retry, which takes the failed rows and the unread notes
 * together, and a reader who has just been told a note failed should not have to
 * hunt for it.
 */
export const IMPORT_NOTES_RETRY_ELSEWHERE = 'Retry on the Import tab reads it again';

/**
 * **How far the reading has got on this person**, after the commit — the same
 * line's answer to the same question, once there is one.
 *
 * The three numbers do not have to sum to the total: a unit the plan keeps as
 * background is filed and never queued, so a row whose notes are all background
 * has a total and nothing else, and that is what the last clause says. A row
 * still in the queue says `reading…` rather than a fraction, because the
 * fraction that matters — how far the *session* has got — is the strip's, and
 * two of them on one screen are two different denominators.
 *
 * `null` where there is nothing filed, which is also what a row that became
 * nobody carries.
 */
export function importRowNotesStatusLine(status: {
  total: number;
  read: number;
  failed: number;
  pending: number;
}): string | null {
  if (status.total <= 0) return null;
  const said = joinClauses([
    status.read > 0 ? `${plural(status.read, 'note', 'notes')} read` : null,
    status.failed > 0 ? `${plural(status.failed, 'note', 'notes')} could not be read` : null,
    status.pending > 0 ? IMPORT_NOTES_READING : null,
  ]);
  return said.length > 0 ? said : IMPORT_NOTES_BACKGROUND_ONLY;
}

/**
 * **The tab's own account of what it is showing** — under the counts, while the
 * dry run has been read and nothing has been written yet.
 *
 * The list is the file's direct statements and only those. What the bios say is
 * a second pass, behind the people, and the office should be told that here
 * rather than work it out from a record that looks thin.
 */
export const IMPORT_PEOPLE_DIRECT_ONLY =
  'What you see here is what the file states directly. Notes are read after import.';

/* ————— the Import tab's two decisions ————— */

/**
 * **The block's own name**, above the switch that is the whole of it. The
 * decision had no heading at all: a checkbox, a price and an explainer sat
 * loose under the summary line and read as three unrelated remarks.
 */
export const IMPORT_NOTES_HEAD = 'Notes';

/**
 * **The switch, in the word the office used for the thing they uploaded** — a
 * **bio** in a document, a **note** in a grid, the same split
 * {@link importRowNotesLine} makes on the People tab.
 */
export function importReadNotesLabel(shape: ImportShape): string {
  return shape === 'document' ? 'Read the bios now' : 'Read the notes now';
}

/** The unit's word, which every sentence in this block turns on. */
const notesUnit = (shape: ImportShape): string => (shape === 'document' ? 'bio' : 'note');

/**
 * **What the switch does, in one line beside it** (§1.9, §1.11).
 *
 * The switch used to say only its own name, with `about $3` stamped after it
 * and the order of events on a line above: three fragments for one decision,
 * and none of them said what *reading* actually is. This is the sentence a
 * person needs to answer the switch — what happens to each bio — with the
 * mechanism itself one press away in {@link importReadingDisclosure}.
 *
 * **The price came off on 2026-09-06.** It said "About $3." from the session's
 * `estimated_usd`, and that number was a per-unit rate an order of magnitude
 * under what a bio actually costs to read. A wrong number in front of a decision
 * is worse than no number, and a right one is not available — the cost varies
 * with how much each bio says. Reading is not charged per note since the run
 * defaults sweep (2026-09-26), so nothing about money stands beside the switch.
 */
export function importReadNotesGist(shape: ImportShape): string {
  return (
    `Each ${notesUnit(shape)} is read once and what it says ` +
    '(employer, city, family, interests) is added to the person.'
  );
}

/** The disclosure over the sentence above, closed by default. */
export const IMPORT_READING_DISCLOSURE = 'What does reading do?';

/**
 * **What reading does, in one line** (copy audit §4 row 31): when it runs, where
 * the facts go, and that leaving the switch off keeps the text on each person
 * for **Read the rest** later, and that the monthly allowance can pause it
 * (`IMPORT_PAUSED_AT_LIMIT` is that pause). It names no figure: reading is not
 * charged per unit.
 */
export function importReadingDisclosure(shape: ImportShape): readonly string[] {
  const unit = notesUnit(shape);
  return [
    `Each ${unit} is read once after import. Its facts are filed on the person with the ` +
      `${unit} as the source. ${unit[0]!.toUpperCase()}${unit.slice(1)}s left unread are kept ` +
      'and can be read later. Reading pauses if the monthly research allowance runs out.',
  ];
}

/** The same order, said again while it is happening. */
export const IMPORT_NOTES_FACTS_APPEAR = 'Facts from the notes appear on record as they are read.';
export const IMPORT_COMMIT = 'Import';
export const IMPORT_COMMITTING = 'Starting…';
export const IMPORT_CANCEL = 'Cancel this import';
export const IMPORT_CANCELLING = 'Canceling…';
export const IMPORT_UNDO = 'Undo this import';
export const IMPORT_UNDOING = 'Undoing…';
export const IMPORT_RETRY_ROWS_WORKING = 'Trying again…';
export const IMPORT_SEE_THE_PEOPLE = 'See the people';
export const IMPORT_KEEP = 'Keep it';
export const IMPORT_CANCEL_LINE =
  'Nothing has been written, so nothing comes off the records. The file stays with this import.';
export const IMPORT_UNDO_LINE =
  'Everything this file wrote comes off the records. Anyone who has been worked on since is kept and listed.';
/** The Import tab of a canceled session: there is no door left to show. */
export const IMPORT_CANCELED_LINE = 'Canceled before anything was written.';

/**
 * **Starting again on the same file** (import note §4.2).
 *
 * The label says *this file* because that is the whole of what distinguishes it
 * from New import, which is the only other way on and means finding the export
 * again. The line under it says what comes across, in the order somebody asks:
 * the mapping, then the answers they gave about people, then what does not — the
 * conversation, which starts over with a summary rather than pretending to
 * continue.
 *
 * It is not "Retry this import": the neighboring button with that shape retries
 * the *rows* of a committed session, and two gestures a finger apart called the
 * same thing is the reading mistake worth the extra word.
 */
export const IMPORT_RETRY_FILE = 'Start again on this file';
export const IMPORT_RETRY_FILE_WORKING = 'Starting…';

/**
 * **And it does not promise a plan on a session that never had one.**
 *
 * A session canceled before the planner settled a mapping is retried into one
 * holding no plan and no rows — which is the ordinary shape of a cancel, not an
 * edge case — and a line reading "the plan and the answers you gave carry over"
 * would be describing something that did not happen. The file is what carries
 * in both, and that is what each sentence leads with.
 */
export function importRetryFileHint(hasPlan: boolean): string {
  return hasPlan
    ? 'Starts again on this file. The plan and your answers carry over.'
    : 'Starts again on this file. This import never settled a plan, so the new one starts ' +
        'from the file.';
}

/** The one line back to where the work came from, on a session that was retried. */
export const IMPORT_RETRIED_FROM = 'Started again from an earlier import of this file.';
export const IMPORT_RETRIED_FROM_LINK = 'See that import';

export function importRetryRowsLabel(count: number): string {
  return `Retry failed rows: ${formatCount(count)}`;
}

export const IMPORT_NOT_PREVIEWED_YET = 'Run the preview first, from the line above the tabs.';

/* ————————————————————————— what the dry run found (§4.3) ————————————————————————— */

/**
 * **The block above the Import button**, when the dry run found something about
 * the file as a whole rather than about one row.
 *
 * The **sentences themselves are the server's** and are not here: each carries
 * this file's own numbers ("143 of 176 rows share the name …"), and a number is
 * the only part of a finding anybody can check. What is here is the frame around
 * them — a heading that says why they are between the office and the button, and
 * the one sentence a checkbox can carry.
 *
 * The heading is *Before this imports* rather than a warning word. Nothing here
 * says the file is wrong; it says somebody should look. A file that really is a
 * column of one name is confirmed once and imported, and a heading that called
 * it an error would have been wrong every one of those times.
 */
export const IMPORT_FINDINGS_HEAD = 'Before this imports';
export const IMPORT_FINDINGS_HINT =
  'The preview found these about the file as a whole. Check each one to import anyway.';

/**
 * **The checkbox, and why it is not "I understand".**
 *
 * What the office is being asked is whether they read a sentence and still want
 * the file, which is two statements and reads best as two. "Import anyway" is
 * also the honest description of what the box does: it does not fix anything and
 * it does not mark the finding as a mistake — the finding stays true and the
 * import runs.
 */
export const IMPORT_FINDING_CONFIRM = 'Import anyway';

/**
 * **The same three findings, on the Plan tab** (2026-09-14).
 *
 * The office reads a finding on the Import tab and repairs it here, two screens
 * apart: the sentence saying the names are probably coming from the wrong column
 * was nowhere near the mapping that chooses the column. So the block is drawn on
 * this tab too, read-only — the tick stays where the commit is, because it is a
 * decision about the commit — and the lead-in says which of the two things to do
 * rather than leaving a warning with no verb on it.
 */
export const IMPORT_FINDINGS_PLAN_HINT =
  'The preview found these. Fix the plan here, or confirm them on the Import tab.';

/**
 * **Where each finding is answered**, one short line per kind.
 *
 * The first two are about the name: the plan reads it out of one column, and a
 * file whose names all repeat or do not read like names is almost always that
 * column being the wrong one. The third is about what the rest of the columns
 * write — a row that mints a person and nothing else is a row whose other
 * columns went nowhere.
 *
 * They point rather than instruct. The name column is not edited on this tab —
 * the planner rewrites it when asked in the conversation — so a line reading "change it
 * here" would be naming a control that is not on the screen.
 *
 * **Only the name pointer names a heading, because only that one is always
 * drawn.** `PlanEverything` is on the Plan tab whatever the plan says, so *Where
 * the name comes from* is somewhere to send a reader every time. The **Columns**
 * table is not: the tab draws it for two columns or more, puts the single
 * column's target on the row above for one, and has no target control at all for
 * none — and none is exactly the file `rows_write_nothing` is usually about,
 * since a plan that maps nothing but the name maps no columns. So that pointer
 * names the *choice* rather than the section it happens to sit in, and stays
 * true in all three layouts.
 *
 * The fold's own heading is composed from {@link IMPORT_PLAN_EVERYTHING_HEAD},
 * which is declared above this line. `IMPORT_PLAN_NAME_HEAD` is not: it is
 * further down the file, and a `const` read before its own declaration is a
 * blank screen at load rather than a type error, so that one is written out and
 * the Plan tab's spec asserts the pointer still contains it — which is what
 * keeps the two copies from drifting apart in silence.
 */
export const IMPORT_FINDING_WHERE: Readonly<Record<ImportFindingKind, string>> = {
  names_repeat: `About the name column: Where the name comes from, under ${IMPORT_PLAN_EVERYTHING_HEAD}.`,
  names_not_names: `About the name column: Where the name comes from, under ${IMPORT_PLAN_EVERYTHING_HEAD}.`,
  rows_write_nothing:
    'About what each column writes: give the columns that should become facts a target, ' +
    'or leave them if this file really is only names.',
};

/** Why Import is off while a finding is unconfirmed — the block below says which. */
export const IMPORT_FINDINGS_UNCONFIRMED =
  'Read what the preview found about this file, and check each one, before importing.';

/**
 * **Why the Import button is off** — the server's own refusals, mirrored so the
 * office reads the reason instead of pressing a button to be told it
 * (`IMPORT_NOT_PREVIEWED`, `IMPORT_HAS_UNRESOLVED_ROWS`).
 *
 * **There is a third refusal now and it is deliberately not here**
 * (`IMPORT_HALTED`, §4.3). This function answers from the session alone, and
 * whether the dry run's findings have been confirmed is a question about boxes
 * somebody has or has not ticked on the Commit tab in front of them — state this
 * file has no business holding. The tab folds {@link IMPORT_FINDINGS_UNCONFIRMED}
 * in beside this answer and draws the findings block under the button; see
 * `routes/import/commit.tsx`.
 *
 * **`previewed_at` and `preview_stale`, and nothing derived.** The first
 * stand-in was the session's old cost estimate, on the grounds that it was
 * `null` until a dry run — and it answered a different question twice over,
 * being a perfectly good number on a session whose preview had *since* gone
 * stale (a name fixed inline, a plan edited, a level changed). So the button was
 * enabled on exactly the sessions the server was about to refuse. Both fields
 * are on the session now and both are the server's own reading of its own rule,
 * which is why the estimate's removal costs this function nothing.
 *
 * `null` is "nothing is stopping it", which is what enables the button.
 */
export function importCommitBlockedBy(session: {
  counts: ImportCounts;
  previewed_at: string | null;
  preview_stale: boolean;
}): string | null {
  if (session.previewed_at === null) return IMPORT_NOT_PREVIEWED_YET;
  if (session.preview_stale) return IMPORT_PREVIEW_STALE;
  if (session.counts.needs_a_look > 0) {
    return `${plural(session.counts.needs_a_look, 'person', 'people')} still need a look.`;
  }
  return null;
}

/** The Undo confirmation, which names the count out loud. */
export function importUndoQuestion(count: number, forEvent = false): string {
  const who = forEvent ? plural(count, 'attendee', 'attendees') : plural(count, 'person', 'people');
  return `Undo this import? ${who} came from this file.`;
}

/** The way on from an attendee list's result: the list it went onto. */
export const IMPORT_OPEN_EVENT_ATTENDEES = "Open the event's attendees";
export function importGoesOntoLine(eventName: string): string {
  return `${IMPORT_INTO_EVENT} ${eventName}`;
}
/** The fold over an attendee list's record choices, whose defaults nearly every list wants. */
export const IMPORT_CHANGE_WHAT_THEY_BECOME = 'Change what they become';

export const IMPORT_UNDO_KEPT_HEAD = 'These people were kept';
export const IMPORT_UNDO_KEPT_WHY =
  'Somebody has worked on them since the import, so removing them would take that work with it.';

export function importUndoRemovedLine(removed: {
  people: number;
  facts: number;
  prose_inputs: number;
  attendees?: number;
  attendees_reverted?: number;
}): string {
  const attendees = removed.attendees ?? 0;
  const reverted = removed.attendees_reverted ?? 0;
  const graph = attendees > 0 || reverted > 0;
  return joinClauses([
    removed.people > 0 || !graph ? `${plural(removed.people, 'person', 'people')} removed` : null,
    removed.facts > 0 ? `${plural(removed.facts, 'fact', 'facts')} taken back` : null,
    removed.prose_inputs > 0 ? `${plural(removed.prose_inputs, 'note', 'notes')} deleted` : null,
    attendees > 0 ? `${plural(attendees, 'attendee', 'attendees')} removed` : null,
    reverted > 0
      ? `this list’s details taken back from ${plural(reverted, 'attendee', 'attendees')} another list also names`
      : null,
  ]);
}

/** The File tab. */
export const IMPORT_FILE_REPLACE = 'Replace the file';
export const IMPORT_FILE_REPLACING = 'Reading the file…';
export const IMPORT_FILE_LOCKED_NOTE = 'The file cannot be replaced once the import has run.';
export const IMPORT_FILE_HISTORY_HEAD = 'Earlier uploads';
/**
 * The File tab is where the description is read in full — the session's head and
 * its ledger row both clamp it to two lines, and a board's description runs to a
 * paragraph.
 */
export const IMPORT_FILE_DESCRIPTION_HEAD = 'What this file is';
export const IMPORT_FILE_PROFILE_HEAD = 'What the file holds';

/**
 * **A file this build can no longer read the profile of.**
 *
 * Both halves the File tab used to be missing — what the file holds and every
 * earlier upload — are on the session read since 2026-09-06. What is left for
 * this line is the honest remainder: a session profiled by an older build,
 * whose stored shape the current contract does not parse. The server answers an
 * empty summary rather than throwing, because the session page is where the
 * office goes to find out what is wrong with a session and it has to render.
 */
export const IMPORT_FILE_PROFILE_UNAVAILABLE =
  'What is in this file could not be read back. Replace it to have it read again.';

export function importFileSize(bytes: number): string {
  if (bytes < 1024) return `${formatCount(bytes)} bytes`;
  if (bytes < 1024 * 1024) return `${formatCount(Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The container the bytes arrived in, said as the office would say it. */
export const IMPORT_CONTAINER_LABELS: Readonly<Record<ImportContainer, string>> = {
  csv: 'CSV',
  xlsx: 'Spreadsheet',
  pdf: 'PDF',
  /* No office ever uploads one: a dossier session is opened by the
     `dossier:import` operator tool. The label is here because the record says
     where a person came from, and "imported from dossier-import-2026-09-10" is
     what the office should read on the forty prospects it wrote. */
  dossier: 'Research packages',
};

/** The record's own clause: "imported 6 Sep from Contacts.xlsx" (§2.5). */
export function recordImportClause(at: string, filename: string): string {
  return `imported ${formatDay(at)} from ${filename}`;
}

/** The line above the People table when one session is what is being read (§2.6). */
export function peopleImportFilterLine(description: string): string {
  return `Imported in: ${description}`;
}

export const PEOPLE_IMPORT_FILTER_CLEAR = 'Clear';

/* ————— the words the routes used to hold inline ————— */

/**
 * **Every string the Imports pages print, in this file and nowhere else.**
 *
 * The ledger's five column headings, the two loading lines and the word on the
 * button that walks away from a modal were written into the JSX, which is the
 * one thing §3.5 cannot check: a rule about what the office reads is only a rule
 * if the words are somewhere it can be read off. They are also the strings most
 * likely to be said twice — "Cancel" is the walk-away on two different modals on
 * two different tabs, and the two must not drift apart.
 */
/**
 * The ledger's columns, in the order they are read. **The file comes first**:
 * the description is a sentence written for the planner and it ran as the row's
 * link, so a board and its second upload were two rows whose only difference was
 * a paragraph. The filename is what names a session; the sentence sits beside it
 * saying what it is.
 */
export const IMPORTS_TABLE_HEADS = {
  file: 'File',
  what: 'What it is',
  who: 'Who',
  when: 'When',
  state: 'State',
} as const;

/** The one word for "this read has not come back yet", everywhere. */
export const LOADING = 'Loading…';

/** Walking away from a modal without doing the thing it asked about. */
export const CANCEL = 'Cancel';

/**
 * **What a door says to a member whose access does not reach it**
 * (privilege-levels note §7).
 *
 * It lives on the **write** rather than on the control that opens it, which is
 * the whole point: entry points multiply — a menu item, a pasted link, an empty
 * state's call to action — and each new one is another chance to forget a gate.
 * The write is one place, and a member who arrives by a route nobody thought to
 * close reads a sentence instead of collecting a refusal.
 *
 * It says who can do it, because that is the only part the reader can act on.
 */
export const ACCESS_OWNER_ONLY = 'Only an owner can make this change.';

/** The way out of a door a reader cannot use. Not `Cancel`: there is nothing to cancel. */
export const ACCESS_CLOSE = 'Close';

/** Whoever opened a session, when the name is not on the row. */
export const SOMEONE_HERE = 'Someone here';

/* ————— the People tab, counted in people ————— */

/**
 * **The tab counts people, not rows** (§1.3).
 *
 * A row of the file is a person — that is the whole of what the tab is for — and
 * "600 rows" is the file's word for them. The office is looking at a list of
 * people they are about to put on record, and every other surface in the product
 * says so.
 */
export const importPeopleCount = (count: number): string => plural(count, 'person', 'people');

/**
 * **Everyone the file produces**, said on the line the level segments are on —
 * the number every other number on the tab is a share of.
 *
 * It used to be the chips' own total, sitting inside the bin row beside "needs a
 * look", where it read as a third bin number and was the one nobody could place.
 * The count a filter row wants beside it is the size of the thing being
 * filtered.
 */
export const importRowsInAll = (count: number): string => `${formatCount(count)} in all`;

/* ————— a name that arrives twice (§1.5) ————— */

/**
 * **The two answers, as the office would say them.** Not "merge" and "keep
 * both": the question on the screen is which of two things the file is doing,
 * and these are the two readings of it.
 */
export const IMPORT_DUPLICATE_SAME = 'Same person';
export const IMPORT_DUPLICATE_DIFFERENT = 'Different people';

/** The same two, over everything the filter reaches. */
export const IMPORT_BULK_DUPLICATE_SAME = 'Join as one person';
export const IMPORT_BULK_DUPLICATE_DIFFERENT = 'Treat as different people';

/**
 * **Who else on this file carries this name** — the question, made answerable.
 *
 * "Another row on this file has the same name" is not a thing anybody can act on
 * without being told which row, and on a file whose entries repeat the names are
 * identical by construction — so the parser's own address for the other entry is
 * the only thing that tells them apart, and it is said in brackets after the
 * name rather than instead of it.
 */
export function importDuplicateGroupLine(
  peers: readonly { locator: string; display_name: string }[],
): string {
  return `Also on this file: ${peers
    .map((peer) => `${peer.display_name} (${peer.locator})`)
    .join(', ')}`;
}

/** What a folded row became: everything it carries is on that person now. */
export const importFoldedIntoLine = (name: string): string => `Joined with ${name}`;

/* ————— two rows that disagree about who they are (§1.5) ————— */

/**
 * **The five identifier kinds mid-sentence**, where {@link ANCHOR_KIND_LABELS}
 * is the same five as a table's row heading.
 *
 * "Two rows share an identifier and disagree on Phone number" is a sentence with
 * a column heading dropped into it. What the office says is *phone*, so that is
 * what the line says, and the two spellings live side by side rather than one of
 * them being case-folded at the call site — `external_id` is a phrase either way
 * and would not survive it.
 */
export const ANCHOR_KIND_WORDS: Readonly<Record<IdentifierAnchorKind, string>> = {
  email: 'email',
  phone: 'phone',
  member_number: 'member number',
  external_id: 'external id',
  handle: 'profile link',
};

/** `a`, `a and b`, `a, b, and c` — American English, serial comma and all. */
const andList = (parts: readonly string[]): string => {
  if (parts.length <= 1) return parts[0] ?? '';
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
};

/** One row of a conflict, as the office would point at it: who, and where. */
const conflictWho = (row: { display_name: string; source_label: string }): string =>
  `${row.display_name} (${row.source_label})`;

/**
 * **Which rows disagree with this one, and about what** — the sentence
 * `identifier_conflict` was missing.
 *
 * "Two rows share an identifier and disagree on another" names neither the
 * identifier nor the other row, and on Punta Brava's re-import the other row was
 * four hundred lines away: the office could see that something was wrong and not
 * what, or with whom. Everything in it is a thing the dry run already knew.
 *
 * The other shape is the one where nothing in the *file* is shared — two rows
 * whose different identifiers both belong to one person already on record — and
 * it says that instead of pretending to name an identifier it has not got.
 */
export function importConflictLine(conflict: ImportRowConflict, selfLocator: string): string {
  const others = conflict.rows.filter((row) => row.locator !== selfLocator);
  const who = andList(others.map(conflictWho));
  const head =
    conflict.shared.length > 0
      ? `Shares ${andList(conflict.shared.map((one) => one.summary))} with ${who}`
      : `Is the same person on record as ${who}`;
  if (conflict.disagree_on.length === 0) return `${head}.`;
  const subject = conflict.rows.length === 2 ? 'the two differ' : 'these rows differ';
  return `${head}; ${subject} on ${andList(
    conflict.disagree_on.map((kind) => ANCHOR_KIND_WORDS[kind]),
  )}.`;
}

/**
 * **What *different people* costs**, said before it is pressed rather than found
 * in the dry run's lines afterwards.
 *
 * An identifier two rows claim is evidence for neither, so it is written to
 * neither — which is the part of the answer nobody would guess, and the part
 * that makes it safe. Nothing to say where the file shares no identifier at all.
 */
export const importConflictDropLine = (conflict: ImportRowConflict): string | null =>
  conflict.shared.length === 0
    ? null
    : `Different people writes ${andList(
        conflict.shared.map((one) => one.summary),
      )} to ${conflict.rows.length === 2 ? 'neither of them' : 'none of them'}.`;

/**
 * **The rows of one disagreement do not agree about the answer** — the state a
 * file can reach without anybody being careless, and the one that needs saying
 * out loud because nothing on the row looks wrong.
 *
 * The answer doors write a word across the whole set, so a set holds two answers
 * only when the *set* has moved underneath them: a correction re-unions two
 * that were settled separately, a plan edit brings another row in, two tabs
 * answer at the same moment. Nothing is folded and nothing is dropped until they
 * agree, and the line says exactly that rather than leaving a row that looks
 * answered sitting in the bin.
 */
export const IMPORT_CONFLICT_MIXED =
  'The rows of this disagreement do not all have the same answer. Nothing is joined and nothing is dropped until every one of them says the same thing.';

/**
 * **How many times this file printed this name** — the card's own line, under
 * the name it is a card about.
 *
 * "Entries" and not "rows": a document has no rows, and what the office is
 * counting is how many times the file wrote this person down.
 */
export const importGroupEntriesLine = (count: number): string =>
  `${formatCount(count)} ${count === 1 ? 'entry' : 'entries'} on this file`;

/**
 * **Whether the copies say the same thing** — two words beside a group, and the
 * first thing anybody looks for when deciding whether one name printed twice is
 * one person.
 *
 * A cue and not a verdict: identical text is a file that printed an entry twice,
 * which is nearly always one person, and different text is the case where the
 * office has to read both. Neither one answers the question, which is why the
 * two answers are still buttons.
 */
export const IMPORT_DUPLICATE_TEXT_SAME = 'same text';
export const IMPORT_DUPLICATE_TEXT_DIFFERENT = 'different text';

/**
 * **The answer, once it has been given**, said as what it does rather than as
 * the button that was pressed: the later copies go onto the first one, or every
 * copy stays and is its own person.
 */
export const IMPORT_DUPLICATE_ANSWERED_SAME = 'Joined with the first';
export const IMPORT_DUPLICATE_ANSWERED_DIFFERENT = 'Kept as different people';

/** One bad copy dropped, without answering the question about the rest. */
export const IMPORT_COPY_SET_ASIDE = 'Set aside';

/** The clamp on a copy's own text, opened and closed. */
export const IMPORT_EXCERPT_MORE = 'More';
export const IMPORT_EXCERPT_LESS = 'Less';
export const importExcerptToggleLabel = (open: boolean, where: string): string =>
  open ? `Show less of what ${where} says` : `Show all of what ${where} says`;

/** A copy the file carries that says nothing at all — no note, no bio. */
export const IMPORT_COPY_NO_TEXT = 'No notes on this entry';

/**
 * **The rest of what caught a row**, under the one it is filed by.
 *
 * The row reads one line, because one row is one gesture; this is the rest of
 * the account, and a row that fails nothing else says nothing at all.
 */
export function binReasonAlsoLine(reasons: readonly string[]): string | null {
  const rest = reasons
    .slice(1)
    .map((rule) => binReasonLine(rule))
    .filter((line): line is string => line !== null);
  return rest.length === 0 ? null : `also: ${rest.map(lower).join(' · ')}`;
}

/** A bin line reads as a sentence on its own; after "also:" it is a clause. */
const lower = (line: string): string =>
  /^[A-Z][a-z]/.test(line) ? `${line.slice(0, 1).toLowerCase()}${line.slice(1)}` : line;

/* ————— what the commit did, and what is left ————— */

export const IMPORT_RESULT_HEAD = 'What this import did';
export const IMPORT_UNDONE_HEAD = 'Undone';
export const IMPORT_NOTES_FAILED_HEAD = 'Some notes could not be read';

/** The retry button counts the rows *and* the notes, because both come back. */
export function importRetryLabel(rows: number, notes: number): string {
  if (rows > 0 && notes > 0) {
    return `Try the ${formatCount(rows)} that failed and the ${formatCount(notes)} unread notes again`;
  }
  if (rows > 0) return `Retry failed rows: ${formatCount(rows)}`;
  return `Read the ${formatCount(notes)} notes that failed again`;
}

/* ————— the read-only halves of the plan (§1.7) ————— */

export const IMPORT_PLAN_JOINS_HEAD = 'A second sheet, attached to the first';
export const IMPORT_PLAN_RECORD_FIELDS_HEAD = 'Details kept on the person';
export const IMPORT_PLAN_BIN_HEAD = 'What gets held back for a look';
export const IMPORT_PLAN_NAME_HEAD = 'Where the name comes from';
export const IMPORT_PLAN_SOURCE_HEAD = 'What counts as a person';
export const IMPORT_PLAN_USER_MATCH_HEAD = 'People who sign in here';
export const IMPORT_PLAN_NO_BIN_RULES = 'Nothing is held back beyond the name checks.';
export const IMPORT_PLAN_NO_RECORD_FIELDS = 'No columns are kept as details.';
export const IMPORT_PLAN_NO_JOINS = 'One sheet, read on its own.';

/** The plan moved under an unsaved edit — the two ways out, named (D3). */
export const IMPORT_PLAN_MOVED =
  'The plan changed while you were editing. Reload to take the new one, or keep editing and save over it.';
export const IMPORT_PLAN_RELOAD = 'Reload';
export const IMPORT_PLAN_KEEP_EDITING = 'Keep editing';

/** The name rule, in words: which column, and whether a couple is split. */
export function planNameRuleWords(rule: {
  kind: 'column' | 'columns' | 'heading';
  column?: { index: number; header: string };
  columns?: readonly { index: number; header: string }[];
}): string {
  const name = (column: { index: number; header: string }) =>
    column.header.length > 0 ? column.header : `column ${column.index}`;
  if (rule.kind === 'heading') return 'The heading above each entry';
  if (rule.kind === 'columns') return (rule.columns ?? []).map(name).join(' then ');
  return rule.column ? name(rule.column) : 'a column';
}

export const NAME_SPLIT_LABELS: Readonly<
  Record<'none' | 'couple_heading' | 'couple_cell', string>
> = {
  none: 'One name per entry',
  /* The bios document's "Mr & Mrs" headings, which are two people (§1.5). */
  couple_heading: 'A heading naming two people is split into two',
  /* The same thing in a spreadsheet's name column (§1.16a): "Stephen & Erica
     Malbon" is two people, and the row's identifiers stay with the first. */
  couple_cell: 'A cell naming two people is split into two',
};

export const USER_MATCH_LABELS: Readonly<
  Record<'propose_member' | 'propose_default' | 'off', string>
> = {
  propose_member: 'Somebody on this file who signs in here is matched to their own record',
  propose_default: 'Somebody on this file who signs in here is matched, at the default level',
  off: 'Nobody on this file is matched to somebody who signs in here',
};

export const RECORD_RULE_LABELS: Readonly<Record<'row' | 'heading', string>> = {
  row: 'One row of the sheet is one person',
  heading: 'One heading and the block under it is one person',
};

/** One transform, said as what it does to the cell (§2.2's six). */
export function transformWords(transform: {
  kind: string;
  separator?: string;
  format?: string;
  prefix?: string;
  find?: string;
  replace?: string;
}): string {
  switch (transform.kind) {
    case 'split_list':
      return `split on “${transform.separator ?? ','}”`;
    case 'strip_state_suffix':
      return 'drop the state on the end';
    /* §1.16b. A verb phrase like its neighbours: this list is read as one line
       of steps ("split on ',', then drop the state on the end"), and a noun
       phrase in the middle of it reads as a description of the column rather
       than as something the import does to it. */
    case 'split_orgs':
      return 'split the organizations in the cell';
    case 'place_suffix':
      return 'move the place on each name to its location';
    case 'respace':
      return 'put the spaces back in run-together names';
    case 'date':
      return transform.format ? `read as a date (${transform.format})` : 'read as a date';
    case 'strip_prefix':
      return `drop “${transform.prefix ?? ''}” from the front`;
    case 'replace':
      return `replace “${transform.find ?? ''}” with “${transform.replace ?? ''}”`;
    case 'trim':
      return 'trim the spaces';
    default:
      return transform.kind;
  }
}

/* ————— the file, before it is sent ————— */

/**
 * **25 MB, under Cloud Run's 32** — the same cap the server's upload
 * interceptor enforces, checked here so the office is told before the browser
 * spends a minute sending a file that will be refused (C2).
 */
export const IMPORT_MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export function importFileTooBig(bytes: number, name: string): string | null {
  return bytes <= IMPORT_MAX_UPLOAD_BYTES ? null : fileTooBigLine(bytes, name);
}

/**
 * The one sentence for a file over the cap, with its size and a way to make it
 * smaller when the page knows the file. Fewer columns only helps a spreadsheet;
 * a PDF can only be split. A bare 413 from the server names no file, so it says
 * only the cap.
 */
export function fileTooBigLine(bytes: number | null, name: string | null = null): string {
  const size = bytes === null ? 'too big' : importFileSize(bytes);
  const cap = `That file is ${size}. The most that can be sent at once is ${importFileSize(
    IMPORT_MAX_UPLOAD_BYTES,
  )}.`;
  if (name === null) return cap;
  if (/\.(csv|xlsx|xls)$/i.test(name)) return `${cap} Split it, or export fewer columns.`;
  return `${cap} Split it into smaller files.`;
}

/* ————————————————————————— the knowledge base —————————————————————————
   One page that answers "what does the system know about X, and who does X
   touch" (design note 2026-09-07). Every word here is an end-result word: the
   page never says entity, and it never names the two reads behind it. What it
   lists are people, organisations and events; what it counts are connections;
   what it says about a row is whether the org tracks them or merely has them on
   record. */

/** The rail entry and the page's own head — the name settled in the session. */
export const KNOWLEDGE_TITLE = 'Knowledge base';

export const KNOWLEDGE_SEARCH_PLACEHOLDER = 'A person, an organization, an event';

/**
 * The accessible name of the chip group. "What to show", in the People screen's
 * own grammar ("Who to show", "Order") — a reader hearing "Kind" is being read
 * the name of a column rather than told what the buttons do.
 */
export const KNOWLEDGE_KIND_LABEL = 'What to show';

/**
 * The chips, in the plural — they name what a chip *narrows to*, which is a
 * group of things rather than one thing.
 */
export const KNOWLEDGE_KIND_CHIPS: Readonly<Record<KnowledgeKind, string>> = {
  person: 'People',
  org: 'Organizations',
  event: 'Events',
};

/**
 * The kind in the panel's head and on a hop row, in the singular. Same three
 * words the network's detail panel hangs after a name, and deliberately
 * identical: a reader crossing from the graph to this page must not meet a
 * second vocabulary for the same three things.
 *
 * **A search row does not carry one.** The results are grouped under the chips'
 * own three headings, so every row on the page is already under the word — and
 * saying it again on each of fifty rows is fifty repetitions of something the
 * heading two lines up has said.
 */
export const KNOWLEDGE_KIND_WORDS: Readonly<Record<KnowledgeKind, string>> = {
  person: 'Person',
  org: 'Organization',
  event: 'Event',
};

/** The opening view: the one bounded list the page shows before it is asked. */
export const KNOWLEDGE_MOST_CONNECTED = 'Most connected';
/** A kind with nothing under it in the opening view. */
export const KNOWLEDGE_KIND_EMPTY: Readonly<Record<KnowledgeKind, string>> = {
  person: 'No people on record yet.',
  org: 'No organizations on record yet.',
  event: 'No events on record yet.',
};

/** A search that matched nothing, which is not the same as an empty org. */
export const KNOWLEDGE_NONE_MATCH = NO_MATCHES;

/**
 * **The cap, said as an instruction rather than as a number of pages** (§1.1).
 * There is no page two here on purpose: fifty rows is the most a reader can look
 * through, and past it the answer is a better search, not more scrolling.
 */
export const KNOWLEDGE_CAPPED = 'More than fifty match. Narrow the search.';

/**
 * The panel's four blocks, in the order they are read: the drawing first, then
 * the three lists under it.
 *
 * The graph leads because it is the answer to the question the page is opened
 * with — who does this touch — and a reader who has to scroll past two lists to
 * reach the picture reads the lists instead of the picture.
 */
export const KNOWLEDGE_GRAPH_HEAD = 'Connections';
export const KNOWLEDGE_TOUCHES_HEAD = 'Who it touches';
export const KNOWLEDGE_FACTS_HEAD = 'On record';
export const KNOWLEDGE_PROVENANCE_HEAD = 'Where it came from';

/** The counts after the two disclosures' heads: "On record · 37 facts". */
export const knowledgeFactCount = (count: number): string => plural(count, 'fact', 'facts');
export const knowledgeSourceCount = (count: number): string => plural(count, 'source', 'sources');

export const KNOWLEDGE_NO_TOUCHES = 'Nothing else on record touches this yet.';
export const KNOWLEDGE_NO_FACTS = 'Nothing is on record about this yet.';
export const KNOWLEDGE_NO_PROVENANCE = 'Nothing on record says where this came from.';

/**
 * **The panel column with nothing in it.**
 *
 * The column is on the page from the first frame — the list does not start wide
 * and jump left on the first selection — so it has to say what it is for while
 * it is empty. It names the three kinds a reader can pick, which is also the
 * answer to "what is this column", and it makes no claim about what is on
 * record.
 */
export const KNOWLEDGE_PANEL_EMPTY = 'Pick a person, organization or event to see who it touches.';

/** The panel's own controls. Nothing else is offered here — no merge, no rename. */
export const KNOWLEDGE_PANEL_CLOSE = 'Close';
export const KNOWLEDGE_OPEN_RECORD = 'Open record';

/* ———— the whole graph, opened from the page's footer ———— */

export const WHOLE_GRAPH_OPEN = 'See the whole graph';
export const WHOLE_GRAPH_TITLE = 'Whole graph';
export const WHOLE_GRAPH_CHOOSE_ACCOUNT = 'Choose a protection account to draw its graph.';
export const WHOLE_GRAPH_EMPTY = 'Nothing on record is tied to anything else yet.';
export const WHOLE_GRAPH_TOO_SLOW =
  'This graph is too large to read at once. The counts are not available either.';
export const WHOLE_GRAPH_FILTER_LABEL = 'Shown on the graph';
export const WHOLE_GRAPH_NOT_TRACKED = 'Not tracked';
export const WHOLE_GRAPH_NONE_SHOWN = 'Nothing on record matches the chips chosen.';

/** Beside the totals once a chip is off: how much of the graph is on screen. */
export function wholeGraphShowing(shown: number, total: number): string {
  return `Showing ${formatCount(shown)} of ${plural(total, 'record', 'records')}`;
}

/** "1,240 people · 310 organizations · 12 events · 4,800 ties". */
export function wholeGraphTotals(totals: {
  people: number;
  organizations: number;
  events: number;
  ties: number;
}): string {
  return [
    plural(totals.people, 'person', 'people'),
    plural(totals.organizations, 'organization', 'organizations'),
    plural(totals.events, 'event', 'events'),
    plural(totals.ties, 'tie', 'ties'),
  ].join(' · ');
}

/** Said in place of the picture when the graph is past what the view draws. */
export function wholeGraphTooLarge(cap: number): string {
  return `More than ${formatCount(cap)} records are tied into this graph, which is more than this view draws.`;
}

/** While the worker places the dots, with how far along it is once it says. */
export function wholeGraphLayingOut(records: number, progress = 0): string {
  const done = Math.floor(progress * 100);
  return `Laying out ${plural(records, 'record', 'records')}…${done > 0 ? ` ${done}%` : ''}`;
}

/** Said before a large graph is laid out, since the layout and drawing can slow the tab. */
export function wholeGraphLargeWarning(records: number): string {
  return `This graph has ${plural(records, 'record', 'records')}. Drawing it can take a while and may slow this browser tab until it is done.`;
}
export const WHOLE_GRAPH_DRAW_ANYWAY = 'Draw it';

/**
 * **When this last moved**, said with what the date is — the panel's head keeps
 * it, and the row does not.
 *
 * A date is the least of the six things a row could say and it was the longest,
 * so it left the row entirely (§1.3, revised): a reader scanning fifty rows is
 * asking who these people are, not when a record was last written to. It stays
 * where a reader has already stopped on one thing and can use it.
 */
export function knowledgeUpdated(at: string): string {
  return `Updated ${formatDay(at)}`;
}

/** The accessible name of the control that opens a row's panel. */
export function knowledgeOpenLabel(name: string): string {
  return `What is on record about ${name}`;
}

/**
 * **What a row says the org's standing with somebody is**: the vertical's own
 * word after "Tracked", or the same "Known" the People list uses for a person
 * the org holds without a record.
 *
 * A vertical this build has no words for gets "Tracked" alone rather than a
 * word borrowed from somebody else's vocabulary — the rule `subjectStatusLabel`
 * states, applied one level up.
 *
 * **A null status gets the same "Tracked" alone**, and it is a different reason
 * for the same answer (organisations-as-subjects note §1 R1): a tracked
 * organisation has no status because neither vertical has a word for one, not
 * because this build failed to recognise the vertical. There is nothing after
 * the separator to print in either case, so there is no separator.
 */
export function knowledgeStanding(
  entry: { tracked: { status: SubjectStatus | null } | null },
  vertical: string,
): string {
  if (!entry.tracked) return KNOWN_LEVEL_LABEL;
  const status = entry.tracked.status;
  const word = status === null ? null : subjectStatusLabel(vertical, status);
  return word ? `Tracked · ${word}` : 'Tracked';
}

/** "31 connections" — the count the row and the touch row both carry. */
export function knowledgeConnections(count: number): string {
  return plural(count, 'connection', 'connections');
}

/**
 * **Where a row came from, in one phrase.**
 *
 * The label is the *thing* and this is the sentence around it, which differs by
 * kind: an import is named by its file, a run by the person it was about, and
 * the three kinds with no referent carry the phrase itself. `unknown` says
 * nothing at all — a row that reads "From unknown" is a row claiming to know
 * something it does not.
 */
export function knowledgeSourcePhrase(source: KnowledgeSource): string | null {
  switch (source.kind) {
    case 'research':
      return `From research on ${source.label}`;
    /* The gesture, not a place it came from: nobody sent this person in a file,
       the office said one record was two people and this is the other one. */
    case 'split':
      return `Split from ${source.label}`;
    case 'unknown':
      return null;
    default:
      return `From ${source.label}`;
  }
}

/**
 * What each provenance line is, in a word. "Research" rather than "Run": the
 * office reads runs as research everywhere else in this app (`RUN_KIND_LABELS`),
 * and the machinery's word for one is not a word this page owes anybody.
 */
export const KNOWLEDGE_PROVENANCE_WORDS: Readonly<Record<ProvenanceKind, string>> = {
  import: 'Import',
  run: 'Research',
  note: 'Note',
  intake: 'Added by hand',
  /* The gesture rather than the row: what the office did was join two records,
     and the prose the system filed to cite it is bookkeeping the reader never
     asked for. */
  link: 'Link',
  /* What the office did, in its own word. The line's label already says which
     record it was split from. */
  split: 'Split',
};

/**
 * The label beside that word. A run's label is its kind on the wire, so it is
 * read through the same table the run strip and the run history use; everything
 * else already arrives as a phrase a person wrote or a file was called.
 */
export function provenanceLabel(entry: ProvenanceEntry): string {
  if (entry.kind !== 'run') return entry.label;
  return RUN_KIND_LABELS[entry.label as RunKind] ?? humanize(entry.label);
}

/**
 * **Whose fact this is, when the panel is not centred on them.**
 *
 * The record's sentence leaves its subject implicit — "Blue Harbour, Chief
 * executive" is written for the person it is about. An organisation's panel
 * lists the same lineages from the other end, and twelve of them unattributed
 * read as twelve facts about the organisation with nobody in them. So the
 * subject is named ahead of the sentence, and only there: on the person's own
 * panel the sentence is already about them and naming them would be the app
 * telling a reader who they are looking at.
 */
export function knowledgeFactSubject(subject: string, line: string): string {
  return `${subject}: ${line}`;
}

/**
 * **The panel is a read of a page, not of a dossier** — past the cap it says so
 * and points at the record, which is the surface that holds everything.
 *
 * It cannot say "and 14 more": the server sends a cap bit rather than a total,
 * on purpose (`KNOWLEDGE_FACTS_LIMIT`), and inventing the remainder from a page
 * that is by definition short of it would be a number nobody counted.
 *
 * **"on the record" is only true where there is a record to open.** A record is
 * a *tracked* person's dossier: there is none for an organisation or an event —
 * this panel is the whole of what the app shows about one — and none for a
 * person the org merely knows of, who has no dossier until somebody tracks them.
 * The kind alone got the second of those wrong, and sent a known person's reader
 * to a page the panel itself declines to link.
 *
 * So the caller passes the same gate that decides the link (`hasRecord`), and
 * the two cannot drift: if the head offers no way to the record, the line does
 * not name one.
 */
export function knowledgeFactsCapped(shown: number, hasRecord: boolean): string {
  const head = `The first ${formatCount(shown)} are shown`;
  return hasRecord ? `${head}; there are more on record.` : `${head}; there are more.`;
}

/**
 * **The one line People keeps about the people it no longer lists** (§1.6).
 *
 * "at least" when the knowledge search came back capped, because the count is
 * then a floor: the cap covers tracked and known together, so there may be
 * known people the search never reached.
 */
export function alsoKnownLine(count: number, capped: boolean): string {
  const people = plural(count, 'known person', 'known people');
  const verb = count === 1 ? 'matches' : 'match';
  return capped ? `Also at least ${people} ${verb}` : `Also ${people} ${verb}`;
}

/* ————————————————————————— linking two records by hand ————————————————————————— */

/**
 * **The knowledge panel's one write** (organisation-profile note §1.1): the
 * office saying, of an organisation or an event it is looking at, that it stands
 * in a relation to another record it already holds.
 *
 * The words here are the whole of what makes the gesture safe to offer on a page
 * whose every other control is a read. Nothing in them is a term of art and
 * nothing in them is an id: the office picks a name out of a search, picks a
 * sentence, reads the sentence, and presses.
 */
/**
 * One string for the door and for the head of what it opens: the modal is that
 * button pressed, and a reader who has just pressed "Link to another record"
 * should be looking at "Link to another record". Two constants held the same
 * words and could drift into two names for one gesture.
 */
export const KNOWLEDGE_LINK_ACTION = 'Link to another record';

/**
 * The head's own sub-line, which does two jobs: it says what is about to be
 * written down, and it says *which* record the sentence will be about — the
 * panel is a column beside a list, and the modal covers the name it was opened
 * from.
 */
export const knowledgeLinkLead = (name: string): string =>
  `Say how ${name} stands to another organization or event already on record.`;

export const KNOWLEDGE_LINK_SEARCH_LABEL = 'Find the other record';
export const KNOWLEDGE_LINK_SEARCH_PLACEHOLDER = 'Search organizations and events';

/** Below the minimum the box asks for nothing, and says so rather than sitting blank. */
export const KNOWLEDGE_LINK_TYPE_MORE = 'Type a name to search.';
export const KNOWLEDGE_LINK_NONE_MATCH = NO_MATCHES;
export const KNOWLEDGE_LINK_SEARCHING = 'Searching…';

/**
 * **Only what is already on record**, said where a reader would otherwise wonder
 * why they cannot type a new name. This door is a picker on purpose: the point
 * of it is to land on the *exact* existing record, and a name typed here would
 * be a second way to mint one.
 */
export const KNOWLEDGE_LINK_PICKER_NOTE =
  'Only existing records can be linked. People are not linked this way.';

/** The same rule from a person's panel, where the other record is a person too. */
export const KNOWLEDGE_FAMILY_PICKER_NOTE = 'Only people already on record can be picked.';

/** The family question, the other person named first: "How is Ada Moreno related to Theo Moreno?" */
export const knowledgeFamilyAsk = (other: string, here: string): string =>
  `How is ${other} related to ${here}?`;

export const KNOWLEDGE_FAMILY_PLACEHOLDER = 'Pick a relation';

/** The chosen record, and the way back to the search. */
export const knowledgeLinkChosen = (name: string): string => `Linking to ${name}.`;
export const KNOWLEDGE_LINK_CHANGE = 'Pick a different record';

/* One question, asked in the same words at both doors on purpose: the office is
   naming a tie between two records, and the gesture is the same one whether they
   are standing on a record or looking at a likely-merge card. */
export const KNOWLEDGE_LINK_RELATION_ASK = MERGE_CANDIDATE_READING_ASK;

/**
 * **Nothing these two can be to each other.** Two events, or anything with a
 * person in it: there is no such fact to write, so the modal says so instead of
 * offering a sentence the server would refuse.
 */
export const KNOWLEDGE_LINK_NO_READING =
  'These two cannot be linked to each other. An event is linked to the organization that ' +
  'hosted it, and organizations to one another.';

/* And the same confirmation, in the same words — see above. */
export const KNOWLEDGE_LINK_PREVIEW_LEAD = MERGE_CANDIDATE_RECORD_LEAD;

export const KNOWLEDGE_LINK_SAVE = 'Record it';
export const KNOWLEDGE_LINK_WORKING = 'Recording…';

/**
 * **The link was already there.** The office pressed a button and the record did
 * not change, which is a success and not a refusal — so it is said as what is
 * true, in the same place the confirmation would go.
 */
export const KNOWLEDGE_LINK_ALREADY = 'That was already on record.';
export const KNOWLEDGE_LINK_DONE = 'Recorded.';

/**
 * **The record picked out of the search is no longer there.** The server answers
 * 404 on either side of the pair for everything it cannot reach, and between the
 * search and the press that is one thing in practice: the other record was
 * merged into another, or somebody removed it. `errorLine` would fall through to
 * the envelope's own "Not found", which is a fragment rather than something a
 * reader can do anything with, so this door says what happened and what to do
 * about it.
 */
export const KNOWLEDGE_LINK_GONE = 'That record is no longer here. Search again.';

/* ————————————————————————— rename by hand ————————————————————————— */
/**
 * **Rename this record** (organisations-as-subjects note §2 chunk 1) — one
 * string for the door and for the head of what it opens, following the link
 * block above: a reader who has just pressed a button should be looking at the
 * words they pressed.
 *
 * "Rename" alone is the *control*, because the panel head and the record header
 * are rows of short actions beside "Link to another record" and a status; the
 * long form is the modal's own head, where there is room to say which of the
 * things on the page is about to change.
 */
export const KNOWLEDGE_RENAME_ACTION = 'Rename';
export const KNOWLEDGE_RENAME_TITLE = 'Rename this record';

/**
 * The head's sub-line: which record, by name, because the modal covers the name
 * it was opened from and the field below is pre-filled with a name a reader
 * could otherwise mistake for something they typed.
 */
export const knowledgeRenameLead = (name: string): string => `This record is named ${name}.`;

export const KNOWLEDGE_RENAME_LABEL = 'Name';

/**
 * **The half of a rename nobody can see**, said where the decision is made. It
 * is the whole reason renaming is safe here: without the redirect, the next
 * thing that used the old name would file a second record. A reader deciding
 * whether to change a name is deciding on this, so it is a help line under the
 * field rather than a confirmation afterwards.
 */
export const KNOWLEDGE_RENAME_HELP = 'Searches for the old name still find this record.';

export const KNOWLEDGE_RENAME_SAVE = 'Rename';
export const KNOWLEDGE_RENAME_WORKING = 'Renaming…';

/**
 * **The field is untouched**, so there is nothing to do — said on the disabled
 * button's own line rather than as an error, because nothing has gone wrong.
 * The modal opens in exactly this state, which is why it has to read as a
 * description and not as a complaint.
 */
export const KNOWLEDGE_RENAME_SAME = 'That is already the name.';

/**
 * **The record is gone.** The panel's Rename is opened from a row the reader
 * picked out of a list, and between the picking and the pressing that record can
 * be merged away or removed. `errorLine` would fall through to the envelope's
 * bare "Not found", which is a fragment; this says what happened.
 */
export const KNOWLEDGE_RENAME_GONE = 'That record is no longer here.';

/* ————————————————————————— join by hand ————————————————————————— */
/**
 * **Join with another record** (organisations-as-subjects note §2 chunk 1,
 * "Join by hand") — one string for the door and for the head of what it opens,
 * following the two blocks above.
 *
 * The long form is used in **both** places, unlike Rename, whose control is one
 * word. "Join" alone says nothing about what is joined to what, and the act has
 * no undo: a reader about to fold two records together should read the whole
 * sentence before they press it and again at the top of what opens.
 *
 * "Join", not "merge". A merge is what the machine does to two rows; joining two
 * entries is what the office is doing, and it is the word the likely-merge card
 * already uses for the same act ("Join these two", "Join them").
 */
export const KNOWLEDGE_JOIN_ACTION = 'Join with another record';
export const KNOWLEDGE_JOIN_TITLE = 'Join with another record';

/**
 * The head's sub-line: which record this is being done *from*, by name, because
 * the modal covers the entry it was opened on and every choice below is about
 * two names a reader has to keep straight.
 */
export const knowledgeJoinLead = (name: string): string => `This is the record for ${name}.`;

/**
 * **When to use this door**, said before the search rather than after the press.
 *
 * It exists because the obvious question — why is there a button for this when
 * the system asks about duplicates on its own — has an answer the reader cannot
 * see: the questions are raised off names that resemble each other, and "Clint"
 * beside "Clint Phillips" resembles nothing. So the line names the two shapes it
 * is actually for, and then says the half of the act nobody can see: the entry
 * that goes away goes on finding the record.
 */
export const KNOWLEDGE_JOIN_HELP =
  'For two entries of the same person, such as a first name from an import or a variant ' +
  'spelling. Searches for the other name find the one you keep.';

export const KNOWLEDGE_JOIN_SEARCH_LABEL = 'Find the other record';
export const KNOWLEDGE_JOIN_SEARCH_PLACEHOLDER = 'Search people';
export const KNOWLEDGE_JOIN_TYPE_MORE = 'Type a name to search.';
export const KNOWLEDGE_JOIN_NONE_MATCH = NO_MATCHES;
export const KNOWLEDGE_JOIN_SEARCHING = 'Searching…';

/**
 * **What this door can reach**, said under the box where a reader would
 * otherwise wonder why a company they can see on the page is not in the list.
 */
export const KNOWLEDGE_JOIN_PICKER_NOTE = 'Only people already on record can be joined this way.';

export const knowledgeJoinChosen = (name: string): string => `Joining with ${name}.`;
export const KNOWLEDGE_JOIN_CHANGE = 'Pick a different record';

/**
 * **Which of the two the office is keeping** — asked by name, twice, and never
 * by id.
 *
 * "Survive" rather than "keep its name", which is what the merge card asks: on
 * that card the direction of the fold is the gate's to decide and only the name
 * is the office's, and here the office decides both at once. One record goes on
 * being the record; the other becomes a pointer to it.
 */
export const KNOWLEDGE_JOIN_KEEP_ASK = 'Which record should survive?';

/**
 * Said beside a name in that choice when the office already keeps a record of
 * that person. It is the one thing about the two sides a reader cannot see from
 * the names, and it decides the answer: a tracked record cannot be folded away,
 * so picking the other one is a refusal waiting to happen.
 */
export const KNOWLEDGE_JOIN_TRACKED_MARK = '(tracked)';

/**
 * **The record is gone.** The picked record can be merged away or removed
 * between the picking and the pressing, and the envelope's bare "Not found" is a
 * fragment. It says the likeliest reason as well, because on this door the
 * likeliest reason is that somebody did the very thing the reader was about to.
 */
export const KNOWLEDGE_JOIN_GONE =
  'That record is no longer here. It may already have been joined.';

/* ————————————————————————— organisations —————————————————————————
   The office's own roster of organisations, beside People
   (organisations-as-subjects note §1 R5/R8). Every word here is an end-result
   word, as everywhere else in this file: the screens say organisation, website,
   connections and tracked, and they never say entity, anchor, subject record or
   domain. No id is ever printed. */

/** The rail entry, the page's own head, and the breadcrumb back to it. */
export const ORGANISATIONS_TITLE = 'Organizations';

/** The one hero on the list, cased as People's "New person" is. */
export const ORGANISATIONS_NEW_ACTION = 'New organization';

/** The search box asks for the one thing it searches, as People's does. */
export const ORGANISATIONS_SEARCH_LABEL = 'Search';
export const ORGANISATIONS_SEARCH_PLACEHOLDER = 'Name';

/** The three columns. "Added" is when the office started keeping the record. */
export const ORGANISATIONS_COLUMNS = {
  name: 'Name',
  /** Drawn in a security org only; every other vertical's rows carry no account. */
  account: 'Protection account',
  connections: 'Connections',
  added: 'Added',
} as const;

/**
 * **Nothing tracked yet**, which is a beginning rather than a shortage — so
 * "yet", by the same rule `PEOPLE_EMPTY_BY_LEVEL` keeps. There is no second
 * empty line for a level here: this list is tracked-only and that is not a
 * filter, so the only other emptiness is a search that matched nobody.
 */
export const ORGANISATIONS_EMPTY = 'No organizations tracked yet.';

/** The search matching nothing, which is not the same as tracking nothing. */
export const ORGANISATIONS_NONE_MATCH = NO_MATCHES;

/* ————— the New Organisation door ————— */

/** The page's head and its breadcrumb tail, in the sentence case a head takes. */
export const ORGANISATION_NEW_TITLE = 'New organization';

export const ORGANISATION_NAME_LABEL = 'Name';

/**
 * **What the office knows, in their own words** — optional, unlike a person's,
 * and since 2026-09-09 the *only* field either organisation door carries.
 *
 * The typed Website box is gone from both of them (owner ruling: "it should also
 * be a single prose field that undergoes extraction, just like w people. No
 * extra form field for website."). A site the office knows is a thing they can
 * write down — "they are at medici.org" — and it lands as the same confirmed
 * anchor the field wrote, read out of the words by the same pass that reads the
 * relationships. Two boxes were two ways of saying one thing, and the second one
 * was the one that could be refused.
 *
 * A person entered from nothing is entered with a sentence about why, because
 * the reading of that sentence is the only thing seeding the record. An
 * organisation is not: the office may know nothing but a name, and the lookup
 * fills the rest in. The help line says what is useful and, in its last
 * sentence, the one thing this box will not do — a paragraph naming three people
 * files no people, and a reader who expects otherwise would think it had failed.
 */
export const ORGANISATION_KNOW_LABEL = 'What you know';
export const ORGANISATION_KNOW_HELP =
  'Anything the office knows: what it does, where it is, who it belongs to. People are not created from here.';

export const ORGANISATION_CREATE = 'Create';
export const ORGANISATION_CREATING = 'Creating…';
export const ORGANISATION_CANCEL = 'Cancel';

/**
 * **The namesake question, in an organisation's words.**
 *
 * The card itself is the person door's, and deliberately: the question is the
 * same question — the same name is on file, is this the same one — and the
 * evidence it lays out (what each is tied to, how much is on it, how long it has
 * stood) is the same evidence. Only the nouns differ, so only the nouns are
 * restated here.
 *
 * "Already on record" for a namesake the office already tracks, the same label
 * every kind of record uses; "subject" is the machinery's noun.
 */
export const ORGANISATION_CONFLICT_LEAD = nameOnRecordLine('organization');
export const ORGANISATION_CONFLICT_NEW = 'Create a new organization';
export const ORGANISATION_CONFLICT_NEW_NOTE = 'Different organization, same name';
export const ORGANISATION_CONFLICT_TAKEN = ALREADY_ON_RECORD;

/* ————— tracking one from the knowledge base ————— */

/**
 * **Track, on an organisation** (R8) — one press and no picker.
 *
 * The person door opens a picker because tracking a person means giving them one
 * of the vertical's words. Neither vertical has a word for an organisation, so
 * there is nothing to choose: the press is the whole gesture, and it says what
 * it will do rather than opening a menu with one item in it.
 */
export const ORGANISATION_TRACK_ACTION = 'Track this organization';
export const ORGANISATION_TRACK_WORKING = 'Tracking…';

/* ————— the organisation's record page ————— */

/**
 * **The line under the name**, when the record has nothing to put in it.
 *
 * A person's subline always has something true to say — when they were added,
 * when they were reclassified. An organisation entered from a name alone has
 * none of that on its head: the website, the location and the jurisdiction are
 * all null until somebody states one. So the fallback says the one thing that is
 * true of every record on this page, which is that the office keeps one.
 */
export const ORGANISATION_SUBLINE_FALLBACK = 'Tracked organization';

/* **Add what you know** had its own words here and its own modal beside them.
   Both are gone (owner ruling, 2026-09-09): what the office knows enters through
   the lookup's box, which is the same door with the same three effects and asks
   at the one moment the office is thinking about this record. The words are
   `ORGANISATION_KNOW_LABEL` and the placeholder beside it, and they are read
   from the two surfaces that still ask — the create page and the lookup box. */

/**
 * **Stop tracking**, and the sentence that says what it does *not* do.
 *
 * The confirmation is the whole of the honesty here: untracking an organisation
 * is not the person shred. Nothing is tombstoned, no fact or relation is
 * touched, and the organisation stays in the knowledge base and on every graph
 * that draws it. The line says so plainly, because "Stop tracking?" on its own
 * reads like a deletion to anybody who has met one.
 */
/**
 * **The way into the substrate, from the foot of a record** (owner ruling,
 * 2026-09-10: "I notice a lack of a 'Raw data' view for organizations (people
 * have this)" — and "Remember that it's not a tab - it's a button at the
 * footer").
 *
 * A constant rather than the literal each foot used to spell for itself, because
 * the two feet now say the same thing about the same reading and the words are
 * the whole of what makes them read as the same door.
 */
export const RAW_DATA_ACTION = 'Raw data';

/**
 * **Asking for the same reading again, from the foot of a record** — the word a
 * person's research tab has always used, now a constant both feet read (owner
 * ruling, 2026-09-10: "once an investigation succeeds, we should remove the
 * button from the top and move it down to the bottom … as a 'Refresh' option,
 * just like with people").
 *
 * "just like with people" is the ruling, so it is the same word in the same
 * place doing the same thing, and a constant is what keeps that true: two feet
 * spelling a literal each is how one of them comes to say "Re-run".
 *
 * What it starts differs by record, and that is the point rather than a
 * discrepancy. A person's opens a picker over the facets a dossier already ran;
 * an organisation's starts the investigation again, there being nothing to pick
 * between — an organisation's depth is one word, and the deeper of the two is
 * the one the record has already had.
 */
export const REFRESH_ACTION = 'Refresh';

export const ORGANISATION_UNTRACK_ACTION = 'Stop tracking';
export const ORGANISATION_UNTRACK_WORKING = 'Stopping…';
export const ORGANISATION_UNTRACK_KEEP = 'Keep tracking';
export function organisationUntrackLine(name: string): string {
  return `Stop tracking ${name}? It stays in the knowledge base; only the record goes.`;
}

/**
 * **No summary yet.** The line said its own reason until chunk 3c: there was no
 * lookup to offer beside it. There is now, and the offer sits above this block
 * rather than in it — the buttons are about the record, and this line is about
 * one block of it, which is why the words did not have to change.
 */
export const ORGANISATION_NO_SUMMARY = 'No summary yet.';

/**
 * **When the summary was last written**, under the prose it dates.
 *
 * A person's dossier stamps "Researched ⟨day⟩" in its foot; an organisation has
 * one block of prose and no foot, so the stamp goes under the prose. "Updated"
 * rather than "Researched": the same two-to-four sentences are rewritten by every
 * lookup, and what the day says is when this text was last true — not when the
 * record was first looked at.
 */
export function organisationSummaryUpdated(day: string): string {
  return `Updated ${day}`;
}

/* ————————————————————————— what a source said it is ————————————————————————— */

/**
 * **About** — the `org_profile` fact, in the source's own account (catalog v6,
 * organisations-as-subjects note §2 chunk 3).
 *
 * It sits under the summary and is a different thing from it, which is the whole
 * reason it has a head of its own: the summary is a *model's* two to four
 * sentences over everything the run found, and this is what a page the run read
 * actually said about the place, field by field. One is a reading and the other
 * is close to a quote, and a block that merged them would leave the office
 * unable to tell which it was looking at.
 *
 * "About" rather than "Profile": the office is reading about an organisation,
 * not inspecting a record type.
 */
export const ORGANISATION_PROFILE_HEAD = 'About';

/** The Overview's panel head, and the words of its aside. */
export const ORGANISATION_SUMMARY_HEAD = 'Summary';
export const ORGANISATION_ASIDE = {
  leadership: 'Leadership',
  investigated: 'Investigated',
  lookedUp: 'Looked up',
  moreLeaders: (count: number): string => `${count} more`,
} as const;

/**
 * **The fields, in the order a reader asks them in**: where it is, how long it
 * has been there, how big it is, what trade it is in, and where to look.
 *
 * Only the ones the source stated are drawn. An empty row would say the run
 * failed to find something it may never have looked for, and five of them under
 * a description would make a read profile look like a form nobody filled in.
 */
export const ORGANISATION_PROFILE_LABELS = {
  where: 'Where',
  founded: 'Founded',
  size: 'Size',
  sector: 'Sector',
  website: 'Website',
} as const;

/**
 * **The record's other websites, said under the one it leads with** (owner
 * ruling, 2026-09-10).
 *
 * An organisation may have more than one confirmed website — Medici was at
 * `medici.md` before it moved to `medi.ci`, and the office has said so — and the
 * head and the About row both print the primary. This is where the rest are, in
 * plain text rather than as links: they are what the company *used to* answer
 * at, and offering to open one is offering a page that may not be there.
 *
 * "Also" and not "Formerly", because nothing on the record knows which: the
 * office may have recorded a second live address. What the line claims is only
 * what it can — this company answers to these too.
 */
export const organisationAlsoWebsites = (hosts: readonly string[]): string | null =>
  hosts.length === 0 ? null : `Also ${hostList(hosts)}`;

/**
 * **The addresses a page printed, said under the record's own** (owner ruling
 * R10, 2026-09-10).
 *
 * A different line from the one above and deliberately a weaker one. "Also
 * medici.md" is the record saying this company answers there — somebody in the
 * office typed it, or a run of this record's own read it. "Also seen as
 * medici.md" is the record saying only that the words appeared on a page beside
 * this company's name, which may be a second address, an old one, or a mistake
 * somebody made in a press release. The office should be able to tell those two
 * apart at a glance, so they are two sentences and not one list.
 *
 * Plain text, like the line above, and for a stronger reason: nothing here
 * promises the address is real.
 */
export const organisationAlsoSeenAs = (hosts: readonly string[]): string | null =>
  hosts.length === 0 ? null : `Also seen as ${hostList(hosts)}`;

/** "a", "a and b", "a, b and c" — the office's own punctuation, not a JSON list. */
const hostList = (hosts: readonly string[]): string =>
  hosts.length === 1
    ? (hosts[0] as string)
    : `${hosts.slice(0, -1).join(', ')} and ${hosts[hosts.length - 1]}`;

/**
 * **Where it is and where it is registered, on one line.**
 *
 * They are two fields and one question, and a company registered somewhere it
 * does not sit is the interesting case rather than a second row: "Austin, Texas
 * · registered in Delaware" says the thing the office would otherwise have to
 * put together itself. The jurisdiction leads on its own where there is no
 * location, because a bare place name in a row labelled Where would read as the
 * address.
 */
export function organisationWhereLine(
  location: string | null,
  jurisdiction: string | null,
): string | null {
  const where = location?.trim() || null;
  const registered = jurisdiction?.trim() || null;
  if (!registered) return where;
  return where ? `${where} · registered in ${registered}` : `Registered in ${registered}`;
}

/**
 * **Where the profile was read**, muted, under the block it explains.
 *
 * The server hands over the labels a reader can place: a host for a page that
 * was fetched, and a phrase for the office's own words, which have no address.
 * The two phrases it can send are known here so the sentence reads as a
 * sentence — "From the office", not "From The office" — and anything else is a
 * host and is printed exactly as it came.
 *
 * A profile is one lineage per organisation, so a second source corroborates
 * this block rather than opening another one, and the line is therefore a list:
 * naming one source for a description three pages agree on would be picking at
 * random.
 */
const PROFILE_SOURCE_PHRASES: Readonly<Record<string, string>> = {
  'The office': 'the office',
  'A source with no address': 'a source with no address',
};

export function organisationProfileSourceLine(
  sources: readonly { label: string }[],
): string | null {
  const named = sources
    .map((source) => source.label.trim())
    .filter((label) => label.length > 0)
    .map((label) => PROFILE_SOURCE_PHRASES[label] ?? label);
  return named.length > 0 ? `From ${named.join(', ')}` : null;
}

/* ————————————————————————— looking an organisation up ————————————————————————— */

/**
 * **The two buttons on the record** (organisations-as-subjects note §1 R3a, §2
 * chunk 3 "As grounded" 2).
 *
 * They name the *depth*, which bends the house rule that a control names the run
 * kind — and it is the ruling, not a slip: for a person the depth is derived
 * from status, and an organisation has none, so the office is the only thing
 * that can say how deep to go. The strip and the history row still say the kind.
 */
export const ORG_LOOKUP_ACTIONS: Record<OrgResearchDepth, string> = {
  lookup: 'Look this up',
  investigation: 'Investigate',
};

/** One working label for both, as the person page's kick-off has. */
export const ORG_LOOKUP_WORKING = 'Starting…';

/**
 * **The box the lookup opens first** (owner ruling, 2026-09-09: "The lookup
 * should prompt to enter 'What you know' in a text box before kicking off the
 * lookup").
 *
 * One field, and it is the same field the create door and Add what you know
 * carry — because what the office types into it does the same thing in all
 * three: it is filed as a statement, read for relationships and stated anchors,
 * and handed to the run as context. Optional, because most of the time the
 * office knows nothing but a name, and that is exactly the record a lookup is
 * for.
 *
 * The placeholder rather than a help line under the box: the box is empty when
 * the reader arrives, so the words belong where the cursor is.
 *
 * **The same box asks before an investigation too** (owner ruling, 2026-09-10:
 * "Unlike the person equivalent, hitting refresh on the org page has no
 * confirmation box. There's also no where for me to add additional data"). So
 * the head and the button that starts it are a word per depth rather than one
 * literal: the box is the same box, and what it says it is about to start is
 * the thing the office pressed.
 *
 * **The head says which press this is, and the two investigation doors are not
 * the same press** (owner ruling, 2026-09-10). The first cut used "Run the
 * investigation again" at both, on the reasoning that the box behind them is one
 * box; the reasoning was about the box and the head is about the reader. The
 * foot's Refresh *is* always a repeat and says so. The Overview's Investigate is
 * the record's first one, and a head telling somebody they are doing a thing
 * again the first time they do it is the app misreading the page back to them.
 *
 * So the head reads off the door as well as the depth: `again` is the foot's
 * Refresh, and only that. The **button** is unchanged either way — "Start the
 * investigation" is right for a first press and a repeat both, because what it
 * starts is the same run.
 */
export const ORG_LOOKUP_MODAL_HEADS: Record<OrgResearchDepth, string> = {
  lookup: ORG_LOOKUP_ACTIONS.lookup,
  investigation: ORG_LOOKUP_ACTIONS.investigation,
};

/**
 * **The head when the press is a repeat** — the record foot's Refresh, the one
 * door that is only ever reached over a reading that already happened.
 *
 * Only investigation depth differs. "Look this up" is the head at a lookup door
 * whether or not one has run before: the shallow read is the cheap one the
 * office repeats without ceremony, and it has no separate "again" door to
 * distinguish it from.
 */
export const ORG_LOOKUP_MODAL_HEADS_AGAIN: Record<OrgResearchDepth, string> = {
  lookup: ORG_LOOKUP_ACTIONS.lookup,
  investigation: 'Research again',
};
export const ORG_LOOKUP_STARTS: Record<OrgResearchDepth, string> = {
  lookup: 'Start the lookup',
  investigation: 'Start the investigation',
};
export const ORG_LOOKUP_STARTING = 'Starting…';
export const ORGANISATION_KNOW_PLACEHOLDER =
  'Anything the office knows: what it does, where it is, who it belongs to.';

/**
 * **The one line under the lookup's box** (owner ruling, 2026-09-10).
 *
 * The box has always carried its invitation in the placeholder, and this is not
 * a second copy of it: it names the one thing the office would not think to type
 * and the run most needs. A company that moved keeps turning up at its old
 * address — Medici's 2018 coverage prints `medici.md`, which it left — and a run
 * that has never been told about it sets every one of those pages aside as
 * another company's. Typed here, the former address goes on the record and the
 * run reads its own history.
 *
 * **And a second address it still answers at**, since a record can hold several
 * (2026-09-10): the line names both cases rather than only the former one,
 * because the run treats them the same way — every confirmed host is this
 * organisation's, and a page carrying any of them is a page about it.
 */
export const ORG_LOOKUP_KNOW_HELP =
  'Add any other website it has, or one it used to have. The lookup reads pages that carry it as this organization’s.';

/**
 * **The strip's verb, by depth.** "Looking up" and "Investigating" are the
 * present tense of the two buttons, so a reader who pressed one sees the words
 * they pressed still happening.
 */
export const ORG_LOOKUP_VERBS: Record<OrgResearchDepth, string> = {
  lookup: 'Looking up',
  investigation: 'Investigating',
};

/** The subject where the strip has no name to say — the run without its record. */
export const ORG_LOOKUP_UNNAMED = 'the organization';

/** "Looking up Medici" / "Investigating Medici". */
export function orgLookupRunningLead(depth: OrgResearchDepth, name: string | null): string {
  return `${ORG_LOOKUP_VERBS[depth]} ${name?.trim() || ORG_LOOKUP_UNNAMED}`;
}

/**
 * **What a finished lookup says**, and deliberately not "Dossier ready".
 *
 * A dossier is a person's, and an organisation's answer is two to four sentences
 * and a leadership list. The phrase names the act that finished, at the depth it
 * was run — the same distinction the two buttons make.
 */
export const ORG_LOOKUP_DONE: Record<OrgResearchDepth, string> = {
  lookup: 'Lookup done',
  investigation: 'Investigation done',
};

/** The run block's label for an organization's run: the button's own word. */
export const ORG_RUN_LABELS: Record<OrgResearchDepth, string> = {
  lookup: 'Lookup',
  investigation: 'Investigation',
};

/** And what a failed one says, at the same two depths. */
export const ORG_LOOKUP_FAILED: Record<OrgResearchDepth, string> = {
  lookup: 'Lookup failed',
  investigation: 'Investigation failed',
};

/**
 * **Why the last run failed**, said on the page after the run block has left it
 * (owner ruling, 2026-09-09: a finished run's bar "should just go away"). The
 * reason stays beside the button that asks again, under the same word the run
 * block used for it ("Lookup failed", "Investigation failed").
 */
export function organisationRunFailedLine(depth: OrgResearchDepth, message: string | null): string {
  const failed = ORG_LOOKUP_FAILED[depth];
  const said = message?.trim();
  if (!said) return `${failed}.`;
  return `${failed}: ${enginePlainReason(said)}`;
}

/**
 * **The engine's own words, unless they were never words** — the belt under the
 * line above.
 *
 * The reason is printed **verbatim**, because the engine is the only thing that
 * knows why a run stopped and a dashboard that paraphrases it would be inventing
 * the one sentence the office reads to decide what to do next. That rule holds
 * for every message the engine writes now that it explains a refusal in plain
 * English (2026-09-10).
 *
 * It did not hold for the messages already on record. The first investigation
 * failed with the engine's internal phrasing — a refusal counted against
 * "analysis kinds", with the machine's own code in parentheses — which tells the
 * office nothing they can act on and reads like a crash. Those rows do not get
 * rewritten, so the reading of them is fixed here instead: a message that still
 * carries **either** tell of the old shape is replaced by the plain sentence for
 * the thing that actually happened.
 *
 * The code pattern insists on SCREAMING_SNAKE with a real underscore rather than
 * any parenthesised capitals, so a legitimate sentence that happens to end in an
 * initialism — "(CEO)" — is left alone. Matching is deliberately narrow: a false
 * positive silently throws away a true reason, which is worse than printing one
 * awkward line.
 */
const ENGINE_JARGON = [
  /\brefused\b[\s\S]*\banalysis kinds?\b/i,
  /\([A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\)/,
];

function enginePlainReason(said: string): string {
  return ENGINE_JARGON.some((tell) => tell.test(said))
    ? 'The summary could not be recorded.'
    : said;
}

/**
 * **The proposal, on the record it is about** — one line under the header, with
 * a Yes and a No beside it.
 *
 * It is the inbox card's question said from the record's own point of view: a
 * reader standing on the page does not need to be told which record this is
 * about, so the line says what the run thinks and nothing else. The inbox says
 * the other half, because there the record has to be named.
 */
export function organisationRenameProposalLine(toName: string): string {
  return `The lookup thinks this is really called ${toName}.`;
}

/* **What the office says** left the record page with the owner's rulings of
   2026-09-09 ("The random bits about what the office knows and how it connects
   to others should be removed"), and its words went with it. The statements are
   still filed — the lookup's own box files one — and still read on the knowledge
   panel; what went is the block on the record that printed them back. */

/**
 * **The organisation record's tabs**, in the order they are read (owner ruling,
 * 2026-09-10: "Take the leadership and investors sections and move them to a
 * 'People' tab").
 *
 * Three now rather than two, and the words are the person record's family:
 * one plain noun each, naming the thing the tab holds rather than the act of
 * reading it. Overview is what the place is, People is who is around it, and
 * Network is the same graph a person's Network tab draws.
 */
export const ORGANISATION_TAB_LABELS = {
  overview: 'Overview',
  people: 'People',
  network: 'Network',
} as const;

/**
 * **Leadership** — who the record says works there or sits on its board, in one
 * list. The catalog's split between a job and a seat is not the office's
 * question, which is who runs this company.
 */
export const ORGANISATION_LEADERSHIP_HEAD = 'Leadership';
export const ORGANISATION_NO_LEADERSHIP = 'No leadership on record.';

/**
 * The heads over Leadership's seniority bands. They are read from the role's
 * words, so they name the titles, not a verified position.
 */
export const ORGANISATION_LEADER_TIER_HEADS = {
  principal: 'Founders and chief executives',
  executive: 'Executives and chairs',
  senior: 'Directors, partners and board',
  staff: 'Staff',
  support: 'Assistants, advisers and former',
} as const;
export const ORGANISATION_LEADERSHIP_FEWER = 'Show fewer';

/**
 * The line under a leader whose source named no title. A board seat is still a
 * seat; a job with no title says so rather than leaving the name bare.
 */
export function organisationLeaderLine(leader: {
  role: string | null;
  since: string | null;
  fact_type: 'employment' | 'board_seat';
}): string {
  const since = leader.since ? organisationSinceClause(leader.since) : null;
  const role =
    leader.role ??
    (leader.fact_type === 'board_seat' ? 'Board member' : since ? null : 'No title stated');
  return joinClauses([role, since]);
}

/**
 * **Who put money in** (owner ruling, 2026-09-10: "A separate section from
 * leadership").
 *
 * There is no empty line to go with the head, and that is the block's own rule
 * rather than an omission: leadership has one because every organisation has
 * somebody running it, so a record with none is a gap the office can close.
 * Investors are different — most organisations on this record have none, and
 * plenty that do will never say so publicly — and "No investors on record" over
 * a family practice would read as a finding. So the block is drawn only where
 * there is something in it, which is the same rule the profile block follows.
 */
export const ORGANISATION_INVESTORS_HEAD = 'Investors';

/**
 * **One investor's line under their name** — the round, the figure, the year and
 * whether they led it, each only where the fact carries it.
 *
 * The words are the source's own throughout (`amount` is "$24 million" because
 * that is what was printed), and the role is the only word this side chooses:
 * `lead` prints "lead" and `participant` prints nothing at all. A round with no
 * stated lead would otherwise have every name in it labelled "participant",
 * which says less than the silence does and takes up more room saying it.
 */
export function organisationInvestmentLine(investment: {
  round: string | null;
  amount: string | null;
  date: string | null;
  role: string | null;
}): string {
  return joinClauses([
    investment.round,
    investment.amount,
    investment.date,
    investment.role === 'lead' ? 'lead' : null,
  ]);
}

/**
 * **When they have been there**, said without saying it twice.
 *
 * The value is the word the source wrote (`dateish`: "2019", "March 2020",
 * "since 2019"), so a bare `Since ${value}` prints "Since since 2019" on a third
 * of the real ones. The clause leads with the source's own spelling when the
 * source already led with it.
 */
export function organisationSinceClause(since: string): string {
  const value = since.trim();
  return /^since\b/i.test(value) ? `Since${value.slice('since'.length)}` : `Since ${value}`;
}

/* **How it stands to others** left the record page the same night and for the
   same ruling. The relations are still facts and are still read on the knowledge
   panel, which is where the Link door lives; the record page no longer carries a
   second list of them, nor the muted line for a name nothing was minted for. */

/* ————————————————————————— events ————————————————————————— */

/** Events (docs/notes/2026-09-23-event-graph-design.md §3a). */
export const EVENTS_TITLE = 'Events';
export const EVENTS_NEW_ACTION = 'New event';
export const EVENTS_EMPTY = 'No events yet.';
export const EVENTS_SEARCH_LABEL = 'Search events';
export const EVENTS_NONE_MATCH = 'Nothing here matches that.';
export const EVENTS_COLUMNS = {
  name: 'Name',
  account: 'Account',
  dates: 'Dates',
  place: 'Place',
} as const;

export const EVENT_NEW_TITLE = 'New event';
export const EVENT_NAME_LABEL = 'Name';
export const EVENT_STARTS_LABEL = 'Starts';
export const EVENT_ENDS_LABEL = 'Ends';
export const EVENT_PLACE_LABEL = 'Place';
export const EVENT_ABOUT_LABEL = 'About this event';
export const EVENT_ABOUT_HELP = 'Host, speakers, topic.';
export const EVENT_CREATE = 'Create';
export const EVENT_CREATING = 'Creating…';
export const EVENT_CANCEL = 'Cancel';
export const EVENT_CONFLICT_LEAD = nameOnRecordLine('event');
export const EVENT_CONFLICT_NEW = 'Create a new event';
export const EVENT_CONFLICT_NEW_NOTE = 'Different event, same name';
export const EVENT_CONFLICT_TAKEN = ALREADY_ON_RECORD;

export const EVENT_TAB_LABELS = { overview: 'Overview' } as const;
export const EVENT_NO_RESEARCH = 'No research yet.';
export const EVENT_ABOUT_HEADING = 'About this event';
export const EVENT_SUMMARY_HEADING = 'Summary';
export const EVENT_RUNS_HEADING = 'Runs';
export const EVENT_SUBLINE_FALLBACK = 'No date or place';
export const EVENT_UNTRACK_ACTION = ORGANISATION_UNTRACK_ACTION;
export const EVENT_UNTRACK_CONFIRM = ORGANISATION_UNTRACK_ACTION;
export const EVENT_UNTRACK_WORKING = ORGANISATION_UNTRACK_WORKING;
export const EVENT_UNTRACK_KEEP = ORGANISATION_UNTRACK_KEEP;
export function eventUntrackLine(name: string): string {
  return `Stop tracking ${name}? It stays in the knowledge base with its attendee list.`;
}

/** "Oct 14, 2026", or "Oct 14, 2026 – Oct 15, 2026", or null with no date on record. */
export function eventDates(startsOn: string | null, endsOn: string | null): string | null {
  if (!startsOn) return endsOn ? formatDate(endsOn) : null;
  if (!endsOn || endsOn === startsOn) return formatDate(startsOn);
  return `${formatDate(startsOn)} – ${formatDate(endsOn)}`;
}
export const EVENT_RESEARCH_START = 'Start research';
export const EVENT_RESEARCH_AGAIN = 'Research again';
export const EVENT_RESEARCH_STARTING = 'Starting…';
export const EVENT_RESEARCH_CANCEL = 'Cancel';
export const EVENT_RESEARCH_CONFIRM_LINE =
  'Searches public sources for the event, its hosts and its coverage.';
export function eventResearchFailedLine(reason: string | null): string {
  return reason ? `Event research failed: ${reason}` : 'Event research failed.';
}

/** The badge on an attendee upload, in the ledger and the session head: where it goes. */
export const IMPORT_INTO_EVENT = 'Attendees of';
/**
 * The Attendees tab's line on an upload of the list that has not finished,
 * after the file's name. Nothing reaches the list until it is imported.
 */
export function eventOpenUploadLine(phase: ImportPhase | null): string {
  if (phase === null) return 'stopped before it finished.';
  const step = importNextStep({ state: 'in_progress', phase });
  if (step === null) return 'is importing.';
  return `is not imported yet: ${step.charAt(0).toLowerCase()}${step.slice(1)}.`;
}
export const EVENT_TAB_ATTENDEES = 'Attendees';
export const EVENT_ATTENDEES_EMPTY = 'No attendee list.';
export const EVENT_ATTENDEES_UPLOAD = 'Upload list';
export const EVENT_ATTENDEES_UPLOAD_MORE = 'Upload more';
export const EVENT_ATTENDEES_UPLOAD_TITLE = 'Upload attendee list';
export const EVENT_ATTENDEES_SEARCH = 'Search attendees';
export const EVENT_ATTENDEES_COLUMNS = {
  name: 'Name',
  details: 'Details',
  seat: 'Seat',
  record: 'On record',
  status: 'Status',
} as const;
export const EVENT_ATTENDEE_STATUS_LABELS = {
  pending: 'Not scanned',
  scanned: 'Scanned',
  failed: 'Scan failed',
  promoted: 'Added to records',
} as const;
/** The status of an attendee rated from their profile on the record rather than searched. */
export const EVENT_ATTENDEE_RATED_FROM_RECORD = 'Rated from their record';
export function eventAttendeesCountLine(total: number, linked: number): string {
  const people = `${total} ${total === 1 ? 'attendee' : 'attendees'}`;
  return linked > 0 ? `${people} · ${linked} already on record` : people;
}
export const EVENT_ATTENDEES_UPLOAD_SUBMIT = 'Upload';
export const EVENT_ATTENDEES_UPLOAD_PLACEHOLDER = 'the registration export, with emails';
/* An event that already has a list: does this file add to it or stand in for it
   (docs/notes/2026-09-30-replace-attendee-list-spec.md)? */
/* A replace's dry run (docs/notes/2026-09-30-replace-attendee-list-spec.md). */
export function importReplaceHeadline(before: number, after: number): string {
  return before === after
    ? `The list stays at ${plural(after, 'attendee', 'attendees')}`
    : `${plural(before, 'attendee', 'attendees')} become ${formatCount(after)}`;
}
export function importReplaceCounts(diff: {
  added: readonly unknown[];
  dropped: readonly unknown[];
  dropped_raised: readonly unknown[];
  seats: readonly unknown[];
  details: readonly unknown[];
  unchanged: number;
  to_scan: number;
}): string {
  const dropped = diff.dropped.length + diff.dropped_raised.length;
  return [
    diff.added.length ? `${formatCount(diff.added.length)} added` : null,
    dropped ? `${formatCount(dropped)} dropped` : null,
    diff.seats.length ? `${plural(diff.seats.length, 'seat', 'seats')} changed` : null,
    diff.details.length
      ? `${plural(diff.details.length, 'other detail', 'other details')} changed`
      : null,
    `${formatCount(diff.unchanged)} unchanged`,
    diff.to_scan ? `${formatCount(diff.to_scan)} to scan` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}
export const importReplaceToConfirm = (count: number): string => `${formatCount(count)} to confirm`;
export const importReplaceTakeOff = (name: string): string =>
  `Take ${name} off this event's list. Their record stays.`;
export const importReplaceMore = (count: number): string => `and ${formatCount(count)} more`;
export const IMPORT_REPLACED_BY = 'A newer attendee list replaced this one.';
export const IMPORT_REPLACED_BY_LINK = 'See the newer list';
export const IMPORT_REPLACE_ON_RECORD = 'On record, not on the new file';
export const IMPORT_REPLACE_DROPPED_RAISED = 'Dropped, with findings';
export const IMPORT_REPLACE_DROPPED_RAISED_NOTE = 'Their scan findings go with them.';
export const IMPORT_REPLACE_DROPPED = 'Dropped';
export const IMPORT_REPLACE_ADDED = 'Added';
export const IMPORT_REPLACE_ADDED_NOTE = 'Not scanned yet. A scan covers only them.';
export const IMPORT_REPLACE_SEATS = 'Seats changed';
export const IMPORT_REPLACE_DETAILS = 'Other details changed';
export const IMPORT_REPLACE_RESCAN = 'scanned again with the next scan';
export const IMPORT_REPLACE_DETAILS_KEPT = 'Scan results are kept as they are.';
export const IMPORT_REPLACE_DETAILS_RESCAN =
  'Anyone marked is scanned again with the next scan; the rest keep their results.';
export const EVENT_ATTENDEES_UPLOAD_MODE = 'What this file does';
export const EVENT_ATTENDEES_UPLOAD_ADD = 'Add to the list';
export const EVENT_ATTENDEES_UPLOAD_ADD_HINT = 'New people join; nobody leaves.';
export const EVENT_ATTENDEES_UPLOAD_REPLACE = 'Replace the list';
export const EVENT_ATTENDEES_UPLOAD_REPLACE_HINT =
  'The preview shows who is added, dropped or changed before anything is saved.';
export function eventScanAction(count: number): string {
  return `Scan ${count} ${count === 1 ? 'attendee' : 'attendees'}`;
}
export const EVENT_SCAN_STARTING = 'Starting…';
export const EVENT_SCAN_CANCEL = 'Cancel';
/**
 * The scan's confirm, counting every kind of attendee it covers: those searched
 * on the web, those on the record rated from their profile, and those already
 * scanned, rated on this analysis from what their scan found.
 */
export function eventScanConfirmLine(searched: number, rated: number, rerated = 0): string {
  const parts: string[] = [];
  if (searched > 0) {
    parts.push(
      `${searched} ${searched === 1 ? 'attendee' : 'attendees'} will be searched on the web by name and what the list gives`,
    );
  }
  if (rated > 0) parts.push(`${rated} on record will be rated from their profile`);
  if (rerated > 0) {
    parts.push(
      `${rerated} already scanned will be rated on this analysis from what was found before, with a few searches where needed`,
    );
  }
  return `${parts.join('; ')}. Nothing new is put on record.`;
}
export const EVENT_ATTENDEES_REVIEW_FILTER = 'Needs review';
export const EVENT_ATTENDEES_ALL_FILTER = 'All';
export const ATTENDEE_READINGS_HEAD = 'Who this could be';
export const ATTENDEE_NO_READINGS = 'The scan found nothing under this name.';
export const ATTENDEE_NOT_SCANNED = 'Not scanned yet.';
export const ATTENDEE_BINDING_LABELS = {
  confirmed: 'Matches what the list gives',
  name_only: 'Name only',
  on_record: 'On record',
} as const;
export const ATTENDEE_THIS_IS_THEM = 'This is them';
export const ATTENDEE_PICKING = 'Saving…';
/** On the reading the office picked: the event's answer, not a record. */
export const ATTENDEE_PICKED = 'Picked for this event';
/** Takes the event's pick back. */
export const ATTENDEE_UNPICK = 'Not them';
/** Over Add to records while a reading is picked: what comes across with them. */
export const ATTENDEE_ADD_CARRIES_PICK =
  'Adding them to the records brings the picked match with them.';
export const ATTENDEE_ADD_NEW = 'Add to records';
export const ATTENDEE_ADD_LINKED = 'Add with their record';
export const ATTENDEE_ADDING = 'Adding…';
export const ATTENDEE_NONE_OF_THESE = 'None of these';
export const ATTENDEE_ON_RECORD_LINE = 'Already on record as';
export const ATTENDEE_PROMOTED_LINE = 'Added to records.';
export const ATTENDEE_OPEN_RECORD = 'Open';
export const ATTENDEE_USE_THIS_PERSON = 'Use this person';
export const ATTENDEE_SOMEONE_ELSE = 'Wrong person';
export const ATTENDEE_NAME_CONFLICT_LINE =
  'Someone with this name is already on record. Use them if it is the same person.';
export const ATTENDEE_IDENTIFIER_CONFLICT_LINE =
  'Someone on record already has this email or phone.';
export const EVENT_ATTENDEE_NEEDS_REVIEW = 'Needs review';
export const EVENT_ATTENDEES_DELETE = 'Delete attendee list';
export const EVENT_ATTENDEES_DELETING = 'Deleting…';
export const EVENT_ATTENDEES_KEEP = 'Keep';
export const EVENT_ATTENDEES_DELETE_LINE =
  'Deletes every attendee and everything the scans found. People already on record stay.';
export const EVENT_PROFILE_HEADING = 'What the event says it is';
export const EVENT_PROFILE_FROM_PRESS = 'from press coverage';
/**
 * The profile's particulars as label-value rows, only those stated, each saying
 * whether press rather than the event's own pages supplied it.
 */
export function eventProfileRows(profile: EventProfile): {
  label: string;
  value: string;
  fromPress: boolean;
}[] {
  const press = (...keys: EventProfile['from_press']) =>
    keys.some((key) => profile.from_press.includes(key));
  /* A stated day reads as the event's dates do; anything else as the page wrote it. */
  const day = (value: string) => (/^\d{4}-\d{2}-\d{2}$/.test(value) ? formatDate(value) : value);
  const when = profile.starts_on
    ? profile.ends_on && profile.ends_on !== profile.starts_on
      ? `${day(profile.starts_on)} – ${day(profile.ends_on)}`
      : day(profile.starts_on)
    : null;
  const rows: [string, string | null, boolean][] = [
    ['When', when, press('starts_on', 'ends_on')],
    [
      'Where',
      [profile.venue, profile.place].filter(Boolean).join(', ') || null,
      press('venue', 'place'),
    ],
    ['Format', profile.format, press('format')],
    ['Size', profile.expected_size, press('expected_size')],
    ['Website', profile.website, press('website')],
  ];
  return rows.flatMap(([label, value, fromPress]) => (value ? [{ label, value, fromPress }] : []));
}

/* ——— Event analysis frames (phase 2) ——— */
export const EVENT_TAB_ANALYSIS = 'Analysis';
export const EVENT_FRAME_NOT_ASSESSED = 'Not assessed';
/** Hover hints on the tiers whose names alone could be misread, by tone. */
export const EVENT_TIER_HINTS: Partial<
  Record<'concern' | 'unknown' | 'quiet' | 'clear' | 'none', string>
> = {
  unknown: 'A possible match needs review.',
  quiet: 'Nothing flagged. Matched on name only.',
};
export const EVENT_ANALYSIS_NO_LIST = 'Add the attendee list on the Attendees tab first.';
export const EVENT_ANALYSIS_NONE_OFFERED = 'No analyses are available for your organization yet.';
export const EVENT_ANALYSIS_NOBODY = 'Nobody in this group.';
/** Under the attendee total at the center of a factor's tier ring. */
export const EVENT_ANALYSIS_TOTAL = 'Attendees';
/** The Overview's pointer to the next step of the event flow. */
export const EVENT_NEXT_ADD_ATTENDEES = 'Next: add the attendee list';
export const EVENT_NEXT_ANALYSES = 'See the analyses';
export const EVENT_ANALYSIS_SCANNING =
  'The attendees are being scanned. Results appear here as they come in.';
/**
 * What a scan now would cover beside the results: attendees never searched,
 * people on the record whose profile has changed since they were rated, and
 * attendees scanned before this analysis who are not rated on it yet.
 */
export function eventAnalysisUnscannedLine(
  searched: number,
  rated: number,
  rerated = 0,
  /** The office changed what the analysis is rated against. */
  newInput = false,
): string {
  const parts = [
    searched > 0
      ? `${searched} ${searched === 1 ? 'attendee has' : 'attendees have'} not been scanned yet`
      : null,
    rated > 0 ? `${rated} on record ${rated === 1 ? 'has' : 'have'} new information to rate` : null,
    rerated > 0
      ? `${rerated} already scanned ${rerated === 1 ? 'is' : 'are'} not rated ${newInput ? 'against this text' : 'on this analysis'} yet`
      : null,
  ].filter(Boolean);
  return `${parts.join('; ')}.`;
}
/** The analysis's written report: opened when one exists, else written. */
/** The analysis's input box: what the office enters for its parameterized factors. */
export const EVENT_ANALYSIS_INPUT_MAX = 4000;
export const EVENT_ANALYSIS_INPUT_HINT = 'Attendees are rated against this text.';
/** Above an analysis's results: what its ratings were made against. */
export function eventAnalysisRatedAgainst(label: string, value: string): string {
  return `Rated against ${label.charAt(0).toLowerCase()}${label.slice(1)}: ${value}`;
}
export const EVENT_ANALYSIS_FULL_REPORT = 'Read the full report';
/** The report is written when the scan completes (2026-10-03). */
export const EVENT_ANALYSIS_REPORT_WRITING =
  'The report is being written. It appears here when it is ready.';
/** Under a report field asked with the scan: it shapes the write-up, never a rating. */
export const EVENT_ANALYSIS_REPORT_FIELD_HINT =
  'Used in the write-up only. Attendees are not rated against it.';
/** A frame's tier label for an attendee or a reading; null is not assessed. */
export function eventFrameTierLabel(frame: EventFrameDef, tier: string | null): string {
  if (tier === null) return EVENT_FRAME_NOT_ASSESSED;
  return frame.tiers.find((one) => one.key === tier)?.label ?? tier;
}

/* ——— Event advance on events (phase 3B) ——— */
export const EVENT_DELETE_ACTION = 'Delete event';
export const EVENT_DELETE_WORKING = 'Deleting…';
export const EVENT_DELETE_KEEP = 'Keep the event';
export function eventDeleteLine(name: string): string {
  return `This deletes ${name} for good, with its attendee list and every attendance, host and sponsor link. People and organizations stay. ${CANNOT_BE_UNDONE}`;
}
export const EVENT_ADD_PEOPLE = 'Add people';
export const EVENT_ADD_PEOPLE_SUBMIT = 'Add to the event';
export const EVENT_ADD_PEOPLE_WORKING = 'Adding…';
export const EVENT_ADD_PEOPLE_LINE = 'Each person is recorded as attending this event.';
export function eventReportAction(displayName: string): string {
  return `Write ${displayName.toLowerCase()}`;
}

const capitalized = (text: string): string => `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

/* ——— The event page and its Analysis tab, as the blend draws them
   (docs/notes/2026-09-30-ui-redesign-blend-spec.md) ——— */

export function eventResearchedOn(day: string): string {
  return `Researched ${day}`;
}
export function eventSummaryWritten(day: string): string {
  return `Written from the research · ${day}`;
}
/** The Overview's disclosure over what the record holds about the event. */
export function eventFactsDisclosure(count: number): string {
  return `Show the ${count} ${count === 1 ? 'fact' : 'facts'} on record`;
}
export const EVENT_ASIDE_RESEARCH = 'Research';
export const EVENT_ASIDE_NOT_RESEARCHED = 'Not yet';
export const EVENT_ASIDE_ATTENDEES = 'Attendees';
export const EVENT_ASIDE_NO_LIST = 'No list yet';
export function eventAttendeesOnRecord(count: number): string {
  return `${count} already on record`;
}
export const EVENT_ASIDE_ON_RECORD = 'Already on record';
export const EVENT_PROFILE_WHEN = 'When';
export const EVENT_PROFILE_WHERE = 'Where';

/** An attendee nobody tracks: no status to split them by. */
export const EVENT_NOT_TRACKED = 'Not tracked';
/** Tracked, with no status the record gave. */
export const EVENT_TRACKED = 'Tracked';
/** The one-line conclusion under a report-only analysis's ring of who is coming. */
export function eventTrackedConclusion(tracked: number, total: number): string {
  const rest = total - tracked;
  if (tracked === 0) return `None of the ${numberWord(total)} attendees is tracked.`;
  const head =
    tracked === total
      ? total === 1
        ? 'The one attendee is tracked.'
        : total === 2
          ? 'Both attendees are tracked.'
          : `All ${numberWord(total)} attendees are tracked.`
      : `${capitalized(numberWord(tracked))} of the ${numberWord(total)} attendees ${tracked === 1 ? 'is' : 'are'} tracked`;
  if (rest === 0) return head;
  return `${head}; ${numberWord(rest)} ${rest === 1 ? 'is' : 'are'} not.`;
}

/**
 * How a frame's raised group reads in a sentence, singular then plural. Keyed
 * by frame so a new frame fails to compile until it has its words.
 */
const RAISED_PHRASES: Readonly<Record<EventFrameKey, readonly [string, string]>> = {
  disruption: ['is flagged', 'are flagged'],
  diligence: ['is flagged', 'are flagged'],
  goal_fit: ['matches the goals', 'match the goals'],
  public_profile: ['has a public profile', 'have a public profile'],
  money_event: ['has a money event stated', 'have a money event stated'],
};

/**
 * **A factor's one-line conclusion**: how many of the attendees its raised
 * group holds, who they are and where the list seats them, then how many are
 * left to check. The legend beside it carries every other count.
 */
export function eventFactorConclusion(
  frame: EventFrameKey,
  total: number,
  raised: readonly { name: string; seat: string | null }[],
  raisedCount: number,
  toCheck: number,
): string {
  const [one, many] = RAISED_PHRASES[frame];
  const of = `of the ${numberWord(total)}`;
  const lead =
    raisedCount === 0
      ? `None ${of} ${many}.`
      : `${capitalized(numberWord(raisedCount))} ${of} ${raisedCount === 1 ? one : many}${
          raised.length > 0
            ? `: ${andList([
                ...raised.map((who) =>
                  who.seat ? `${who.name}, seated in ${who.seat}` : who.name,
                ),
                ...(raisedCount > raised.length
                  ? [`${numberWord(raisedCount - raised.length)} more`]
                  : []),
              ])}`
            : ''
        }.`;
  const check =
    toCheck === 0
      ? 'Nobody is left to check.'
      : `${capitalized(numberWord(toCheck))} ${toCheck === 1 ? 'needs' : 'need'} a check.`;
  return `${lead} ${check}`;
}

/** An attention entry's line: its group, whether it waits on the office. */
export const EVENT_ATTENTION_NEEDS_REVIEW = 'needs review';
/** Opens the attendee's readings, where the office says which one is them. */
export const EVENT_ATTENTION_CHECK_WHO = 'Check who this is';
/**
 * The Analysis tab's line over the attendees the scan could not tell apart,
 * before their names; while the scan's report waits on them, it says so.
 */
export function eventAnalysisReviewLine(count: number, reportHeld = false): string {
  if (reportHeld) {
    return count === 1
      ? 'The report is waiting. Before writing it, check who this attendee is:'
      : `The report is waiting. Before writing it, check who these ${formatCount(count)} attendees are:`;
  }
  return count === 1
    ? 'One attendee could be more than one person. Check who:'
    : `${formatCount(count)} attendees could each be more than one person. Check who:`;
}
/** The held report's line once nobody is left to match. */
export const EVENT_REPORT_HELD_READY = 'Everyone is matched. The report is ready to write.';
export const EVENT_REPORT_WRITE = 'Write the report';
/** Writes the held report with attendees still unmatched. */
export const EVENT_REPORT_WRITE_NOW = 'Write it now';
export const EVENT_REPORT_WRITE_STARTING = 'Starting…';
/** Over an analysis whose report was written before the attendee list last changed. */
export const EVENT_REPORT_STALE = 'The attendee list has changed since this report was written.';
export const EVENT_REPORT_REFRESH = 'Refresh';
/** After the names the review line has room for. */
export function eventAnalysisReviewMore(count: number): string {
  return `and ${formatCount(count)} more`;
}
export const EVENT_SEAT_WORD = 'Seat';
export function eventAttentionMore(count: number): string {
  return `${count} more in this group`;
}
export function eventPassagesDisclosure(count: number): string {
  return `Show the ${count} ${count === 1 ? 'passage' : 'passages'}`;
}

export const EVENT_SEATING_HEAD = 'Where the list seats them';
export const EVENT_SEATING_NOTE =
  'As the attendee list gives them. The list has no venue plan, so this is a grouping, not a map.';
export const EVENT_SEATING_ROWLESS = 'No row';
export function eventSeatingPartial(shown: number, seated: number): string {
  return `The first ${shown} of ${seated} seated.`;
}
/**
 * **The seating's one line**: who shares a row with each raised attendee, and
 * where the others sit on the frame. Computed from the list's seat columns.
 */
export function eventSeatingConclusion(
  shares: readonly {
    name: string;
    area: string;
    others: readonly { name: string; seat: string | null; tier: string }[];
  }[],
  seated: number,
  areas: number,
): string {
  if (shares.length === 0)
    return `${capitalized(numberWord(seated))} ${seated === 1 ? 'attendee is' : 'attendees are'} seated, across ${numberWord(areas)} ${areas === 1 ? 'row' : 'rows'}.`;
  return shares
    .map(({ name, area, others }) => {
      if (others.length === 0) return `Nobody else on the list shares ${area} with ${name}.`;
      const who = andList(
        others.map(
          (other) =>
            `${other.name}${other.seat ? ` at ${other.seat}` : ''}, ${other.tier.charAt(0).toLowerCase()}${other.tier.slice(1)}`,
        ),
      );
      return `${name} shares ${area} with ${others.length === 1 ? 'one other attendee' : `${numberWord(others.length)} other attendees`}: ${who}.`;
    })
    .join(' ');
}

export const EVENT_REPORT_FROM = 'From the report';
export function eventReportVersion(number: number, day: string): string {
  return `Version ${number} · ${day}`;
}
/**
 * What the full report adds to the summary on the tab: the sections not quoted,
 * by title, and for a rated analysis the groups written up with their sources.
 */
export function eventReportAdds(titles: readonly string[], groups: boolean): string {
  const named = titles.map((title) => `${title.charAt(0).toLowerCase()}${title.slice(1)}`);
  if (groups && named.length > 0)
    return `Also in it: ${andList(named)}, and each group written up with sources, ready to print.`;
  if (groups) return 'Also in it: each group written up with sources, ready to print.';
  if (named.length > 0) return `Also in it: ${andList(named)}, with sources.`;
  return 'The same, with sources, ready to print.';
}

export function eventRatedDisclosure(total: number): string {
  return `Show how each of the ${total} was rated`;
}
export function eventAttendeesDisclosure(total: number): string {
  return `Show the ${total} ${total === 1 ? 'attendee' : 'attendees'}`;
}
export function eventDisclosureTracked(count: number): string {
  return `${count} tracked`;
}
export function eventFilterLine(label: string, count: number, total: number): string {
  return `${label} · ${count} of ${total}`;
}
export const EVENT_SHOW_EVERY_GROUP = 'Show every group';

/**
 * **A newer dashboard is live** (2026-10-14). Shown from the moment the tab
 * learns it is running old code until it reloads, which it does by itself at the
 * next navigation; the button is for a reader who wants it now.
 */
export const STALE_BUILD_LABEL = 'A newer version of the dashboard is ready';
export const STALE_BUILD_LINE =
  'It loads when you next move to another page. Reload now to get it sooner.';
export const STALE_BUILD_RELOAD = 'Reload';
